import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';

const LanguageContext = createContext(null);

const translations = {
  en: {
    "signup.title": "Create your account",
    "signup.subtitle": "Start a new organization or join your team.",
    "signup.tabOrg": "New organization",
    "signup.tabJoin": "Join my team",
    "signup.name": "Full name",
    "signup.email": "Email address",
    "signup.phone": "Phone (optional)",
    "signup.orgName": "Organization name",
    "signup.orgCode": "Organization code",
    "signup.joinCode": "Staff join code",
    "signup.joinHint": "Ask your administrator for the organization code and the staff join code.",
    "signup.password": "Password",
    "signup.passwordHint": "At least 12 characters.",
    "signup.confirmPassword": "Confirm password",
    "signup.passwordsMismatch": "Passwords do not match",
    "signup.passwordTooShort": "Password must be at least 12 characters",
    "signup.agreePrefix": "By signing up you agree to our",
    "signup.terms": "Terms of Service",
    "signup.and": "and",
    "signup.privacy": "Privacy Policy",
    "signup.submit": "Create account",
    "signup.submitting": "Creating...",
    "signup.haveAccount": "Already have an account?",
    "signup.signIn": "Sign in",
    "signup.checkEmailTitle": "Check your email",
    "signup.checkEmailBody": "We sent a confirmation link to {email}. Open it to finish signing up. The link works for 24 hours.",
    "signup.resend": "Resend email",
    "signup.resent": "If a sign-up is waiting for this email, a new link has been sent.",
    "signup.failed": "Sign-up failed. Please try again.",
    "signup.verifying": "Confirming your email...",
    "signup.verifyOrg": "Your organization \"{org}\" is ready.",
    "signup.verifyJoin": "You have joined \"{org}\".",
    "signup.systemCode": "Your system code",
    "signup.systemCodeNote": "Enter this code when you sign in on the website or in the app.",
    "signup.continueSignIn": "Continue to sign in",
    "signup.verifyFailed": "This confirmation link is invalid or has expired.",
    "signup.tryAgain": "Sign up again",
    "login.newHere": "New to MITIFM?",
    "login.createAccount": "Create an account",
    "nav.billing": "Subscription",
    "billing.title": "Subscription",
    "billing.subtitle": "Manage your plan and payments.",
    "billing.statusTrialing": "Free trial",
    "billing.statusActive": "Active",
    "billing.statusGrace": "Payment overdue",
    "billing.statusReadOnly": "Expired (read-only)",
    "billing.trialLeft": "{days} days left in your free trial.",
    "billing.activeUntil": "Paid until {date}.",
    "billing.graceLeft": "Your plan has ended. Pay within {days} days to avoid read-only mode.",
    "billing.readOnly": "Your subscription has expired. The system is read-only: you can view your data but not change it. Choose a plan to continue.",
    "billing.plan": "Plan",
    "billing.users": "Up to {n} users",
    "billing.unlimitedUsers": "Unlimited users",
    "billing.choosePlan": "Choose a plan",
    "billing.monthly": "Monthly",
    "billing.yearly": "Yearly",
    "billing.yearlyHint": "Best value",
    "billing.perMonth": "/ month",
    "billing.perYear": "/ year",
    "billing.currentBadge": "Current plan",
    "billing.payButton": "Pay with Mobile Money",
    "billing.phoneLabel": "Mobile Money number (MTN or Airtel)",
    "billing.phonePlaceholder": "e.g. 0788123456",
    "billing.phoneHelp": "We will send a payment request to this phone. Approve it by entering your Mobile Money PIN.",
    "billing.hostedHelp": "You will pay on Flutterwave’s secure page with Mobile Money (MTN or Airtel) or a bank card. We never see your card or PIN.",
    "billing.phoneInvalid": "Enter a valid MTN or Airtel number, for example 0788123456.",
    "billing.resultApprove": "Check your phone and approve the payment request (enter your Mobile Money PIN).",
    "billing.resultLate": "We have not received the confirmation yet. If you approved the payment, your subscription will activate by itself within a few minutes.",
    "billing.redirecting": "Sending the payment request...",
    "billing.paymentsOff": "Online payment is not available yet. Please contact support@mitifm.com.",
    "billing.secureNote": "Payments go directly through MTN Mobile Money and Airtel Money. We never see or store your Mobile Money PIN.",
    "billing.history": "Payment history",
    "billing.noPayments": "No payments yet.",
    "billing.colDate": "Date",
    "billing.colPlan": "Plan",
    "billing.colAmount": "Amount",
    "billing.colStatus": "Status",
    "billing.paid": "Paid",
    "billing.pending": "Pending",
    "billing.failed": "Failed",
    "billing.checkoutFailed": "We could not start the payment. Please try again.",
    "billing.noneNeeded": "This system does not need a subscription.",
    "billing.resultChecking": "Confirming your payment...",
    "billing.resultSuccess": "Payment received. Thank you!",
    "billing.resultSuccessBody": "Your subscription is active.",
    "billing.resultPending": "We are still waiting for confirmation from the payment provider. This page updates by itself. If you approved the payment on your phone it should only take a minute.",
    "billing.resultFailed": "The payment was not completed. You can try again.",
    "billing.resultUnknown": "We could not find this payment.",
    "billing.backToBilling": "Back to subscription",
    "billing.bannerTrial": "Free trial: {days} days left.",
    "billing.bannerGrace": "Your plan has ended. {days} days left before the system becomes read-only.",
    "billing.bannerReadOnly": "Subscription expired: the system is read-only.",
    "billing.bannerAction": "Manage subscription",
    "billing.bannerAskAdmin": "Ask your administrator to renew.",
    "billing.includes": "Included services",
    "billing.feature.maintenance": "Preventive maintenance",
    "billing.feature.inventory": "Inventory & parts",
    "billing.feature.asset_requests": "Vendor asset requests & quotes",
    "billing.feature.bookings": "Space bookings & public booking page",
    "billing.feature.data_tools": "Data import & export",
    "joinCode.title": "Staff sign-up",
    "joinCode.description": "Let your staff create their own account. They need your organization code and this staff join code.",
    "joinCode.orgCode": "Organization code",
    "joinCode.joinCode": "Staff join code",
    "joinCode.allow": "Allow staff sign-up",
    "joinCode.regenerate": "New code",
    "joinCode.regenerateConfirm": "Create a new staff join code? The old code will stop working.",
    "joinCode.copyInvite": "Copy invite link",
    "joinCode.copied": "Copied",
    "joinCode.unavailable": "Staff sign-up is available for organization systems only.",
    "joinCode.note": "New staff start with limited access (My Tasks). You can change their role in Users.",
    "joinCode.failed": "Could not update staff sign-up. Please try again.",
    'language.english': 'English',
    'language.french': 'French',
    'language.kinyarwanda': 'Kinyarwanda',
    'language.label': 'Language',
    'nav.dashboard': 'Dashboard',
    'nav.workOrders': 'Work Orders',
    'nav.assets': 'Assets',
    'nav.facilities': 'Facilities',
    'nav.maintenance': 'Maintenance',
    'nav.inventory': 'Inventory',
    'nav.bookings': 'Bookings',
    'nav.reportsInbox': 'Reports Inbox',
    'nav.messages': 'Messages',
    'nav.myTeam': 'My Team',
    'nav.users': 'Users',
    'nav.settings': 'Settings',
    'nav.myTasks': 'My Tasks',
    'nav.myReports': 'My Reports',
    'nav.developer': 'Developer',
    'sidebar.profile': 'Main Menu',
    'sidebar.myWork': 'My Work',
    'sidebar.administration': 'Administration',
    'sidebar.changePassword': 'Change Password',
    'sidebar.logout': 'Logout',
    'sidebar.publicBooking': 'Public Booking Page',
    'header.hello': 'Hello',
    'header.systemTitle': 'MITI Facility Management System',
    'header.facilityManagement': 'Facility Management',
    'login.title': 'Sign in to your account',
    'login.subtitle': 'Enter your credentials to access the system',
    'login.email': 'Email Address',
    'login.password': 'Password',
    'login.systemCode': 'System code (optional)',
    'login.systemCodeHint': 'Leave blank unless your organization gave you a system code.',
    'login.forgotPassword': 'Forgot password?',
    'login.signIn': 'Sign In',
    'login.signingIn': 'Signing in...',
    'login.demoCredentials': 'Demo Credentials',
    'login.changePassword': 'Change Password',
    'login.cancel': 'Cancel',
    'login.save': 'Save',
    'login.minimum6': 'Minimum 6 characters',
    'login.passwordsDoNotMatch': 'Passwords do not match',
    'login.passwordChanged': 'Password changed successfully!',
    'login.failed': 'Login failed. Please try again.',
    'role.employee': 'Employee',
    'account.currentPassword': 'Current Password',
    'account.newPassword': 'New Password',
    'account.confirmNewPassword': 'Confirm New Password',
    'account.confirmPassword': 'Confirm Password',
    'account.min12': 'Minimum 12 characters',
    'account.reenterPassword': 'Re-enter new password',
    'account.saving': 'Saving...',
    'account.passwordsDoNotMatch': 'Passwords do not match',
    'account.changeFailed': 'Failed to change password',
    'account.forgotTitle': 'Forgot password?',
    'account.forgotDescription': 'Enter your account email and we will send a reset link.',
    'account.sendResetLink': 'Send reset link',
    'account.sending': 'Sending...',
    'account.backToLogin': 'Back to login',
    'account.resetEmailFailed': 'Could not send reset email',
    'account.resetTitle': 'Set a new password',
    'account.resetDescription': 'Choose a new password for your FMS account.',
    'account.returnToLogin': 'Return to login',
    'account.resetPassword': 'Reset password',
    'account.resetFailed': 'Could not reset password',
    'account.showPassword': 'Show password',
    'account.hidePassword': 'Hide password',
    'navigation.open': 'Open navigation menu',
    'navigation.close': 'Close navigation menu',
    'common.loading': 'Loading...', 'common.quantity': 'Quantity', 'common.notes': 'Notes', 'common.submitting': 'Submitting...',
    'developer.deleteFailed': 'Could not delete that system', 'developer.title': 'Developer Controls', 'developer.subtitle': 'Global system availability and maintenance mode', 'developer.loadFailed': 'Could not load system status', 'developer.updateFailed': 'Could not update system status', 'developer.deactivateConfirm': 'Deactivate the whole system? Users and public operations will be blocked until you reactivate it.', 'developer.active': 'System is active', 'developer.inactive': 'System is deactivated', 'developer.available': 'Users and public services are available.', 'developer.developerOnly': 'Only developers can access the system.', 'developer.updating': 'Updating...', 'developer.deactivate': 'Deactivate whole system', 'developer.activate': 'Activate whole system', 'developer.deactivateSystemConfirm': 'Deactivate "{name}"? Its users won\'t be able to sign in until it is reactivated.', 'developer.deleteSystemConfirm': 'Permanently delete "{name}"?\n\nThis will PERMANENTLY DESTROY its entire database — every user, space, work order, asset, booking and setting. This CANNOT be undone.\n\nDeactivating instead just blocks sign-ins and can be reversed later.', 'developer.deleteSystemPrompt': 'To confirm permanent deletion, type the system\'s name exactly: {name}', 'developer.deleteSystemNameMismatch': 'System name did not match — deletion cancelled.',
    'vendor.invalidLink': 'This link is invalid or expired', 'vendor.submitFailed': 'Could not submit quotation', 'vendor.loading': 'Loading request...', 'vendor.submitted': 'Quotation submitted', 'vendor.review': 'The facility team will review your quotation and contact you.', 'vendor.title': 'Vendor Quotation', 'vendor.description': 'Please provide your quotation for the requested asset.', 'vendor.name': 'Vendor name *', 'vendor.email': 'Vendor email *', 'vendor.amount': 'Quotation amount *', 'vendor.deliveryTime': 'Delivery time *', 'vendor.deliveryPlaceholder': 'e.g. 5 business days', 'vendor.submit': 'Submit quotation',
    'reminder.endingNow': 'Ending now', 'reminder.oneMinute': '1 min left', 'reminder.minutes': 'min left', 'reminder.bookingEnding': 'Booking Ending Soon', 'reminder.rentalRenewal': 'Rental Renewal Due', 'reminder.guest': 'Guest', 'reminder.new': 'new notifications', 'reminder.title': 'Notifications', 'reminder.enableDescription': 'Enable phone alerts for bookings, reports, orders, and quotations.', 'reminder.enable': 'Enable', 'reminder.blocked': 'Phone alerts are blocked. Enable notifications for this site in your browser settings.', 'reminder.none': 'No notifications yet', 'reminder.renewalIn': 'Rental renewal in', 'reminder.days': 'days', 'reminder.tenant': 'Tenant', 'reminder.noEmail': 'No email', 'reminder.ends': 'Ends:', 'reminder.people': 'people', 'reminder.refreshes': 'Alerts refresh every 30 seconds',
    'dashboard.overview': 'Overview of all facility operations',
    'dashboard.facilityOverview': 'your facility overview',
    'dashboard.unavailable': 'Dashboard unavailable',
    'dashboard.loadError': 'Unable to load dashboard data. Check that the backend is running.',
    'dashboard.refresh': 'Refresh',
    'dashboard.retry': 'Retry',
    'dashboard.greeting': 'Good to see you,',
    'dashboard.attention': 'Here is what needs your attention today.',
    'dashboard.totalWorkOrders': 'Total Work Orders',
    'dashboard.openOrders': 'Open Orders',
    'dashboard.criticalPriority': 'Critical Priority',
    'dashboard.totalAssets': 'Total Assets',
    'dashboard.availableSpaces': 'Available Spaces',
    'dashboard.overdueMaintenance': 'Overdue Maintenance',
    'dashboard.lowStock': 'Low Stock',
    'dashboard.assetRequests': 'Asset Requests',
    'dashboard.unreadAlerts': 'Unread Alerts',
    'dashboard.workOrderTrend': 'Work Order Trend (Last 6 Months)',
    'dashboard.created': 'Created',
    'dashboard.completed': 'Completed',
    'dashboard.workOrderStatus': 'Work Order Status',
    'dashboard.recentWorkOrders': 'Recent Work Orders',
    'dashboard.viewAll': 'View All',
    'dashboard.workOrderNumber': 'WO #',
    'dashboard.title': 'Title',
    'dashboard.priority': 'Priority',
    'dashboard.status': 'Status',
    'dashboard.noWorkOrders': 'No work orders yet',
    'dashboard.upcomingMaintenance': 'Upcoming Maintenance',
    'dashboard.noUpcomingMaintenance': 'No upcoming maintenance',
    'dashboard.overdue': 'Overdue',
    'dashboard.days': 'days',
    'dashboard.general': 'General',
    'dashboard.request': 'Request',
    'dashboard.requester': 'Requester',
    'dashboard.date': 'Date',
    'dashboard.quantity': 'Qty',
    'dashboard.noAssetRequests': 'No asset requests',
    'dashboard.upcomingBookings': 'Upcoming Bookings',
    'dashboard.untitledBooking': 'Untitled booking',
    'dashboard.noUpcomingBookings': 'No upcoming bookings',
    'status.open': 'Open',
    'status.inProgress': 'In Progress',
    'status.completed': 'Completed'
    ,'priority.low': 'Low',
    'priority.medium': 'Medium',
    'priority.high': 'High',
    'priority.critical': 'Critical',
    'status.assigned': 'Assigned',
    'status.onHold': 'On Hold',
    'status.cancelled': 'Cancelled',
    'status.pending': 'Pending',
    'status.approved': 'Approved',
    'status.rejected': 'Rejected',
    "layout.logoAlt": "logo",
    "layout.tagline": "Facility Management",
    "layout.myFacilityFallback": "My Facility",
    "about.backToHome": "Back to Home",
    "about.logoAlt": "logo",
    "about.facilityManagementLabel": "FACILITY MANAGEMENT",
    "about.footerRights": "All rights reserved.",
    "contact.backToHome": "Back to Home",
    "contact.logoAlt": "logo",
    "contact.facilityManagementLabel": "FACILITY MANAGEMENT",
    "contact.footerRights": "All rights reserved.",
    "services.backToHome": "Back to Home",
    "services.logoAlt": "logo",
    "services.facilityManagementLabel": "FACILITY MANAGEMENT",
    "services.footerRights": "All rights reserved.",
    "legal.backToHome": "Back to Home",
    "legal.logoAlt": "logo",
    "legal.facilityManagementLabel": "FACILITY MANAGEMENT",
    "legal.footerRights": "All rights reserved.",
    "about.eyebrow": "About Us",
    "about.heroTitle": "Managing Facilities Smarter",
    "about.feature1Title": "Modern Infrastructure",
    "about.feature1Desc": "State-of-the-art facilities equipped with the latest technology for a productive environment.",
    "about.feature2Title": "Secure & Managed",
    "about.feature2Desc": "24/7 security monitoring, access control, and the MITI facility management team.",
    "about.feature3Title": "Sustainable Operations",
    "about.feature3Desc": "Energy-efficient systems and eco-friendly practices to minimize environmental impact.",
    "about.feature4Title": "Professional Support",
    "about.feature4Desc": "Dedicated facility managers and support staff to ensure your needs are met promptly.",
    "contact.eyebrow": "Contact Us",
    "contact.heroTitle": "Get In Touch",
    "contact.heroSubtitle": "Have questions about booking, pricing, or facility management? We're here to help.",
    "contact.phoneLabel": "Phone",
    "contact.emailReplyTime": "We reply within 24 hours",
    "contact.emailLabel": "Email",
    "contact.locationLabel": "Location",
    "contact.locationSubtitle": "Visit our main facility",
    "services.eyebrow": "Our Services",
    "services.heroTitle": "Everything You Need",
    "services.heroSubtitle": "From space bookings to full facility management, we cover it all.",
    "services.spaceBookingTitle": "Space Booking",
    "services.spaceBookingDesc": "Book conference rooms, training halls, offices and more with real-time availability and instant confirmation.",
    "services.assetManagementTitle": "Asset Management",
    "services.assetManagementDesc": "Track and manage all facility equipment, machinery, and resources with lifecycle and depreciation tracking.",
    "services.maintenanceTitle": "Maintenance",
    "services.maintenanceDesc": "Preventive and corrective maintenance scheduling to keep your facility running at peak performance.",
    "services.workOrdersTitle": "Work Orders",
    "services.workOrdersDesc": "Submit and track service requests with priority management and real-time status updates.",
    "services.bookingManagementTitle": "Booking Management",
    "services.bookingManagementDesc": "Full visibility of all space bookings with approval workflow, conflict detection, and calendar view.",
    "services.paymentsTitle": "{currency} Payments",
    "services.paymentsDesc": "All space rentals priced in {currency}. Clear pricing per hour with amount shown before booking.",
    "legal.termsTitle": "Terms of Service",
    "legal.privacyTitle": "Privacy Policy",
    "legal.notFilledIn": "This page hasn't been filled in yet.",
    "booking.typeOffice": "Office",
    "booking.typeConferenceRoom": "Conference Room",
    "booking.typeTrainingRoom": "Training Room",
    "booking.typeStockRoom": "Stock Room",
    "booking.typeServerRoom": "Server Room",
    "booking.typeBathroom": "Bathroom",
    "booking.typeParking": "Parking",
    "booking.typeCommonArea": "Common Area",
    "booking.typeLab": "Lab",
    "booking.typeOther": "Other",
    "booking.endAfterStartError": "End time must be after start",
    "booking.submitFailedError": "Booking failed. Please try again.",
    "booking.floorLabel": "Floor",
    "booking.peopleSuffix": "people",
    "booking.rentalSubmittedTitle": "Rental Order Submitted!",
    "booking.bookingSubmittedTitle": "Booking Request Submitted!",
    "booking.referenceLabel": "Booking Reference",
    "booking.depositDueLabel": "Deposit Due Now (to confirm interest)",
    "booking.percentOfTotal": "% of the total order value",
    "booking.totalOrderValue": "Total Order Value:",
    "booking.managerWillConfirm": "Manager will confirm your booking at",
    "booking.doneButton": "Done",
    "booking.alreadyBookedLabel": "Already booked:",
    "booking.moreSuffix": "+{n} more",
    "booking.orderDetailsHeading": "📅 Order Details",
    "booking.orderTypeLabel": "Order Type *",
    "booking.shortBookingOption": "Short booking",
    "booking.spaceRentalOption": "Space rental",
    "booking.rentalPeriodLabel": "Rental period *",
    "booking.month1": "1 month",
    "booking.month3": "3 months",
    "booking.month6": "6 months",
    "booking.month12": "12 months",
    "booking.priceUnitLabel": "Price unit *",
    "booking.perHourOption": "Per hour ·",
    "booking.perDayOption": "Per day ·",
    "booking.perMonthOption": "Per month ·",
    "booking.paymentMethodLabel": "Payment method *",
    "booking.bankTransferOption": "Bank transfer",
    "booking.mobileMoneyOption": "Mobile Money",
    "booking.cardOption": "Card",
    "booking.cashOnApprovalOption": "Cash on approval",
    "booking.startLabel": "Start *",
    "booking.endLabel": "End *",
    "booking.attendeesLabel": "Attendees",
    "booking.eventTitleLabel": "Event Title",
    "booking.titlePlaceholder": "e.g. Team Meeting, Training Session",
    "booking.purposeLabel": "Purpose",
    "booking.balanceDueLabel": "Balance due on arrival · Currency:",
    "booking.depositRequiredNow": "Deposit required now ({pct}%)",
    "booking.yourDetailsHeading": "👤 Your Details",
    "booking.fullNameLabel": "Full Name *",
    "booking.emailLabel": "Email *",
    "booking.phoneLabel": "Phone",
    "booking.phonePlaceholder": "+250 7XX XXX XXX",
    "booking.organisationLabel": "Organisation / Personal",
    "booking.organisationPlaceholder": "Enter organisation name or Personal",
    "booking.notesLabel": "Additional Notes",
    "booking.reviewNotice": "ℹ️ Your request will be reviewed by the facility manager. You'll be contacted at your email to confirm.",
    "booking.depositAgreementText": "I agree to pay a deposit of {amt} ({pct}% of the total) to confirm I am genuinely interested in this booking. The remaining balance is due as described above.",
    "booking.termsAgreementText": "I agree to the booking terms: the information provided is accurate, payment is due according to the selected method, and cancellation or termination may be subject to facility policy.",
    "booking.submittingText": "Submitting...",
    "booking.submitButton": "Submit Booking Request",
    "booking.backToHome": "Back to Home",
    "booking.availableSpacesLabel": "Available Spaces",
    "booking.pageTitle": "Book Your Space Today",
    "booking.pricesShownIn": "All prices shown in {currency}",
    "booking.allFacilitiesOption": "All Facilities",
    "booking.allTypesOption": "All Types",
    "booking.selectSpaceOption": "Select a space",
    "booking.continueButton": "Continue",
    "booking.loadErrorMessage": "Spaces are temporarily unavailable. Please try again.",
    "booking.retryButton": "Retry",
    "booking.noSpacesFoundHeading": "No spaces found",
    "booking.adjustSearchText": "Adjust your search or filters.",
    "booking.chooseSpacePrompt": "Choose a facility and space above to view details and start a booking.",
    "booking.allRightsReserved": "All rights reserved.",
    "publicBooking.navHome": "Home",
    "publicBooking.navAbout": "About",
    "publicBooking.navServices": "Services",
    "publicBooking.navBookSpace": "Book a Space",
    "publicBooking.navReport": "Report an Issue",
    "publicBooking.navContact": "Contact",
    "publicBooking.dashboardLink": "Dashboard →",
    "publicBooking.loginLink": "Login",
    "publicBooking.installAppLabel": "Install {app}",
    "publicBooking.dismissInstallAria": "Dismiss install prompt",
    "publicBooking.installHintAndroid": "Add fast access from your home screen.",
    "publicBooking.installHintIos": "Tap Share, then Add to Home Screen.",
    "publicBooking.installButton": "Install",
    "publicBooking.showSlideAria": "Show slide {n}",
    "publicBooking.lookInsideLabel": "A Look Inside",
    "publicBooking.lookInsideHeading": "Everything your facility needs, working together",
    "publicBooking.featureWorkOrdersTitle": "Work Orders",
    "publicBooking.featureWorkOrdersDesc": "Submit and track maintenance requests in real time.",
    "publicBooking.featureBookingsTitle": "Bookings",
    "publicBooking.featureBookingsDesc": "Reserve spaces and manage schedules with ease.",
    "publicBooking.featureMaintenanceTitle": "Maintenance",
    "publicBooking.featureMaintenanceDesc": "Preventive care keeps every facility running smoothly.",
    "publicBooking.featureAnalyticsTitle": "Analytics",
    "publicBooking.featureAnalyticsDesc": "Clear insight into usage, costs, and performance.",
    "publicBooking.trustedByLabel": "Trusted By",
    "publicBooking.ourPartnersHeading": "Our Partners",
    "publicBooking.footerBlurb": "MITI facility management platform serving businesses across Rwanda. Book spaces, manage assets, and streamline operations.",
    "publicBooking.quickLinksHeading": "Quick Links",
    "publicBooking.servicesHeading": "Services",
    "publicBooking.svcSpaceBooking": "Space Booking",
    "publicBooking.svcAssetManagement": "Asset Management",
    "publicBooking.svcMaintenance": "Maintenance",
    "publicBooking.svcWorkOrders": "Work Orders",
    "publicBooking.svcVendorManagement": "Vendor Management",
    "publicBooking.contactHeading": "Contact",
    "publicBooking.followUsHeading": "Follow us",
    "publicBooking.followOnAria": "Follow {system} on {label}",
    "publicBooking.followOnTitle": "Follow us on {label}",
    "publicBooking.allRightsReserved": "All rights reserved.",
    "publicBooking.privacyPolicyLink": "Privacy Policy",
    "publicBooking.termsLink": "Terms of Service",
    "report.submitFailedError": "Could not submit your report.",
    "report.backToHome": "Back to Home",
    "report.pageTitle": "Report a Facility Concern",
    "pay.title": "Pay your booking",
    "pay.pendingNotice": "Pay the deposit to confirm your booking automatically. We then email your invoice showing the remaining balance.",
    "pay.inactive": "This booking is not active.",
    "pay.depositButton": "Pay deposit",
    "pay.codeTitle": "Pay with the MTN MoMo code",
    "pay.codeSteps": "Dial the code below, enter your Mobile Money PIN, then enter the transaction ID from the confirmation SMS so we can verify your payment.",
    "pay.claimPending": "Payment submitted, waiting for verification:",
    "pay.claimPhone": "MTN number you paid from",
    "pay.claimRef": "Transaction ID from SMS",
    "pay.claimSubmit": "I have paid ({amount})",
    "pay.loading": "Loading...",
    "pay.invalidLink": "This payment link is not valid.",
    "pay.invoiceNo": "Invoice",
    "pay.space": "Space",
    "pay.facility": "Facility",
    "pay.period": "Period",
    "pay.total": "Total",
    "pay.paid": "Paid so far",
    "pay.balance": "Balance due",
    "pay.downloadInvoice": "Download invoice (PDF)",
    "pay.success": "Payment received! A receipt was sent to your email.",
    "pay.failed": "The payment was not completed. Please try again.",
    "pay.timeout": "We have not received confirmation yet. If you approved the payment, your receipt will be emailed shortly.",
    "pay.notConfirmed": "This booking has not been confirmed yet.",
    "pay.fullyPaid": "This booking is fully paid. Thank you!",
    "pay.onlineUnavailable": "Online payment is not available yet. Please contact {email}.",
    "pay.payDeposit": "Pay the deposit: {amount}",
    "pay.payFull": "Pay the full balance: {amount}",
    "pay.phoneLabel": "MTN or Airtel number",
    "pay.starting": "Sending request...",
    "pay.payButton": "Pay with Mobile Money",
    "pay.approvePrompt": "Check your phone and approve the payment with your Mobile Money PIN. This page updates automatically.",
    "pay.receipts": "Receipts",
    "pay.startFailed": "We could not start the payment. Please try again.",
    "report.introText": "Have a confirmed booking? Enter its email or phone number, we'll email you a code to confirm it's you, then report an issue, request help, or send a suggestion.",
    "report.emailLabel": "Booking Email *",
    "report.contactLabel": "Booking email or phone *",
    "report.contactPlaceholder": "you@example.com or 07XXXXXXXX",
    "report.contactInvalid": "Enter the email or phone number used on your confirmed booking.",
    "report.sendCode": "Send code",
    "report.resendCode": "Resend code",
    "report.codeLabel": "6-digit code *",
    "report.codeRequired": "Request a code and enter the 6-digit code we emailed you.",
    "report.codeSentNotice": "If this matches a confirmed booking, a 6-digit code was sent to the email address on that booking.",
    "report.emailPlaceholder": "you@example.com",
    "report.subjectLabel": "Subject *",
    "report.subjectPlaceholder": "What do you need help with?",
    "report.categoryLabel": "Category",
    "report.catIncident": "Facility Issue",
    "report.catMaintenance": "Maintenance Request",
    "report.catSafety": "Safety Issue",
    "report.catSuggestion": "Suggestion",
    "report.catComplaint": "Complaint",
    "report.catOther": "Other Request",
    "report.priorityLabel": "Priority",
    "report.priorityLow": "Low",
    "report.priorityMedium": "Medium",
    "report.priorityHigh": "High",
    "report.priorityUrgent": "Urgent",
    "report.detailsLabel": "Details *",
    "report.detailsPlaceholder": "Describe the issue, request, or suggestion...",
    "report.sendingText": "Sending...",
    "report.sendButton": "Send Report",
    "report.allRightsReserved": "All rights reserved.",
    "bookings.statusPending": "Pending",
    "bookings.statusConfirmed": "Confirmed",
    "bookings.statusCancelled": "Cancelled",
    "bookings.statusTerminated": "Terminated",
    "bookings.paymentUnpaid": "Unpaid",
    "bookings.paymentPartial": "Partial",
    "bookings.paymentPaid": "Paid",
    "bookings.pdfHeading": "FMS Pro — Booking Requests",
    "bookings.pdfFacilityLabel": "Facility:",
    "bookings.pdfAllFacilities": "All",
    "bookings.pdfGeneratedLabel": "Generated:",
    "bookings.pdfColGuest": "Guest",
    "bookings.pdfColEmail": "Email",
    "bookings.pdfColSpace": "Space",
    "bookings.pdfColStart": "Start",
    "bookings.pdfColEnd": "End",
    "bookings.pdfColAttendees": "Attendees",
    "bookings.pdfColStatus": "Status",
    "bookings.pageTitle": "Booking Requests",
    "bookings.pendingSuffix": "pending",
    "bookings.allFacilities": "All facilities",
    "bookings.subtitle": "— manage space booking requests",
    "bookings.refresh": "Refresh",
    "bookings.downloadPdf": "Download PDF",
    "bookings.statTotal": "Total",
    "bookings.statPending": "Pending",
    "bookings.statConfirmed": "Confirmed",
    "bookings.statCancelled": "Cancelled",
    "bookings.tabAll": "All",
    "bookings.tabPending": "Pending",
    "bookings.tabConfirmed": "Confirmed",
    "bookings.tabCancelled": "Cancelled",
    "bookings.colGuest": "Guest",
    "bookings.colSpace": "Space",
    "bookings.colWhen": "When",
    "bookings.colGuestsCount": "Guests",
    "bookings.colPurpose": "Purpose",
    "bookings.colStatus": "Status",
    "bookings.colPayment": "Payment",
    "bookings.colPublic": "Public",
    "bookings.colActions": "Actions",
    "bookings.badgePublic": "Public",
    "bookings.badgeInternal": "Internal",
    "bookings.actionViewDetailsTitle": "View details",
    "bookings.actionConfirm": "Confirm",
    "bookings.actionDeclineTitle": "Decline",
    "bookings.actionCancel": "Cancel",
    "bookings.emptyState": "No booking requests yet",
    "bookings.modalDetailsTitle": "Booking Request Details",
    "bookings.sectionGuestInfo": "👤 Guest Information",
    "bookings.labelName": "Name",
    "bookings.labelCompany": "Company",
    "bookings.labelEmail": "Email",
    "bookings.labelPhone": "Phone",
    "bookings.sectionBookingDetails": "📅 Booking Details",
    "bookings.labelSpace": "Space",
    "bookings.labelAttendees": "Attendees",
    "bookings.labelFrom": "From",
    "bookings.labelTo": "To",
    "bookings.labelPurpose": "Purpose",
    "bookings.labelNotes": "Notes",
    "bookings.labelStatus": "Status",
    "bookings.labelSource": "Source",
    "bookings.sourcePublic": "🌐 Public Booking",
    "bookings.sourceInternal": "🏢 Internal",
    "bookings.labelTerminationReason": "Termination reason",
    "bookings.labelPayment": "Payment",
    "bookings.paidOfText": "paid of",
    "bookings.amountReceivedPlaceholder": "Amount received",
    "bookings.btnSavePayment": "Save payment",
    "bookings.btnClose": "Close",
    "bookings.btnDecline": "✕ Decline",
    "bookings.btnConfirmBooking": "✓ Confirm Booking",
    "bookings.btnDelete": "Delete",
    "bookings.labelRenewBooking": "Renew booking",
    "bookings.btnRequestRenewal": "Request renewal",
    "bookings.labelTerminateBooking": "Terminate booking",
    "bookings.terminationReasonPlaceholder": "Reason for termination",
    "bookings.btnTerminate": "Terminate",
    "spaces.typeOffice": "Office",
    "spaces.typeConferenceRoom": "Conference Room",
    "spaces.typeTrainingRoom": "Training Room",
    "spaces.typeStockRoom": "Stock Room",
    "spaces.typeServerRoom": "Server Room",
    "spaces.typeBathroom": "Bathroom",
    "spaces.typeParking": "Parking",
    "spaces.typeCommonArea": "Common Area",
    "spaces.typeLab": "Lab",
    "spaces.typeOther": "Other",
    "spaces.statusAvailable": "Available",
    "spaces.statusOccupied": "Occupied",
    "spaces.statusMaintenance": "Under Maintenance",
    "spaces.statusReserved": "Reserved",
    "spaces.pageTitle": "Space Management",
    "spaces.subtitle": "Room allocation, occupancy & bookings",
    "spaces.addFacility": "Add Facility",
    "spaces.addSpace": "Add Space",
    "spaces.statTotal": "Total Spaces",
    "spaces.statAvailable": "Available",
    "spaces.statOccupied": "Occupied",
    "spaces.statOccupancyRate": "Occupancy Rate",
    "spaces.tabSpaces": "Spaces",
    "spaces.tabBuildings": "Buildings",
    "spaces.yourFacilityLabel": "Your Facility: ",
    "spaces.manageHint": "— You can manage spaces in this facility",
    "spaces.searchPlaceholder": "Search spaces...",
    "spaces.allBuildings": "All Buildings",
    "spaces.allStatus": "All Status",
    "spaces.floorLabel": "Floor",
    "spaces.btnBook": "Book",
    "spaces.emptyState": "No spaces found",
    "spaces.floorsCount": "floor(s)",
    "spaces.yourFacilityBadge": "Your Facility",
    "spaces.spacesCountLabel": "spaces",
    "spaces.managerLabel": "Manager:",
    "spaces.btnViewSpaces": "View Spaces",
    "spaces.btnEdit": "Edit",
    "spaces.btnDelete": "Delete",
    "spaces.modalEditSpace": "Edit Space",
    "spaces.modalAddSpace": "Add New Space",
    "spaces.labelBuilding": "Building *",
    "spaces.selectBuildingOption": "Select building",
    "spaces.labelSpaceName": "Space Name *",
    "spaces.labelFloor": "Floor",
    "spaces.labelRoomNumber": "Room Number",
    "spaces.labelType": "Type",
    "spaces.labelCapacity": "Capacity",
    "spaces.labelArea": "Area (m²)",
    "spaces.labelPricePerHour": "Price per hour ({currency})",
    "spaces.labelPricePerDay": "Price per day ({currency})",
    "spaces.labelPricePerMonth": "Price per month ({currency})",
    "spaces.labelStatus": "Status",
    "spaces.labelDepartment": "Department",
    "spaces.labelAssignedTo": "Assigned To",
    "spaces.noAssignmentOption": "No assignment",
    "spaces.labelAmenities": "Amenities",
    "spaces.amenitiesPlaceholder": "e.g. Projector, Whiteboard, AC",
    "spaces.btnCancel": "Cancel",
    "spaces.savingLabel": "Saving...",
    "spaces.btnSaveSpace": "Save Space",
    "spaces.modalEditFacility": "Edit Facility",
    "spaces.modalAddFacility": "Add New Facility",
    "spaces.labelFacilityName": "Facility Name *",
    "spaces.labelAddress": "Location / Address",
    "spaces.labelFloorsCount": "Number of Floors (optional)",
    "spaces.labelTotalArea": "Total Area (m²)",
    "spaces.labelOpenTime": "Opens at (booking hours)",
    "spaces.labelCloseTime": "Closes at (booking hours)",
    "spaces.hoursHint": "Optional. Guests can only book hourly slots inside these hours. Leave both empty for no limit.",
    "spaces.labelFacilityManager": "Facility Manager *",
    "spaces.requiredHint": "(required)",
    "spaces.selectManagerOption": "Select a manager",
    "spaces.managerHint": "Only managers and admins can be assigned as facility managers.",
    "spaces.btnUpdateFacility": "Update Facility",
    "spaces.btnSaveFacility": "Save Facility",
    "spaces.modalBookSpace": "Book a Space",
    "spaces.labelMeetingTitle": "Meeting Title",
    "spaces.meetingTitlePlaceholder": "e.g. Team Standup",
    "spaces.labelOrderType": "Order Type",
    "spaces.orderTypeBooking": "Short booking",
    "spaces.orderTypeRental": "Space rental",
    "spaces.labelRentalPeriod": "Rental period",
    "spaces.rentalMonths1": "1 month",
    "spaces.rentalMonths3": "3 months",
    "spaces.rentalMonths6": "6 months",
    "spaces.rentalMonths12": "12 months",
    "spaces.labelPriceUnit": "Price unit",
    "spaces.perHourOption": "Per hour ·",
    "spaces.perDayOption": "Per day ·",
    "spaces.perMonthOption": "Per month ·",
    "spaces.labelStartDateTime": "Start Date & Time *",
    "spaces.labelEndDateTime": "End Date & Time *",
    "spaces.labelAttendees": "Attendees",
    "spaces.labelPurpose": "Purpose",
    "spaces.depositRequiredLabel": "Deposit required to confirm interest ({pct}%)",
    "spaces.depositAgreementText": "The booker agrees to pay a deposit of {amount} ({pct}% of the total) to confirm genuine interest before this booking is approved. Track the deposit and remaining balance via the booking's payment status.",
    "spaces.btnConfirmBooking": "Confirm Booking",
    "assets.pageTitle": "Asset Management",
    "assets.pageSubtitleFacility": "— track equipment & lifecycle",
    "assets.pageSubtitle": "Track equipment, lifecycle & depreciation",
    "assets.addAsset": "Add Asset",
    "assets.statTotalAssets": "Total Assets",
    "assets.statTotalValue": "Total Value",
    "assets.searchPlaceholder": "Search by name, tag, serial...",
    "assets.filterAllStatus": "All Status",
    "assets.filterAllCategories": "All Categories",
    "assets.colTag": "Tag",
    "assets.colName": "Name",
    "assets.colCategory": "Category",
    "assets.colLocation": "Location",
    "assets.colStatus": "Status",
    "assets.colCondition": "Condition",
    "assets.colPurchaseDate": "Purchase Date",
    "assets.colCurrentValue": "Current Value",
    "assets.colActions": "Actions",
    "assets.noAssetsFound": "No assets found",
    "assets.modalEditTitle": "Edit Asset",
    "assets.modalAddTitle": "Add New Asset",
    "assets.fieldName": "Name *",
    "assets.fieldCategory": "Category",
    "assets.selectCategory": "Select category",
    "assets.fieldStatus": "Status",
    "assets.fieldBuilding": "Building",
    "assets.selectBuilding": "Select building",
    "assets.fieldAssignedTo": "Assigned To",
    "assets.unassigned": "Unassigned",
    "assets.fieldManufacturer": "Manufacturer",
    "assets.fieldModel": "Model",
    "assets.fieldSerialNumber": "Serial Number",
    "assets.fieldCondition": "Condition",
    "assets.fieldPurchaseDate": "Purchase Date",
    "assets.fieldPurchaseCost": "Purchase Cost ({currency})",
    "assets.fieldCurrentValue": "Current Value ({currency})",
    "assets.fieldWarrantyExpiry": "Warranty Expiry",
    "assets.fieldNotes": "Notes",
    "assets.cancel": "Cancel",
    "assets.saving": "Saving...",
    "assets.saveAsset": "Save Asset",
    "assets.statusActive": "Active",
    "assets.statusInactive": "Inactive",
    "assets.statusUnderRepair": "Under Repair",
    "assets.statusDisposed": "Disposed",
    "assets.statusLost": "Lost",
    "assets.conditionExcellent": "Excellent",
    "assets.conditionGood": "Good",
    "assets.conditionFair": "Fair",
    "assets.conditionPoor": "Poor",
    "inventory.txnStockIn": "Stock In",
    "inventory.txnStockOut": "Stock Out",
    "inventory.txnAdjusted": "Adjusted",
    "inventory.partItems": "items",
    "inventory.partStatistics": "statistics",
    "inventory.loadErrorSuffix": "could not be loaded.",
    "inventory.restartServerHint": "Please restart the backend server.",
    "inventory.loadErrorGeneric": "Inventory data could not be loaded. Please check the API connection.",
    "inventory.transactionsLoadError": "Transactions could not be loaded.",
    "inventory.filtersLoadError": "Inventory filters could not be loaded. Check that the backend is running.",
    "inventory.pageTitle": "Inventory Management",
    "inventory.pageSubtitle": "Track supplies, parts & equipment stock",
    "inventory.addItem": "Add Item",
    "inventory.statTotalItems": "Total Items",
    "inventory.statLowStock": "Low Stock",
    "inventory.statOutOfStock": "Out of Stock",
    "inventory.statTotalValue": "Total Value",
    "inventory.tabItems": "Items",
    "inventory.tabTransactions": "Transactions",
    "inventory.searchItemsPlaceholder": "Search items...",
    "inventory.statusInStock": "In Stock",
    "inventory.statusLowStock": "Low Stock",
    "inventory.statusOutOfStock": "Out of Stock",
    "inventory.searchTxnPlaceholder": "Search item, code, or person...",
    "inventory.filterAllMovements": "All Movements",
    "inventory.colCode": "Code",
    "inventory.colName": "Name",
    "inventory.colCategory": "Category",
    "inventory.colStock": "Stock",
    "inventory.colMin": "Min",
    "inventory.colUnitCost": "Unit Cost",
    "inventory.colStatus": "Status",
    "inventory.colFacility": "Facility",
    "inventory.colActions": "Actions",
    "inventory.noItemsFound": "No inventory items",
    "inventory.colDate": "Date",
    "inventory.colItem": "Item",
    "inventory.colMovement": "Movement",
    "inventory.colQty": "Qty",
    "inventory.colReason": "Reason / Notes",
    "inventory.colBy": "By",
    "inventory.colWorkOrder": "Work Order",
    "inventory.noTransactionsFound": "No stock movements recorded yet",
    "inventory.modalEditItemTitle": "Edit Item",
    "inventory.modalAddItemTitle": "Add Inventory Item",
    "inventory.fieldItemCode": "Item Code *",
    "inventory.itemCodePlaceholder": "e.g. CLN-001",
    "inventory.fieldCategory": "Category",
    "inventory.selectCategory": "Select category",
    "inventory.fieldFacility": "Facility",
    "inventory.allFacilities": "All facilities",
    "inventory.fieldUnit": "Unit",
    "inventory.unitPlaceholder": "e.g. pieces, litres",
    "inventory.fieldCurrentQty": "Current Qty",
    "inventory.fieldMinimumStock": "Minimum Stock",
    "inventory.fieldUnitCost": "Unit Cost ({currency})",
    "inventory.fieldStorageLocation": "Storage Location",
    "inventory.locationPlaceholder": "e.g. Storage Room A, Shelf 3",
    "inventory.cancel": "Cancel",
    "inventory.saving": "Saving...",
    "inventory.saveItem": "Save Item",
    "inventory.modalStockInTitle": "📥 Stock In — {name}",
    "inventory.modalStockOutTitle": "📤 Stock Out — {name}",
    "inventory.currentStockLabel": "Current stock:",
    "inventory.unitsFallback": "units",
    "inventory.fieldTransactionType": "Transaction Type",
    "inventory.optionStockInReceived": "📥 Stock In (Received)",
    "inventory.optionStockOutUsed": "📤 Stock Out (Used)",
    "inventory.optionAdjustSetQty": "🔄 Adjust (Set exact qty)",
    "inventory.fieldQuantity": "Quantity *",
    "inventory.placeholderNewTotalQty": "New total quantity",
    "inventory.placeholderQuantity": "Quantity",
    "inventory.fieldReasonStockOut": "Reason for stock out *",
    "inventory.fieldNotes": "Notes",
    "inventory.placeholderReasonOut": "e.g. Used for maintenance WO-00123",
    "inventory.placeholderReasonIn": "e.g. Received from supplier",
    "inventory.stockOutAuditNote": "Every stock-out is logged with your name, the quantity, and this reason in the Transactions tab.",
    "inventory.recording": "Recording...",
    "inventory.recordTransaction": "Record Transaction",
    "maintenance.pageTitle": "Maintenance Management",
    "maintenance.pageSubtitle": "— preventive & corrective maintenance",
    "maintenance.pageSubtitleShort": "Preventive & corrective maintenance schedules",
    "maintenance.importTooltip": "Import schedules from a spreadsheet (.xlsx, .xls, .csv)",
    "maintenance.importing": "Reading...",
    "maintenance.importButton": "Import",
    "maintenance.exportTooltip": "Export current schedules to a spreadsheet",
    "maintenance.exporting": "Exporting...",
    "maintenance.exportButton": "Export",
    "maintenance.logWorkButton": "Log Work",
    "maintenance.newScheduleButton": "New Schedule",
    "maintenance.statOverdue": "Overdue",
    "maintenance.statDueThisMonth": "Due This Month",
    "maintenance.statTotalSchedules": "Total Schedules",
    "maintenance.statTotalLogs": "Total Logs",
    "maintenance.tabSchedules": "Schedules",
    "maintenance.tabUpcoming": "Upcoming",
    "maintenance.tabLogs": "Logs",
    "maintenance.colTitle": "Title",
    "maintenance.colSpace": "Maintained Space",
    "maintenance.colAsset": "Asset",
    "maintenance.colFrequency": "Frequency",
    "maintenance.colStartDate": "Starting Date",
    "maintenance.colEndDate": "Ending Date",
    "maintenance.colAssigned": "Assigned",
    "maintenance.colLastPerformed": "Last Performed",
    "maintenance.colParts": "Parts",
    "maintenance.colActions": "Actions",
    "maintenance.freqDaily": "daily",
    "maintenance.freqWeekly": "weekly",
    "maintenance.freqMonthly": "monthly",
    "maintenance.freqQuarterly": "quarterly",
    "maintenance.freqSemiAnnual": "semi-annual",
    "maintenance.freqAnnual": "annual",
    "maintenance.markDoneTooltip": "Mark done (logs it and auto-schedules the next due date)",
    "maintenance.logTooltip": "Log maintenance (add notes, parts, cost)",
    "maintenance.edit": "Edit",
    "maintenance.delete": "Delete",
    "maintenance.noSchedules": "No schedules yet",
    "maintenance.startingLabel": "Starting: ",
    "maintenance.endingLabel": "Ending: ",
    "maintenance.assignedLabel": "Assigned: ",
    "maintenance.noUpcoming": "No upcoming maintenance in the next 30 days",
    "maintenance.logColDate": "Date",
    "maintenance.logColSpace": "Maintained Space",
    "maintenance.logColAsset": "Asset",
    "maintenance.logColDescription": "Description",
    "maintenance.logColFindings": "Findings",
    "maintenance.logColPartsUsed": "Parts Used",
    "maintenance.logColCost": "Cost",
    "maintenance.logColPerformedBy": "Performed By",
    "maintenance.noLogs": "No maintenance logs yet",
    "maintenance.editScheduleModalTitle": "Edit Schedule",
    "maintenance.newScheduleModalTitle": "New Maintenance Schedule",
    "maintenance.labelTitle": "Title *",
    "maintenance.labelDescription": "Description",
    "maintenance.labelAction": "Maintenance Action",
    "maintenance.labelAsset": "Asset",
    "maintenance.labelAffectedSpace": "Affected Space",
    "maintenance.labelFrequency": "Frequency",
    "maintenance.labelStartDate": "Starting Date *",
    "maintenance.labelEndDate": "Ending Date",
    "maintenance.labelAssignedTo": "Assigned To",
    "maintenance.labelEstimatedHours": "Estimated Hours",
    "maintenance.scheduleTitlePlaceholder": "e.g. Monthly HVAC Filter Replacement",
    "maintenance.actionPlaceholder": "Explain what work will take place and how the space may be affected",
    "maintenance.noSpecificAsset": "No specific asset",
    "maintenance.noSpecificSpace": "No specific space",
    "maintenance.unassigned": "Unassigned",
    "maintenance.endDateHint": "Auto-calculated from the start date and frequency",
    "maintenance.notifyAffectedLabel": "Email affected confirmed bookings and rentals before this action",
    "maintenance.checking": "Checking...",
    "maintenance.previewAffected": "Preview affected people",
    "maintenance.affectedCountLabel": "confirmed booking(s) or rental(s) will be notified:",
    "maintenance.rental": "Rental",
    "maintenance.booking": "Booking",
    "maintenance.noEmail": "No email address",
    "maintenance.previewHint": "Preview the overlap before saving. Only confirmed bookings and active rentals receive email notices.",
    "maintenance.plannedPartsLabel": "Planned Parts",
    "maintenance.plannedPartsHint": "Pick any items this schedule will need — they're attached as soon as you save.",
    "maintenance.noPartsPlanned": "No parts planned yet.",
    "maintenance.selectPart": "Select a part...",
    "maintenance.addPartButton": "Add",
    "maintenance.partUsed": "Used",
    "maintenance.partReady": "Ready",
    "maintenance.partShort": "Short",
    "maintenance.removeFromPlan": "Remove from plan",
    "maintenance.assetRequestsLabel": "Item/Asset Requests ({n})",
    "maintenance.assetRequestsHint": "Requests the assigned employee filed for something not in stock. Approving and getting a PO approved auto-adds it here — an item restocks and joins Planned Parts above, a new asset attaches to this schedule.",
    "maintenance.requestedBy": "Requested by {name} ·",
    "maintenance.restockItem": "Restock inventory item",
    "maintenance.newAsset": "New asset",
    "maintenance.autoAddedAfterPO": "Auto-added to this schedule after PO approval",
    "maintenance.vendorSubmissions": "Vendor submissions ({n})",
    "maintenance.approveCreateLink": "Approve & create vendor link",
    "maintenance.reject": "Reject",
    "maintenance.copyVendorLink": "Copy vendor link",
    "maintenance.approveQuotation": "Approve selected quotation",
    "maintenance.rejectQuotation": "Reject selected quotation",
    "maintenance.noAssetRequests": "No item or asset requests for this schedule.",
    "maintenance.cancel": "Cancel",
    "maintenance.saving": "Saving...",
    "maintenance.saveSchedule": "Save Schedule",
    "maintenance.createAddParts": "Create & Add Parts",
    "maintenance.logModalTitle": "Log Maintenance Work",
    "maintenance.logLabelAsset": "Asset",
    "maintenance.logLabelSchedule": "Schedule (if applicable)",
    "maintenance.logLabelDescription": "Work Description",
    "maintenance.logLabelFindings": "Findings / Observations",
    "maintenance.logLabelPartsUsed": "Parts Used",
    "maintenance.logLabelCost": "Cost ({currency})",
    "maintenance.logLabelNextDue": "Next Service Due (optional)",
    "maintenance.selectAsset": "Select asset",
    "maintenance.none": "None",
    "maintenance.workDonePlaceholder": "What was done...",
    "maintenance.notEnoughStock": "Not enough in stock",
    "maintenance.addPartUsedButton": "+ Add part used",
    "maintenance.partsDeductionHint": "Each part picked here is deducted from stock when you save — it's blocked if stock is short unless you confirm.",
    "maintenance.notesLabel": "Notes (only if not picking from inventory above)",
    "maintenance.partsNotesPlaceholder": "e.g. Filter #3M-500 (not in inventory)",
    "maintenance.nextDueHint": "Leave blank to auto-continue on the schedule's own frequency from today",
    "maintenance.savingLog": "Saving...",
    "maintenance.saveLog": "Save Log",
    "maintenance.importModalTitle": "Import Maintenance Schedules",
    "maintenance.readyToImport": "ready to import",
    "maintenance.rowsSkipped": "row(s) will be skipped (missing title or a readable date)",
    "maintenance.importColRow": "Row",
    "maintenance.importColTitle": "Title",
    "maintenance.importColFrequency": "Frequency",
    "maintenance.importColStartDate": "Start Date",
    "maintenance.importColAssigned": "Assigned",
    "maintenance.importColNotes": "Notes",
    "maintenance.importing2": "Importing...",
    "maintenance.importCount": "Import {n} schedule(s)",
    "workOrders.pageTitle": "Work Orders",
    "workOrders.pageSubtitle": "— service requests and tasks",
    "workOrders.pageSubtitleShort": "Manage service requests and tasks",
    "workOrders.newButton": "Work Order",
    "workOrders.searchPlaceholder": "Search work orders...",
    "workOrders.allStatus": "All Status",
    "workOrders.statusOpen": "Open",
    "workOrders.statusAssigned": "Assigned",
    "workOrders.statusInProgress": "In Progress",
    "workOrders.statusOnHold": "On Hold",
    "workOrders.statusCompleted": "Completed",
    "workOrders.statusCancelled": "Cancelled",
    "workOrders.allPriority": "All Priority",
    "workOrders.priorityLow": "Low",
    "workOrders.priorityMedium": "Medium",
    "workOrders.priorityHigh": "High",
    "workOrders.priorityCritical": "Critical",
    "workOrders.colWoNumber": "WO #",
    "workOrders.colTitle": "Title",
    "workOrders.colType": "Type",
    "workOrders.colPriority": "Priority",
    "workOrders.colStatus": "Status",
    "workOrders.colAssignedTo": "Assigned To",
    "workOrders.colDueDate": "Due Date",
    "workOrders.colParts": "Parts",
    "workOrders.colActions": "Actions",
    "workOrders.partsReady": "Ready",
    "workOrders.partsShort": "Short",
    "workOrders.view": "View",
    "workOrders.edit": "Edit",
    "workOrders.delete": "Delete",
    "workOrders.noneFound": "No work orders found",
    "workOrders.editModalTitle": "Edit Work Order",
    "workOrders.createModalTitle": "Work Order",
    "workOrders.labelTitle": "Title *",
    "workOrders.labelDescription": "Description",
    "workOrders.labelType": "Type",
    "workOrders.labelPriority": "Priority",
    "workOrders.labelStatus": "Status",
    "workOrders.labelAssignedTo": "Assigned To",
    "workOrders.labelBuilding": "Building",
    "workOrders.labelAsset": "Asset",
    "workOrders.labelDueDate": "Due Date",
    "workOrders.labelEstimatedHours": "Estimated Hours",
    "workOrders.labelNotes": "Notes",
    "workOrders.typeCorrective": "corrective",
    "workOrders.typePreventive": "preventive",
    "workOrders.typeInspection": "inspection",
    "workOrders.typeInstallation": "installation",
    "workOrders.typeOther": "other",
    "workOrders.unassigned": "Unassigned",
    "workOrders.selectBuilding": "Select building",
    "workOrders.noAsset": "No asset",
    "workOrders.cancel": "Cancel",
    "workOrders.saving": "Saving...",
    "workOrders.saveButton": "Save Work Order",
    "workOrders.partsNeededLabel": "Parts Needed ({n})",
    "workOrders.needHaveLabel": "Need {q}{unit} · Have {avail}{unit}",
    "workOrders.partDeducted": "Deducted",
    "workOrders.removeFromPlan": "Remove from plan",
    "workOrders.noPartsPlanned": "No parts planned for this job yet.",
    "workOrders.selectPart": "Select a part...",
    "workOrders.qtyPlaceholder": "Qty",
    "workOrders.addPartButton": "Add",
    "workOrders.completeDeductHint": "Marking this work order Completed auto-deducts these from stock — it's blocked if stock is short unless you confirm.",
    "workOrders.assetRequestsLabel": "Asset Requests ({n})",
    "workOrders.requestedBy": "Requested by {name} ·",
    "workOrders.restockItem": "Restock inventory item",
    "workOrders.newAsset": "New asset",
    "workOrders.autoAddedAfterPO": "Auto-added to this job after PO approval",
    "workOrders.vendorSubmissions": "Vendor submissions ({n})",
    "workOrders.approveCreateLink": "Approve & create vendor link",
    "workOrders.reject": "Reject",
    "workOrders.copyVendorLink": "Copy vendor link",
    "workOrders.approveQuotation": "Approve selected quotation",
    "workOrders.rejectQuotation": "Reject selected quotation",
    "workOrders.noAssetRequests": "No asset requests for this work order.",
    "workOrders.activityLogLabel": "Activity Log ({n})",
    "workOrders.noUpdates": "No updates yet.",
    "workOrders.commentPlaceholder": "Add a comment...",
    "workOrders.postButton": "Post",
    "dashboard.loading": "Loading...",
    "dashboard.allClear": "Everything looks good today — nothing urgent needs your attention.",
    "dashboard.attentionMessage": "You have {n} item(s) that need(s) your attention today.",
    "dashboard.groupWorkOrdersTitle": "Work Orders",
    "dashboard.groupWorkOrdersSubtitle": "Repair and service requests",
    "dashboard.captionTotalWO": "All work orders ever logged, open or finished",
    "dashboard.captionOpenWO": "Logged but nobody's started them yet",
    "dashboard.captionCriticalWO": "Marked urgent — these need attention first",
    "dashboard.groupFacilityTitle": "Facility",
    "dashboard.groupFacilitySubtitle": "Spaces, equipment and maintenance",
    "dashboard.captionAssets": "Equipment and items being tracked in the system",
    "dashboard.captionSpaces": "Rooms and spaces free to book right now",
    "dashboard.captionMaintenance": "Scheduled maintenance that is now late",
    "dashboard.groupInventoryTitle": "Inventory & Requests",
    "dashboard.groupInventorySubtitle": "Stock levels and pending approvals",
    "dashboard.captionLowStock": "Inventory items running low on stock",
    "dashboard.captionRequests": "Requests waiting for your approval",
    "dashboard.captionAlerts": "Notifications you haven't opened yet",
    "dashboard.trendCaption": "How many work orders came in each month (blue) versus how many got finished (green).",
    "dashboard.statusCaption": "All current work orders, split by where they stand right now.",
    "dashboard.recentCaption": "The last few work orders logged, newest first.",
    "dashboard.upcomingMaintCaption": "Maintenance coming up soon, so nothing catches you by surprise.",
    "dashboard.requestsCaption": "Items staff have requested that are waiting on your decision.",
    "dashboard.bookingsCaption": "Space bookings coming up next.",
    "settings.sectionGeneral": "General",
    "settings.systemNameLabel": "System Name",
    "settings.orgNameLabel": "Organization Name",
    "settings.orgNamePlaceholder": "My Organization",
    "settings.contactEmailLabel": "Contact Email",
    "settings.notifLogoLabel": "Notification Logo",
    "settings.removeLogo": "Remove logo",
    "settings.currencyLabel": "Currency",
    "settings.timezoneLabel": "Timezone",
    "settings.sectionHomepage": "Homepage Editor",
    "settings.heroTitleLabel": "Hero heading",
    "settings.heroTitlePlaceholder": "Book Premium Spaces",
    "settings.heroSubtitleLabel": "Hero subheading",
    "settings.heroSubtitlePlaceholder": "For Every Occasion",
    "settings.heroDescLabel": "Hero description",
    "settings.heroDescHint": "Displayed under the hero heading on the homepage. Use {currency} to insert your currency code. A language left blank falls back to the English text.",
    "settings.aboutLabel": "About text",
    "settings.aboutPlaceholder": "Tell visitors about your facilities...",
    "settings.phoneLabel": "Phone",
    "settings.addressLabel": "Address",
    "settings.publicEmailLabel": "Public contact email",
    "settings.hoursLabel": "Business hours",
    "settings.ratingStatLabel": "Hero rating stat",
    "settings.ratingStatHint": "Shown as the \"Rating\" number on the homepage",
    "settings.availabilityStatLabel": "Hero availability stat",
    "settings.availabilityStatHint": "Shown as the \"Available\" number on the homepage",
    "settings.slideTitleLabel": "Hero slide title",
    "settings.slideImageLabel": "Hero slide image",
    "settings.removeImage": "Remove image",
    "settings.slideVideoLabel": "Hero slide video (optional)",
    "settings.slideVideoHint": "If set, this plays instead of the image above. MP4, WebM, or MOV, up to 20 MB.",
    "settings.removingVideo": "Removing...",
    "settings.removeVideo": "Remove video",
    "settings.partnersHeading": "Partners",
    "settings.partnersHint": "Shown as a logo strip on the public homepage. Leave a logo slot empty to hide it.",
    "settings.partnerNameLabel": "Partner {n} name",
    "settings.partnerNamePlaceholder": "e.g. Acme Corp",
    "settings.partnerLogoLabel": "Partner {n} logo",
    "settings.socialHeading": "Social Media Links",
    "settings.legalHeading": "Legal Text",
    "settings.privacyLabel": "Privacy Policy",
    "settings.privacyPlaceholder": "Type the privacy policy text here...",
    "settings.termsLabel": "Terms of Service",
    "settings.termsPlaceholder": "Type the terms of service text here...",
    "settings.sectionSmtp": "Email / SMTP Configuration",
    "settings.smtpBanner": "Configure SMTP to enable welcome emails for new users. Gmail: use App Password with smtp.gmail.com:587",
    "settings.smtpHostLabel": "SMTP Host",
    "settings.smtpPortLabel": "Port",
    "settings.smtpFromLabel": "From Address",
    "settings.smtpUserLabel": "SMTP Username",
    "settings.smtpPasswordLabel": "SMTP Password",
    "settings.testEmailHeading": "Test Email",
    "settings.testEmailPlaceholder": "Send test to...",
    "settings.sendingTest": "Sending...",
    "settings.sendTest": "Send Test",
    "settings.testFailed": "Test failed",
    "settings.sectionDataIO": "Data Export & Import",
    "settings.dataIoIntro": "Export records as CSV for backup or import new buildings, assets, and inventory items. Imports add records and do not overwrite existing data.",
    "settings.exportCsv": "Export CSV",
    "settings.importCsv": "Import CSV",
    "settings.exportSuccess": "{resource} exported successfully.",
    "settings.exportFailed": "Export failed.",
    "settings.importFailed": "Import failed.",
    "settings.sectionBackup": "Backup & Restore",
    "settings.createBackupNow": "Create Backup Now",
    "settings.creatingBackup": "Creating backup...",
    "settings.refresh": "Refresh",
    "settings.backupTableBackup": "Backup",
    "settings.backupTableType": "Type",
    "settings.backupTableSize": "Size",
    "settings.backupTableCreated": "Created",
    "settings.backupTableActions": "Actions",
    "settings.backupFailed": "Failed",
    "settings.download": "Download",
    "settings.restore": "Restore",
    "settings.delete": "Delete",
    "settings.noBackups": "No backups yet — click \"Create Backup Now\" to make the first one.",
    "settings.restoreModalTitle": "Restore from backup?",
    "settings.restoreModalBody": "This will replace all current data with the contents of {filename} (from {date}). A safety backup of the current state will be taken first, but anything added or changed since that backup will be lost.",
    "settings.restoreModalConfirmLabel": "Type RESTORE to confirm:",
    "settings.cancel": "Cancel",
    "settings.restoring": "Restoring...",
    "settings.restoreNow": "Restore Now",
    "settings.sectionSecurity": "Security",
    "settings.securityIntro": "Role-based access control is enforced on all API endpoints:",
    "settings.roleAdminDesc": "full access: facilities, users, settings, all data",
    "settings.roleManagerDesc": "manages spaces in their assigned facility",
    "settings.roleTechnicianDesc": "manages work orders and maintenance logs",
    "settings.roleStaffDesc": "read access + submit work orders + book spaces",
    "settings.newUserPasswordNote": "All new users receive a temporary password via email and are forced to change it on first login.",
    "settings.pageTitle": "System Settings",
    "settings.pageSubtitle": "Admin-only — configure system-wide preferences",
    "settings.savedBadge": "✅ Settings saved",
    "settings.saving": "Saving...",
    "settings.saveSettings": "Save Settings",
    "users.pageTitleManager": "Team — {facility}",
    "users.pageTitleAdmin": "User Management",
    "users.myFacilityFallback": "My Facility",
    "users.subtitleManager": "Manage employees in your facility",
    "users.subtitleAdmin": "Admin — manage all system users",
    "users.addEmployee": "Add Employee",
    "users.addUser": "Add User",
    "users.colName": "Name",
    "users.colEmail": "Email",
    "users.colRole": "Role",
    "users.colJobTitle": "Job Title",
    "users.colFacility": "Facility",
    "users.colDepartment": "Department",
    "users.colStatus": "Status",
    "users.colJoined": "Joined",
    "users.colActions": "Actions",
    "users.active": "Active",
    "users.inactive": "Inactive",
    "users.noneFound": "No users found",
    "users.editModalTitle": "Edit: {name}",
    "users.fullNameLabel": "Full Name",
    "users.emailLabel": "Email Address",
    "users.roleLabel": "Role",
    "users.jobTitleLabel": "Job Title",
    "users.facilityLabel": "Assign to Facility",
    "users.departmentLabel": "Department",
    "users.phoneLabel": "Phone",
    "users.statusLabel": "Status",
    "users.noFacility": "No facility",
    "users.cancel": "Cancel",
    "users.saving": "Saving...",
    "users.saveChanges": "Save Changes",
    "users.createModalTitleManager": "Add Employee to Your Facility",
    "users.createModalTitleAdmin": "Add New User",
    "users.tempPasswordNote": "A temporary password will be generated and emailed to the user.",
    "users.willBeAddedTo": "Will be added to: {facility}",
    "users.creating": "Creating...",
    "users.createUser": "Create User",
    "users.createdModalTitle": "User Created",
    "users.accountCreatedFor": "Account created for {name}",
    "users.welcomeEmailSent": "✅ Welcome email sent to {email} with login credentials.",
    "users.welcomeEmailFailed": "⚠️ Email not sent ({msg}). Please share credentials manually.",
    "users.canChangePasswordNote": "The user can change their password anytime from the sidebar after logging in.",
    "users.done": "Done",
    "role.admin": "Admin",
    "role.manager": "Manager",
    "role.employee": "Employee",
    "developer.createSystemFailed": "Could not create system",
    "developer.openSystemFailed": "Could not open that system",
    "developer.backupHeading": "Backup & Restore",
    "developer.backupIntro": "Backs up the database this session is currently in — the main platform database when you're signed in as developer, or a specific system's database while you're inside it via \"Open\". To back up a different system, open it first, then come back to Settings there.",
    "developer.systemsHeading": "System instances",
    "developer.systemsIntro": "Each system gets its own separate, fully isolated database — its own users, spaces, work orders, assets, branding and settings. Nothing entered in one system is visible from another.",
    "developer.creatingSystem": "Creating…",
    "developer.createSystem": "Create",
    "developer.credsReady": "\"{name}\" is ready — save these credentials now, they won't be shown again",
    "developer.credsAdminEmail": "Admin email:",
    "developer.credsTempPassword": "Temp password:",
    "developer.credsSignInLink": "Sign-in link:",
    "developer.copyPassword": "Copy password",
    "developer.copyLink": "Copy link",
    "developer.dismiss": "Dismiss",
    "developer.noSystems": "No systems yet — create the first one above.",
    "developer.systemActive": "Active",
    "developer.systemDeactivated": "Deactivated",
    "developer.copySignInLink": "Copy sign-in link",
    "developer.openButton": "Open",
    "developer.deactivateButton": "Deactivate",
    "developer.activateButton": "Activate",
    "developer.deleteSystemTitle": "Permanently delete this system and all of its data",
    "developer.deleteButton": "Delete",
    "developer.newSystemNamePlaceholder": "New system name (e.g. Acme Co)",
    "developer.adminEmailPlaceholder": "Admin email (optional)",
    "developer.createBackupNow": "Create Backup Now",
    "developer.creatingBackup": "Creating backup...",
    "developer.refresh": "Refresh",
    "developer.backupTableBackup": "Backup",
    "developer.backupTableType": "Type",
    "developer.backupTableSize": "Size",
    "developer.backupTableCreated": "Created",
    "developer.backupTableActions": "Actions",
    "developer.backupFailed": "Failed",
    "developer.download": "Download",
    "developer.restore": "Restore",
    "developer.delete": "Delete",
    "developer.noBackups": "No backups yet — click \"Create Backup Now\" to make the first one.",
    "developer.restoreModalTitle": "Restore from backup?",
    "developer.restoreModalBody": "This will replace all current data with the contents of {filename} (from {date}). A safety backup of the current state will be taken first, but anything added or changed since that backup will be lost.",
    "developer.restoreModalConfirmLabel": "Type RESTORE to confirm:",
    "developer.cancel": "Cancel",
    "developer.restoring": "Restoring...",
    "developer.restoreNow": "Restore Now",
    "messages.postingStatus": "Posting...",
    "messages.newBadge": "new",
    "myTasks.statusCancelled": "Cancelled",
    "myTasks.requestItemHeading": "Request an item or asset",
    "myTasks.newAssetTab": "New asset",
    "myTasks.restockItemTab": "Restock item",
    "myTasks.selectInventoryItem": "Select inventory item...",
    "myTasks.assetNeededPlaceholder": "Asset needed (e.g. new drill)",
    "myTasks.sendRequestButton": "Send request",
    "myTasks.partDeducted": "Deducted",
    "myTasks.partReady": "Ready",
    "myTasks.noPartsLogged": "Nothing logged as used yet.",
    "myTasks.autoDeductNote": "Marking this work order Completed auto-deducts these from stock.",
    "myTasks.activityLog": "Activity Log",
    "myTasks.submitProgressReport": "Submit progress report",
    "myTasks.nextDue": "Next Due",
    "myTasks.logMaintenanceWork": "Log maintenance work",
    "myTasks.itemsUsedFromStock": "Items used (from stock)",
    "myTasks.needSomethingElseNote": "Need something not in stock instead? Close this and use \"Request item/asset\".",
    "myTasks.logAsDone": "Log as done",
    "myTasks.requestItemAssetButton": "Request item/asset",
    "myTasks.logWorkButton": "Log work",
    "myTasks.noMaintenanceJobs": "No maintenance jobs assigned to you yet",
    "myTasks.createWorkOrderButton": "Create Work Order",
    "myTasks.categoryOptional": "Category (optional)",
    "reports.typeIncident": "Incident",
    "reports.statusNew": "New",
    "reports.statusRead": "Read",
    "reports.statusResolved": "Resolved",
    "reports.statusDismissed": "Dismissed",
    "reports.statPending": "Pending",
    "reports.managersReply": "Manager's Reply",
    "reports.guestBookingFallback": "Guest booking",
    "reports.facilityReportFallback": "Facility report",
    "reports.noEmailFallback": "No email",
    "reports.facilityReportsSubtitle": "{facility} — employee reports",
    "reports.priorityUrgentEmoji": "🚨 Urgent",
    "reports.relatedSpaceBooking": "Related Space Booking",
    "assets.fieldAssetTag": "Asset Tag (optional)",
    "assets.assetTagPlaceholder": "Leave blank to generate automatically",
    "assets.fieldSpace": "Room / Location",
    "assets.selectSpace": "Not in a specific room",
    "assets.selectBuildingFirst": "Select a building first",
    "assets.spaceHint": "Where the asset is kept: office, store, parking... Add rooms under Spaces.",
    "assets.importButton": "Import",
    "assets.importTitle": "Import assets",
    "assets.importHelp": "Upload a CSV or Excel file. Only the name column is required; leave asset_tag empty and tags are generated automatically. Buildings, rooms and categories are matched by name, and new categories are created for you.",
    "assets.importTemplate": "Download template",
    "assets.importStart": "Start import",
    "assets.importing": "Importing…",
    "assets.importDone": "{n} assets imported.",
    "assets.importTagsGenerated": "{n} tags were generated automatically.",
    "assets.importRow": "Row",
    "assets.importMoreErrors": "…and {n} more rows with problems",
    "assets.importNoFile": "Please choose a file first.",
    "assets.importFailed": "Import failed.",
    "publicLink.title": "Your public page",
    "publicLink.description": "Share these links so visitors can see your facilities, book spaces and report problems. Your facility shows up once it has at least one space with status Available.",
    "publicLink.home": "Public booking page",
    "publicLink.report": "Report an issue page",
    "publicLink.copy": "Copy link",
    "publicLink.copied": "Copied",
    "publicLink.open": "Open",
    "publicLink.noAvailable": "No space is set to Available yet, so visitors will see an empty page. Add a space (or set one to Available) below.",
    "publicLink.loggedInNote": "While you are logged in, open the link in a private window or another browser to see what visitors see.",
    "settings.smtpTrust": "Your email password stays on this server, is hidden once saved, and is only used to connect to your email provider when the system sends an email. Use an app password or a dedicated sender address, never your personal password.",
  },
  fr: {
    "signup.title": "Créez votre compte",
    "signup.subtitle": "Créez une nouvelle organisation ou rejoignez votre équipe.",
    "signup.tabOrg": "Nouvelle organisation",
    "signup.tabJoin": "Rejoindre mon équipe",
    "signup.name": "Nom complet",
    "signup.email": "Adresse e-mail",
    "signup.phone": "Téléphone (facultatif)",
    "signup.orgName": "Nom de l'organisation",
    "signup.orgCode": "Code de l'organisation",
    "signup.joinCode": "Code d'inscription du personnel",
    "signup.joinHint": "Demandez à votre administrateur le code de l'organisation et le code d'inscription du personnel.",
    "signup.password": "Mot de passe",
    "signup.passwordHint": "Au moins 12 caractères.",
    "signup.confirmPassword": "Confirmez le mot de passe",
    "signup.passwordsMismatch": "Les mots de passe ne correspondent pas",
    "signup.passwordTooShort": "Le mot de passe doit contenir au moins 12 caractères",
    "signup.agreePrefix": "En vous inscrivant, vous acceptez nos",
    "signup.terms": "Conditions d'utilisation",
    "signup.and": "et notre",
    "signup.privacy": "Politique de confidentialité",
    "signup.submit": "Créer le compte",
    "signup.submitting": "Création...",
    "signup.haveAccount": "Vous avez déjà un compte ?",
    "signup.signIn": "Se connecter",
    "signup.checkEmailTitle": "Vérifiez votre e-mail",
    "signup.checkEmailBody": "Nous avons envoyé un lien de confirmation à {email}. Ouvrez-le pour terminer l'inscription. Le lien est valable 24 heures.",
    "signup.resend": "Renvoyer l'e-mail",
    "signup.resent": "Si une inscription est en attente pour cet e-mail, un nouveau lien a été envoyé.",
    "signup.failed": "L'inscription a échoué. Veuillez réessayer.",
    "signup.verifying": "Confirmation de votre e-mail...",
    "signup.verifyOrg": "Votre organisation « {org} » est prête.",
    "signup.verifyJoin": "Vous avez rejoint « {org} ».",
    "signup.systemCode": "Votre code système",
    "signup.systemCodeNote": "Saisissez ce code lorsque vous vous connectez sur le site ou dans l'application.",
    "signup.continueSignIn": "Continuer vers la connexion",
    "signup.verifyFailed": "Ce lien de confirmation est invalide ou a expiré.",
    "signup.tryAgain": "S'inscrire à nouveau",
    "login.newHere": "Nouveau sur MITIFM ?",
    "login.createAccount": "Créer un compte",
    "nav.billing": "Abonnement",
    "billing.title": "Abonnement",
    "billing.subtitle": "Gérez votre formule et vos paiements.",
    "billing.statusTrialing": "Essai gratuit",
    "billing.statusActive": "Actif",
    "billing.statusGrace": "Paiement en retard",
    "billing.statusReadOnly": "Expiré (lecture seule)",
    "billing.trialLeft": "Il reste {days} jours à votre essai gratuit.",
    "billing.activeUntil": "Payé jusqu'au {date}.",
    "billing.graceLeft": "Votre formule est terminée. Payez dans les {days} jours pour éviter le mode lecture seule.",
    "billing.readOnly": "Votre abonnement a expiré. Le système est en lecture seule : vous pouvez consulter vos données mais pas les modifier. Choisissez une formule pour continuer.",
    "billing.plan": "Formule",
    "billing.users": "Jusqu'à {n} utilisateurs",
    "billing.unlimitedUsers": "Utilisateurs illimités",
    "billing.choosePlan": "Choisissez une formule",
    "billing.monthly": "Mensuel",
    "billing.yearly": "Annuel",
    "billing.yearlyHint": "Meilleure offre",
    "billing.perMonth": "/ mois",
    "billing.perYear": "/ an",
    "billing.currentBadge": "Formule actuelle",
    "billing.payButton": "Payer par Mobile Money",
    "billing.phoneLabel": "Numéro Mobile Money (MTN ou Airtel)",
    "billing.phonePlaceholder": "ex. 0788123456",
    "billing.phoneHelp": "Nous enverrons une demande de paiement à ce téléphone. Validez-la en saisissant votre code PIN Mobile Money.",
    "billing.hostedHelp": "Vous paierez sur la page sécurisée de Flutterwave par Mobile Money (MTN ou Airtel) ou par carte bancaire. Nous ne voyons jamais votre carte ni votre code PIN.",
    "billing.phoneInvalid": "Saisissez un numéro MTN ou Airtel valide, par exemple 0788123456.",
    "billing.resultApprove": "Regardez votre téléphone et validez la demande de paiement (saisissez votre code PIN Mobile Money).",
    "billing.resultLate": "Nous n'avons pas encore reçu la confirmation. Si vous avez validé le paiement, votre abonnement s'activera tout seul dans quelques minutes.",
    "billing.redirecting": "Envoi de la demande de paiement...",
    "billing.paymentsOff": "Le paiement en ligne n'est pas encore disponible. Veuillez contacter support@mitifm.com.",
    "billing.secureNote": "Les paiements passent directement par MTN Mobile Money et Airtel Money. Nous ne voyons ni ne conservons votre code PIN Mobile Money.",
    "billing.history": "Historique des paiements",
    "billing.noPayments": "Aucun paiement pour le moment.",
    "billing.colDate": "Date",
    "billing.colPlan": "Formule",
    "billing.colAmount": "Montant",
    "billing.colStatus": "Statut",
    "billing.paid": "Payé",
    "billing.pending": "En attente",
    "billing.failed": "Échoué",
    "billing.checkoutFailed": "Nous n'avons pas pu démarrer le paiement. Veuillez réessayer.",
    "billing.noneNeeded": "Ce système n'a pas besoin d'abonnement.",
    "billing.resultChecking": "Confirmation de votre paiement...",
    "billing.resultSuccess": "Paiement reçu. Merci !",
    "billing.resultSuccessBody": "Votre abonnement est actif.",
    "billing.resultPending": "Nous attendons encore la confirmation du prestataire de paiement. Cette page se met à jour toute seule. Si vous avez validé le paiement sur votre téléphone, cela ne devrait prendre qu'une minute.",
    "billing.resultFailed": "Le paiement n'a pas abouti. Vous pouvez réessayer.",
    "billing.resultUnknown": "Nous n'avons pas trouvé ce paiement.",
    "billing.backToBilling": "Retour à l'abonnement",
    "billing.bannerTrial": "Essai gratuit : {days} jours restants.",
    "billing.bannerGrace": "Votre formule est terminée. Il reste {days} jours avant le passage en lecture seule.",
    "billing.bannerReadOnly": "Abonnement expiré : le système est en lecture seule.",
    "billing.bannerAction": "Gérer l'abonnement",
    "billing.bannerAskAdmin": "Demandez à votre administrateur de renouveler.",
    "billing.includes": "Services inclus",
    "billing.feature.maintenance": "Maintenance préventive",
    "billing.feature.inventory": "Inventaire et pièces",
    "billing.feature.asset_requests": "Demandes d'actifs aux fournisseurs et devis",
    "billing.feature.bookings": "Réservations d'espaces et page de réservation publique",
    "billing.feature.data_tools": "Import et export de données",
    "joinCode.title": "Inscription du personnel",
    "joinCode.description": "Permettez à votre personnel de créer son propre compte. Il a besoin du code de votre organisation et de ce code d'inscription.",
    "joinCode.orgCode": "Code de l'organisation",
    "joinCode.joinCode": "Code d'inscription du personnel",
    "joinCode.allow": "Autoriser l'inscription du personnel",
    "joinCode.regenerate": "Nouveau code",
    "joinCode.regenerateConfirm": "Créer un nouveau code d'inscription ? L'ancien code ne fonctionnera plus.",
    "joinCode.copyInvite": "Copier le lien d'invitation",
    "joinCode.copied": "Copié",
    "joinCode.unavailable": "L'inscription du personnel n'est disponible que pour les systèmes d'organisation.",
    "joinCode.note": "Les nouveaux membres ont un accès limité (Mes tâches). Vous pouvez changer leur rôle dans Utilisateurs.",
    "joinCode.failed": "Impossible de mettre à jour l'inscription du personnel. Veuillez réessayer.",
    'language.english': 'Anglais',
    'language.french': 'Français',
    'language.kinyarwanda': 'Kinyarwanda',
    'language.label': 'Langue',
    'nav.dashboard': 'Tableau de bord',
    'nav.workOrders': 'Ordres de travail',
    'nav.assets': 'Actifs',
    'nav.facilities': 'Installations',
    'nav.maintenance': 'Maintenance',
    'nav.inventory': 'Inventaire',
    'nav.bookings': 'Réservations',
    'nav.reportsInbox': 'Boîte de rapports',
    'nav.messages': 'Messages',
    'nav.myTeam': 'Mon équipe',
    'nav.users': 'Utilisateurs',
    'nav.settings': 'Paramètres',
    'nav.myTasks': 'Mes tâches',
    'nav.myReports': 'Mes rapports',
    'nav.developer': 'Développeur',
    'sidebar.profile': 'Menu principal',
    'sidebar.myWork': 'Mon travail',
    'sidebar.administration': 'Administration',
    'sidebar.changePassword': 'Changer le mot de passe',
    'sidebar.logout': 'Déconnexion',
    'sidebar.publicBooking': 'Page de réservation publique',
    'header.hello': 'Bonjour',
    'header.systemTitle': 'Système de gestion des installations MITI',
    'header.facilityManagement': 'Gestion des installations',
    'login.title': 'Connectez-vous à votre compte',
    'login.subtitle': 'Saisissez vos identifiants pour accéder au système',
    'login.email': 'Adresse e-mail',
    'login.password': 'Mot de passe',
    'login.systemCode': 'Code système (facultatif)',
    'login.systemCodeHint': "Laissez vide sauf si votre organisation vous a fourni un code système.",
    'login.forgotPassword': 'Mot de passe oublié ?',
    'login.signIn': 'Se connecter',
    'login.signingIn': 'Connexion...',
    'login.demoCredentials': 'Identifiants de démonstration',
    'login.changePassword': 'Changer le mot de passe',
    'login.cancel': 'Annuler',
    'login.save': 'Enregistrer',
    'login.minimum6': 'Minimum 6 caractères',
    'login.passwordsDoNotMatch': 'Les mots de passe ne correspondent pas',
    'login.passwordChanged': 'Mot de passe modifié avec succès !',
    'login.failed': 'Échec de la connexion. Veuillez réessayer.',
    'role.employee': 'Employé',
    'account.currentPassword': 'Mot de passe actuel',
    'account.newPassword': 'Nouveau mot de passe',
    'account.confirmNewPassword': 'Confirmer le nouveau mot de passe',
    'account.confirmPassword': 'Confirmer le mot de passe',
    'account.min12': 'Minimum 12 caractères',
    'account.reenterPassword': 'Saisissez de nouveau le mot de passe',
    'account.saving': 'Enregistrement...',
    'account.passwordsDoNotMatch': 'Les mots de passe ne correspondent pas',
    'account.changeFailed': 'Impossible de modifier le mot de passe',
    'account.forgotTitle': 'Mot de passe oublié ?',
    'account.forgotDescription': 'Saisissez l’e-mail de votre compte et nous vous enverrons un lien de réinitialisation.',
    'account.sendResetLink': 'Envoyer le lien de réinitialisation',
    'account.sending': 'Envoi...',
    'account.backToLogin': 'Retour à la connexion',
    'account.resetEmailFailed': 'Impossible d’envoyer l’e-mail de réinitialisation',
    'account.resetTitle': 'Définir un nouveau mot de passe',
    'account.resetDescription': 'Choisissez un nouveau mot de passe pour votre compte FMS.',
    'account.returnToLogin': 'Retour à la connexion',
    'account.resetPassword': 'Réinitialiser le mot de passe',
    'account.resetFailed': 'Impossible de réinitialiser le mot de passe',
    'account.showPassword': 'Afficher le mot de passe',
    'account.hidePassword': 'Masquer le mot de passe',
    'navigation.open': 'Ouvrir le menu de navigation',
    'navigation.close': 'Fermer le menu de navigation',
    'common.loading': 'Chargement...', 'common.quantity': 'Quantité', 'common.notes': 'Notes', 'common.submitting': 'Envoi...',
    'developer.deleteFailed': 'Impossible de supprimer ce système', 'developer.title': 'Commandes développeur', 'developer.subtitle': 'Disponibilité globale du système et mode maintenance', 'developer.loadFailed': 'Impossible de charger l’état du système', 'developer.updateFailed': 'Impossible de mettre à jour l’état du système', 'developer.deactivateConfirm': 'Désactiver tout le système ? Les utilisateurs et les services publics seront bloqués jusqu’à sa réactivation.', 'developer.active': 'Le système est actif', 'developer.inactive': 'Le système est désactivé', 'developer.available': 'Les utilisateurs et les services publics sont disponibles.', 'developer.developerOnly': 'Seuls les développeurs peuvent accéder au système.', 'developer.updating': 'Mise à jour...', 'developer.deactivate': 'Désactiver tout le système', 'developer.activate': 'Activer tout le système', 'developer.deactivateSystemConfirm': 'Désactiver « {name} » ? Ses utilisateurs ne pourront plus se connecter tant qu’il ne sera pas réactivé.', 'developer.deleteSystemConfirm': 'Supprimer définitivement « {name} » ?\n\nCela DÉTRUIRA DÉFINITIVEMENT toute sa base de données — chaque utilisateur, espace, ordre de travail, actif, réservation et paramètre. Cette action est IRRÉVERSIBLE.\n\nLa désactivation, elle, bloque simplement les connexions et peut être annulée plus tard.', 'developer.deleteSystemPrompt': 'Pour confirmer la suppression définitive, tapez exactement le nom du système : {name}', 'developer.deleteSystemNameMismatch': 'Le nom du système ne correspond pas — suppression annulée.',
    'vendor.invalidLink': 'Ce lien est invalide ou a expiré', 'vendor.submitFailed': 'Impossible d’envoyer le devis', 'vendor.loading': 'Chargement de la demande...', 'vendor.submitted': 'Devis envoyé', 'vendor.review': 'L’équipe de l’installation examinera votre devis et vous contactera.', 'vendor.title': 'Devis fournisseur', 'vendor.description': 'Veuillez fournir votre devis pour l’actif demandé.', 'vendor.name': 'Nom du fournisseur *', 'vendor.email': 'E-mail du fournisseur *', 'vendor.amount': 'Montant du devis *', 'vendor.deliveryTime': 'Délai de livraison *', 'vendor.deliveryPlaceholder': 'p. ex. 5 jours ouvrables', 'vendor.submit': 'Envoyer le devis',
    'reminder.endingNow': 'Se termine maintenant', 'reminder.oneMinute': '1 min restante', 'reminder.minutes': 'min restantes', 'reminder.bookingEnding': 'Réservation bientôt terminée', 'reminder.rentalRenewal': 'Renouvellement de location dû', 'reminder.guest': 'Invité', 'reminder.new': 'nouvelles notifications', 'reminder.title': 'Notifications', 'reminder.enableDescription': 'Activez les alertes sur téléphone pour les réservations, rapports, ordres et devis.', 'reminder.enable': 'Activer', 'reminder.blocked': 'Les alertes du téléphone sont bloquées. Activez les notifications pour ce site dans les paramètres du navigateur.', 'reminder.none': 'Aucune notification', 'reminder.renewalIn': 'Renouvellement de location dans', 'reminder.days': 'jours', 'reminder.tenant': 'Locataire', 'reminder.noEmail': 'Pas d’e-mail', 'reminder.ends': 'Se termine :', 'reminder.people': 'personnes', 'reminder.refreshes': 'Les alertes sont actualisées toutes les 30 secondes',
    'dashboard.overview': 'Vue d’ensemble de toutes les opérations des installations',
    'dashboard.facilityOverview': 'aperçu de votre installation',
    'dashboard.unavailable': 'Tableau de bord indisponible',
    'dashboard.loadError': 'Impossible de charger les données du tableau de bord. Vérifiez que le serveur fonctionne.',
    'dashboard.refresh': 'Actualiser',
    'dashboard.retry': 'Réessayer',
    'dashboard.greeting': 'Heureux de vous revoir,',
    'dashboard.attention': 'Voici ce qui nécessite votre attention aujourd’hui.',
    'dashboard.totalWorkOrders': 'Total des ordres de travail',
    'dashboard.openOrders': 'Ordres ouverts',
    'dashboard.criticalPriority': 'Priorité critique',
    'dashboard.totalAssets': 'Total des actifs',
    'dashboard.availableSpaces': 'Espaces disponibles',
    'dashboard.overdueMaintenance': 'Maintenance en retard',
    'dashboard.lowStock': 'Stock faible',
    'dashboard.assetRequests': 'Demandes d’actifs',
    'dashboard.unreadAlerts': 'Alertes non lues',
    'dashboard.workOrderTrend': 'Tendance des ordres de travail (6 derniers mois)',
    'dashboard.created': 'Créés',
    'dashboard.completed': 'Terminés',
    'dashboard.workOrderStatus': 'État des ordres de travail',
    'dashboard.recentWorkOrders': 'Ordres de travail récents',
    'dashboard.viewAll': 'Voir tout',
    'dashboard.workOrderNumber': 'N° OT',
    'dashboard.title': 'Titre',
    'dashboard.priority': 'Priorité',
    'dashboard.status': 'Statut',
    'dashboard.noWorkOrders': 'Aucun ordre de travail',
    'dashboard.upcomingMaintenance': 'Maintenance à venir',
    'dashboard.noUpcomingMaintenance': 'Aucune maintenance à venir',
    'dashboard.overdue': 'En retard',
    'dashboard.days': 'jours',
    'dashboard.general': 'Général',
    'dashboard.request': 'Demande',
    'dashboard.requester': 'Demandeur',
    'dashboard.date': 'Date',
    'dashboard.quantity': 'Qté',
    'dashboard.noAssetRequests': 'Aucune demande d’actif',
    'dashboard.upcomingBookings': 'Réservations à venir',
    'dashboard.untitledBooking': 'Réservation sans titre',
    'dashboard.noUpcomingBookings': 'Aucune réservation à venir',
    'status.open': 'Ouvert',
    'status.inProgress': 'En cours',
    'status.completed': 'Terminé'
    ,'priority.low': 'Faible',
    'priority.medium': 'Moyenne',
    'priority.high': 'Élevée',
    'priority.critical': 'Critique',
    'status.assigned': 'Attribué',
    'status.onHold': 'En attente',
    'status.cancelled': 'Annulé',
    'status.pending': 'En attente',
    'status.approved': 'Approuvé',
    'status.rejected': 'Rejeté',
    "layout.logoAlt": "logo",
    "layout.tagline": "Gestion des installations",
    "layout.myFacilityFallback": "Mon site",
    "about.backToHome": "Retour à l'accueil",
    "about.logoAlt": "logo",
    "about.facilityManagementLabel": "GESTION DES INSTALLATIONS",
    "about.footerRights": "Tous droits réservés.",
    "contact.backToHome": "Retour à l'accueil",
    "contact.logoAlt": "logo",
    "contact.facilityManagementLabel": "GESTION DES INSTALLATIONS",
    "contact.footerRights": "Tous droits réservés.",
    "services.backToHome": "Retour à l'accueil",
    "services.logoAlt": "logo",
    "services.facilityManagementLabel": "GESTION DES INSTALLATIONS",
    "services.footerRights": "Tous droits réservés.",
    "legal.backToHome": "Retour à l'accueil",
    "legal.logoAlt": "logo",
    "legal.facilityManagementLabel": "GESTION DES INSTALLATIONS",
    "legal.footerRights": "Tous droits réservés.",
    "about.eyebrow": "À propos de nous",
    "about.heroTitle": "Gérer les installations plus intelligemment",
    "about.feature1Title": "Infrastructure moderne",
    "about.feature1Desc": "Des installations de pointe équipées des dernières technologies pour un environnement productif.",
    "about.feature2Title": "Sécurisé et bien géré",
    "about.feature2Desc": "Surveillance de sécurité 24h/24 et 7j/7, contrôle d'accès, et l'équipe de gestion des installations MITI.",
    "about.feature3Title": "Opérations durables",
    "about.feature3Desc": "Des systèmes économes en énergie et des pratiques respectueuses de l'environnement pour réduire l'impact écologique.",
    "about.feature4Title": "Assistance professionnelle",
    "about.feature4Desc": "Des gestionnaires d'installations dévoués et du personnel d'assistance pour répondre rapidement à vos besoins.",
    "contact.eyebrow": "Contactez-nous",
    "contact.heroTitle": "Entrer en contact",
    "contact.heroSubtitle": "Des questions sur les réservations, les tarifs ou la gestion des installations ? Nous sommes là pour vous aider.",
    "contact.phoneLabel": "Téléphone",
    "contact.emailReplyTime": "Nous répondons dans les 24 heures",
    "contact.emailLabel": "E-mail",
    "contact.locationLabel": "Emplacement",
    "contact.locationSubtitle": "Visitez notre établissement principal",
    "services.eyebrow": "Nos services",
    "services.heroTitle": "Tout ce dont vous avez besoin",
    "services.heroSubtitle": "De la réservation d'espaces à la gestion complète des installations, nous couvrons tout.",
    "services.spaceBookingTitle": "Réservation d'espaces",
    "services.spaceBookingDesc": "Réservez des salles de conférence, des salles de formation, des bureaux et plus encore, avec disponibilité en temps réel et confirmation instantanée.",
    "services.assetManagementTitle": "Gestion des actifs",
    "services.assetManagementDesc": "Suivez et gérez tous les équipements, machines et ressources de l'établissement avec un suivi du cycle de vie et de la dépréciation.",
    "services.maintenanceTitle": "Entretien",
    "services.maintenanceDesc": "Planification de la maintenance préventive et corrective pour maintenir votre établissement à un niveau de performance optimal.",
    "services.workOrdersTitle": "Ordres de travail",
    "services.workOrdersDesc": "Soumettez et suivez les demandes de service avec gestion des priorités et mises à jour du statut en temps réel.",
    "services.bookingManagementTitle": "Gestion des réservations",
    "services.bookingManagementDesc": "Visibilité complète de toutes les réservations d'espaces avec flux d'approbation, détection des conflits et vue calendrier.",
    "services.paymentsTitle": "Paiements en {currency}",
    "services.paymentsDesc": "Toutes les locations d'espaces sont facturées en {currency}. Tarification claire par heure avec le montant affiché avant la réservation.",
    "legal.termsTitle": "Conditions d'utilisation",
    "legal.privacyTitle": "Politique de confidentialité",
    "legal.notFilledIn": "Cette page n'a pas encore été remplie.",
    "booking.typeOffice": "Bureau",
    "booking.typeConferenceRoom": "Salle de conférence",
    "booking.typeTrainingRoom": "Salle de formation",
    "booking.typeStockRoom": "Entrepôt",
    "booking.typeServerRoom": "Salle des serveurs",
    "booking.typeBathroom": "Toilettes",
    "booking.typeParking": "Parking",
    "booking.typeCommonArea": "Espace commun",
    "booking.typeLab": "Laboratoire",
    "booking.typeOther": "Autre",
    "booking.endAfterStartError": "L'heure de fin doit être après l'heure de début",
    "booking.submitFailedError": "Échec de la réservation. Veuillez réessayer.",
    "booking.floorLabel": "Étage",
    "booking.peopleSuffix": "personnes",
    "booking.rentalSubmittedTitle": "Commande de location envoyée !",
    "booking.bookingSubmittedTitle": "Demande de réservation envoyée !",
    "booking.referenceLabel": "Référence de réservation",
    "booking.depositDueLabel": "Acompte à payer maintenant (pour confirmer l'intérêt)",
    "booking.percentOfTotal": "% de la valeur totale de la commande",
    "booking.totalOrderValue": "Valeur totale de la commande :",
    "booking.managerWillConfirm": "Le gestionnaire confirmera votre réservation à",
    "booking.doneButton": "Terminé",
    "booking.alreadyBookedLabel": "Déjà réservé :",
    "booking.moreSuffix": "+{n} autres",
    "booking.orderDetailsHeading": "📅 Détails de la commande",
    "booking.orderTypeLabel": "Type de commande *",
    "booking.shortBookingOption": "Réservation courte",
    "booking.spaceRentalOption": "Location d'espace",
    "booking.rentalPeriodLabel": "Durée de location *",
    "booking.month1": "1 mois",
    "booking.month3": "3 mois",
    "booking.month6": "6 mois",
    "booking.month12": "12 mois",
    "booking.priceUnitLabel": "Unité de tarification *",
    "booking.perHourOption": "Par heure ·",
    "booking.perDayOption": "Par jour ·",
    "booking.perMonthOption": "Par mois ·",
    "booking.paymentMethodLabel": "Mode de paiement *",
    "booking.bankTransferOption": "Virement bancaire",
    "booking.mobileMoneyOption": "Mobile Money",
    "booking.cardOption": "Carte",
    "booking.cashOnApprovalOption": "Espèces à l'approbation",
    "booking.startLabel": "Début *",
    "booking.endLabel": "Fin *",
    "booking.attendeesLabel": "Participants",
    "booking.eventTitleLabel": "Titre de l'événement",
    "booking.titlePlaceholder": "ex. Réunion d'équipe, Session de formation",
    "booking.purposeLabel": "Objet",
    "booking.balanceDueLabel": "Solde dû à l'arrivée · Devise :",
    "booking.depositRequiredNow": "Acompte requis maintenant ({pct}%)",
    "booking.yourDetailsHeading": "👤 Vos informations",
    "booking.fullNameLabel": "Nom complet *",
    "booking.emailLabel": "E-mail *",
    "booking.phoneLabel": "Téléphone",
    "booking.phonePlaceholder": "+250 7XX XXX XXX",
    "booking.organisationLabel": "Organisation / Personnel",
    "booking.organisationPlaceholder": "Entrez le nom de l'organisation ou « Personnel »",
    "booking.notesLabel": "Remarques supplémentaires",
    "booking.reviewNotice": "ℹ️ Votre demande sera examinée par le gestionnaire de l'établissement. Vous serez contacté par e-mail pour confirmation.",
    "booking.depositAgreementText": "J'accepte de payer un acompte de {amt} ({pct}% du total) pour confirmer mon intérêt réel pour cette réservation. Le solde restant est dû comme indiqué ci-dessus.",
    "booking.termsAgreementText": "J'accepte les conditions de réservation : les informations fournies sont exactes, le paiement est dû selon la méthode choisie, et l'annulation ou la résiliation peut être soumise à la politique de l'établissement.",
    "booking.submittingText": "Envoi en cours...",
    "booking.submitButton": "Envoyer la demande de réservation",
    "booking.backToHome": "Retour à l'accueil",
    "booking.availableSpacesLabel": "Espaces disponibles",
    "booking.pageTitle": "Réservez votre espace dès aujourd'hui",
    "booking.pricesShownIn": "Tous les prix sont affichés en {currency}",
    "booking.allFacilitiesOption": "Tous les établissements",
    "booking.allTypesOption": "Tous les types",
    "booking.selectSpaceOption": "Sélectionnez un espace",
    "booking.continueButton": "Continuer",
    "booking.loadErrorMessage": "Les espaces sont temporairement indisponibles. Veuillez réessayer.",
    "booking.retryButton": "Réessayer",
    "booking.noSpacesFoundHeading": "Aucun espace trouvé",
    "booking.adjustSearchText": "Ajustez votre recherche ou vos filtres.",
    "booking.chooseSpacePrompt": "Choisissez un établissement et un espace ci-dessus pour voir les détails et démarrer une réservation.",
    "booking.allRightsReserved": "Tous droits réservés.",
    "publicBooking.navHome": "Accueil",
    "publicBooking.navAbout": "À propos",
    "publicBooking.navServices": "Services",
    "publicBooking.navBookSpace": "Réserver un espace",
    "publicBooking.navReport": "Signaler un problème",
    "publicBooking.navContact": "Contact",
    "publicBooking.dashboardLink": "Tableau de bord →",
    "publicBooking.loginLink": "Connexion",
    "publicBooking.installAppLabel": "Installer {app}",
    "publicBooking.dismissInstallAria": "Fermer l'invite d'installation",
    "publicBooking.installHintAndroid": "Ajoutez un accès rapide depuis votre écran d'accueil.",
    "publicBooking.installHintIos": "Appuyez sur Partager, puis Ajouter à l'écran d'accueil.",
    "publicBooking.installButton": "Installer",
    "publicBooking.showSlideAria": "Afficher la diapositive {n}",
    "publicBooking.lookInsideLabel": "Un aperçu",
    "publicBooking.lookInsideHeading": "Tout ce dont votre établissement a besoin, réuni",
    "publicBooking.featureWorkOrdersTitle": "Ordres de travail",
    "publicBooking.featureWorkOrdersDesc": "Soumettez et suivez les demandes de maintenance en temps réel.",
    "publicBooking.featureBookingsTitle": "Réservations",
    "publicBooking.featureBookingsDesc": "Réservez des espaces et gérez les plannings facilement.",
    "publicBooking.featureMaintenanceTitle": "Maintenance",
    "publicBooking.featureMaintenanceDesc": "L'entretien préventif garantit le bon fonctionnement de chaque établissement.",
    "publicBooking.featureAnalyticsTitle": "Analyses",
    "publicBooking.featureAnalyticsDesc": "Une vue claire sur l'utilisation, les coûts et la performance.",
    "publicBooking.trustedByLabel": "Ils nous font confiance",
    "publicBooking.ourPartnersHeading": "Nos partenaires",
    "publicBooking.footerBlurb": "MITI, la plateforme de gestion d'établissements au service des entreprises à travers le Rwanda. Réservez des espaces, gérez les biens et simplifiez vos opérations.",
    "publicBooking.quickLinksHeading": "Liens rapides",
    "publicBooking.servicesHeading": "Services",
    "publicBooking.svcSpaceBooking": "Réservation d'espaces",
    "publicBooking.svcAssetManagement": "Gestion des biens",
    "publicBooking.svcMaintenance": "Maintenance",
    "publicBooking.svcWorkOrders": "Ordres de travail",
    "publicBooking.svcVendorManagement": "Gestion des fournisseurs",
    "publicBooking.contactHeading": "Contact",
    "publicBooking.followUsHeading": "Suivez-nous",
    "publicBooking.followOnAria": "Suivre {system} sur {label}",
    "publicBooking.followOnTitle": "Suivez-nous sur {label}",
    "publicBooking.allRightsReserved": "Tous droits réservés.",
    "publicBooking.privacyPolicyLink": "Politique de confidentialité",
    "publicBooking.termsLink": "Conditions d'utilisation",
    "report.submitFailedError": "Impossible d'envoyer votre signalement.",
    "report.backToHome": "Retour à l'accueil",
    "report.pageTitle": "Signaler un problème dans l'établissement",
    "pay.title": "Payer votre réservation",
    "pay.pendingNotice": "Payez l'acompte pour confirmer automatiquement votre réservation. Nous vous envoyons ensuite la facture avec le solde restant.",
    "pay.inactive": "Cette réservation n'est pas active.",
    "pay.depositButton": "Payer l'acompte",
    "pay.codeTitle": "Payer avec le code MTN MoMo",
    "pay.codeSteps": "Composez le code ci-dessous, saisissez votre code PIN Mobile Money, puis entrez l'identifiant de transaction du SMS de confirmation afin que nous vérifiions votre paiement.",
    "pay.claimPending": "Paiement soumis, en attente de vérification :",
    "pay.claimPhone": "Numéro MTN utilisé pour payer",
    "pay.claimRef": "Identifiant de transaction du SMS",
    "pay.claimSubmit": "J'ai payé ({amount})",
    "pay.loading": "Chargement...",
    "pay.invalidLink": "Ce lien de paiement n'est pas valide.",
    "pay.invoiceNo": "Facture",
    "pay.space": "Espace",
    "pay.facility": "Établissement",
    "pay.period": "Période",
    "pay.total": "Total",
    "pay.paid": "Déjà payé",
    "pay.balance": "Solde à payer",
    "pay.downloadInvoice": "Télécharger la facture (PDF)",
    "pay.success": "Paiement reçu ! Un reçu a été envoyé à votre e-mail.",
    "pay.failed": "Le paiement n'a pas abouti. Veuillez réessayer.",
    "pay.timeout": "Nous n'avons pas encore reçu de confirmation. Si vous avez approuvé le paiement, votre reçu vous sera envoyé par e-mail sous peu.",
    "pay.notConfirmed": "Cette réservation n'est pas encore confirmée.",
    "pay.fullyPaid": "Cette réservation est entièrement payée. Merci !",
    "pay.onlineUnavailable": "Le paiement en ligne n'est pas encore disponible. Veuillez contacter {email}.",
    "pay.payDeposit": "Payer l'acompte : {amount}",
    "pay.payFull": "Payer le solde complet : {amount}",
    "pay.phoneLabel": "Numéro MTN ou Airtel",
    "pay.starting": "Envoi de la demande...",
    "pay.payButton": "Payer par Mobile Money",
    "pay.approvePrompt": "Vérifiez votre téléphone et approuvez le paiement avec votre code PIN Mobile Money. Cette page se met à jour automatiquement.",
    "pay.receipts": "Reçus",
    "pay.startFailed": "Impossible de lancer le paiement. Veuillez réessayer.",
    "report.introText": "Vous avez une réservation confirmée ? Saisissez son e-mail ou son téléphone ; nous vous enverrons un code pour confirmer votre identité, puis signalez un problème, demandez de l'aide ou envoyez une suggestion.",
    "report.emailLabel": "E-mail de réservation *",
    "report.contactLabel": "E-mail ou téléphone de réservation *",
    "report.contactPlaceholder": "vous@exemple.com ou 07XXXXXXXX",
    "report.contactInvalid": "Saisissez l'e-mail ou le numéro de téléphone de votre réservation confirmée.",
    "report.sendCode": "Envoyer le code",
    "report.resendCode": "Renvoyer le code",
    "report.codeLabel": "Code à 6 chiffres *",
    "report.codeRequired": "Demandez un code et saisissez le code à 6 chiffres reçu par e-mail.",
    "report.codeSentNotice": "Si cela correspond à une réservation confirmée, un code à 6 chiffres a été envoyé à l'adresse e-mail de cette réservation.",
    "report.emailPlaceholder": "vous@exemple.com",
    "report.subjectLabel": "Objet *",
    "report.subjectPlaceholder": "De quoi avez-vous besoin ?",
    "report.categoryLabel": "Catégorie",
    "report.catIncident": "Problème d'établissement",
    "report.catMaintenance": "Demande de maintenance",
    "report.catSafety": "Problème de sécurité",
    "report.catSuggestion": "Suggestion",
    "report.catComplaint": "Plainte",
    "report.catOther": "Autre demande",
    "report.priorityLabel": "Priorité",
    "report.priorityLow": "Faible",
    "report.priorityMedium": "Moyenne",
    "report.priorityHigh": "Élevée",
    "report.priorityUrgent": "Urgente",
    "report.detailsLabel": "Détails *",
    "report.detailsPlaceholder": "Décrivez le problème, la demande ou la suggestion...",
    "report.sendingText": "Envoi en cours...",
    "report.sendButton": "Envoyer le signalement",
    "report.allRightsReserved": "Tous droits réservés.",
    "bookings.statusPending": "En attente",
    "bookings.statusConfirmed": "Confirmée",
    "bookings.statusCancelled": "Annulée",
    "bookings.statusTerminated": "Résiliée",
    "bookings.paymentUnpaid": "Non payé",
    "bookings.paymentPartial": "Partiel",
    "bookings.paymentPaid": "Payé",
    "bookings.pdfHeading": "FMS Pro — Demandes de réservation",
    "bookings.pdfFacilityLabel": "Site :",
    "bookings.pdfAllFacilities": "Tous",
    "bookings.pdfGeneratedLabel": "Généré le :",
    "bookings.pdfColGuest": "Invité",
    "bookings.pdfColEmail": "E-mail",
    "bookings.pdfColSpace": "Espace",
    "bookings.pdfColStart": "Début",
    "bookings.pdfColEnd": "Fin",
    "bookings.pdfColAttendees": "Participants",
    "bookings.pdfColStatus": "Statut",
    "bookings.pageTitle": "Demandes de réservation",
    "bookings.pendingSuffix": "en attente",
    "bookings.allFacilities": "Tous les sites",
    "bookings.subtitle": "— gérez les demandes de réservation d'espaces",
    "bookings.refresh": "Actualiser",
    "bookings.downloadPdf": "Télécharger le PDF",
    "bookings.statTotal": "Total",
    "bookings.statPending": "En attente",
    "bookings.statConfirmed": "Confirmées",
    "bookings.statCancelled": "Annulées",
    "bookings.tabAll": "Tous",
    "bookings.tabPending": "En attente",
    "bookings.tabConfirmed": "Confirmées",
    "bookings.tabCancelled": "Annulées",
    "bookings.colGuest": "Invité",
    "bookings.colSpace": "Espace",
    "bookings.colWhen": "Quand",
    "bookings.colGuestsCount": "Participants",
    "bookings.colPurpose": "Objet",
    "bookings.colStatus": "Statut",
    "bookings.colPayment": "Paiement",
    "bookings.colPublic": "Public",
    "bookings.colActions": "Actions",
    "bookings.badgePublic": "Public",
    "bookings.badgeInternal": "Interne",
    "bookings.actionViewDetailsTitle": "Voir les détails",
    "bookings.actionConfirm": "Confirmer",
    "bookings.actionDeclineTitle": "Refuser",
    "bookings.actionCancel": "Annuler",
    "bookings.emptyState": "Aucune demande de réservation pour le moment",
    "bookings.modalDetailsTitle": "Détails de la demande",
    "bookings.sectionGuestInfo": "Informations sur l'invité",
    "bookings.labelName": "Nom",
    "bookings.labelCompany": "Entreprise",
    "bookings.labelEmail": "E-mail",
    "bookings.labelPhone": "Téléphone",
    "bookings.sectionBookingDetails": "Détails de la réservation",
    "bookings.labelSpace": "Espace",
    "bookings.labelAttendees": "Participants",
    "bookings.labelFrom": "Du",
    "bookings.labelTo": "Au",
    "bookings.labelPurpose": "Objet",
    "bookings.labelNotes": "Remarques",
    "bookings.labelStatus": "Statut",
    "bookings.labelSource": "Origine",
    "bookings.sourcePublic": "Réservation publique",
    "bookings.sourceInternal": "Interne",
    "bookings.labelTerminationReason": "Motif de résiliation",
    "bookings.labelPayment": "Paiement",
    "bookings.paidOfText": "payé sur",
    "bookings.amountReceivedPlaceholder": "Montant reçu",
    "bookings.btnSavePayment": "Enregistrer le paiement",
    "bookings.btnClose": "Fermer",
    "bookings.btnDecline": "Refuser",
    "bookings.btnConfirmBooking": "Confirmer la réservation",
    "bookings.btnDelete": "Supprimer",
    "bookings.labelRenewBooking": "Renouveler la réservation",
    "bookings.btnRequestRenewal": "Demander le renouvellement",
    "bookings.labelTerminateBooking": "Résilier la réservation",
    "bookings.terminationReasonPlaceholder": "Motif de la résiliation",
    "bookings.btnTerminate": "Résilier",
    "spaces.typeOffice": "Bureau",
    "spaces.typeConferenceRoom": "Salle de conférence",
    "spaces.typeTrainingRoom": "Salle de formation",
    "spaces.typeStockRoom": "Magasin",
    "spaces.typeServerRoom": "Salle serveur",
    "spaces.typeBathroom": "Toilettes",
    "spaces.typeParking": "Parking",
    "spaces.typeCommonArea": "Espace commun",
    "spaces.typeLab": "Laboratoire",
    "spaces.typeOther": "Autre",
    "spaces.statusAvailable": "Disponible",
    "spaces.statusOccupied": "Occupé",
    "spaces.statusMaintenance": "En maintenance",
    "spaces.statusReserved": "Réservé",
    "spaces.pageTitle": "Gestion des espaces",
    "spaces.subtitle": "Attribution des salles, occupation et réservations",
    "spaces.addFacility": "Ajouter un site",
    "spaces.addSpace": "Ajouter un espace",
    "spaces.statTotal": "Total des espaces",
    "spaces.statAvailable": "Disponibles",
    "spaces.statOccupied": "Occupés",
    "spaces.statOccupancyRate": "Taux d'occupation",
    "spaces.tabSpaces": "Espaces",
    "spaces.tabBuildings": "Sites",
    "spaces.yourFacilityLabel": "Votre site : ",
    "spaces.manageHint": "— vous pouvez gérer les espaces de ce site",
    "spaces.searchPlaceholder": "Rechercher des espaces…",
    "spaces.allBuildings": "Tous les sites",
    "spaces.allStatus": "Tous les statuts",
    "spaces.floorLabel": "Étage",
    "spaces.btnBook": "Réserver",
    "spaces.emptyState": "Aucun espace trouvé",
    "spaces.floorsCount": "étage(s)",
    "spaces.yourFacilityBadge": "Votre site",
    "spaces.spacesCountLabel": "espaces",
    "spaces.managerLabel": "Responsable :",
    "spaces.btnViewSpaces": "Voir les espaces",
    "spaces.btnEdit": "Modifier",
    "spaces.btnDelete": "Supprimer",
    "spaces.modalEditSpace": "Modifier l'espace",
    "spaces.modalAddSpace": "Ajouter un nouvel espace",
    "spaces.labelBuilding": "Site *",
    "spaces.selectBuildingOption": "Sélectionner un site",
    "spaces.labelSpaceName": "Nom de l'espace *",
    "spaces.labelFloor": "Étage",
    "spaces.labelRoomNumber": "Numéro de salle",
    "spaces.labelType": "Type",
    "spaces.labelCapacity": "Capacité",
    "spaces.labelArea": "Superficie (m²)",
    "spaces.labelPricePerHour": "Prix par heure ({currency})",
    "spaces.labelPricePerDay": "Prix par jour ({currency})",
    "spaces.labelPricePerMonth": "Prix par mois ({currency})",
    "spaces.labelStatus": "Statut",
    "spaces.labelDepartment": "Département",
    "spaces.labelAssignedTo": "Attribué à",
    "spaces.noAssignmentOption": "Aucune attribution",
    "spaces.labelAmenities": "Équipements",
    "spaces.amenitiesPlaceholder": "ex. Projecteur, Tableau blanc, Climatiseur",
    "spaces.btnCancel": "Annuler",
    "spaces.savingLabel": "Enregistrement…",
    "spaces.btnSaveSpace": "Enregistrer l'espace",
    "spaces.modalEditFacility": "Modifier le site",
    "spaces.modalAddFacility": "Ajouter un nouveau site",
    "spaces.labelFacilityName": "Nom du site *",
    "spaces.labelAddress": "Emplacement / Adresse",
    "spaces.labelFloorsCount": "Nombre d'étages (facultatif)",
    "spaces.labelTotalArea": "Superficie totale (m²)",
    "spaces.labelOpenTime": "Ouvre à (heures de réservation)",
    "spaces.labelCloseTime": "Ferme à (heures de réservation)",
    "spaces.hoursHint": "Facultatif. Les invités ne peuvent réserver que dans ces heures. Laissez vide pour aucune limite.",
    "spaces.labelFacilityManager": "Responsable du site *",
    "spaces.requiredHint": "(obligatoire)",
    "spaces.selectManagerOption": "Sélectionner un responsable",
    "spaces.managerHint": "Seuls les responsables et administrateurs peuvent être désignés comme responsables de site.",
    "spaces.btnUpdateFacility": "Mettre à jour le site",
    "spaces.btnSaveFacility": "Enregistrer le site",
    "spaces.modalBookSpace": "Réserver un espace",
    "spaces.labelMeetingTitle": "Titre de la réunion",
    "spaces.meetingTitlePlaceholder": "ex. Réunion d'équipe",
    "spaces.labelOrderType": "Type de commande",
    "spaces.orderTypeBooking": "Réservation courte",
    "spaces.orderTypeRental": "Location d'espace",
    "spaces.labelRentalPeriod": "Durée de location",
    "spaces.rentalMonths1": "1 mois",
    "spaces.rentalMonths3": "3 mois",
    "spaces.rentalMonths6": "6 mois",
    "spaces.rentalMonths12": "12 mois",
    "spaces.labelPriceUnit": "Unité de prix",
    "spaces.perHourOption": "Par heure ·",
    "spaces.perDayOption": "Par jour ·",
    "spaces.perMonthOption": "Par mois ·",
    "spaces.labelStartDateTime": "Date et heure de début *",
    "spaces.labelEndDateTime": "Date et heure de fin *",
    "spaces.labelAttendees": "Participants",
    "spaces.labelPurpose": "Objet",
    "spaces.depositRequiredLabel": "Acompte requis pour confirmer l'intérêt ({pct}%)",
    "spaces.depositAgreementText": "Le demandeur accepte de verser un acompte de {amount} ({pct}% du total) pour confirmer un intérêt réel avant que cette réservation ne soit approuvée. Suivez l'acompte et le solde restant via le statut de paiement de la réservation.",
    "spaces.btnConfirmBooking": "Confirmer la réservation",
    "assets.pageTitle": "Gestion des actifs",
    "assets.pageSubtitleFacility": "— suivi du matériel et du cycle de vie",
    "assets.pageSubtitle": "Suivez le matériel, son cycle de vie et sa dépréciation",
    "assets.addAsset": "Ajouter un actif",
    "assets.statTotalAssets": "Total des actifs",
    "assets.statTotalValue": "Valeur totale",
    "assets.searchPlaceholder": "Rechercher par nom, étiquette, numéro de série...",
    "assets.filterAllStatus": "Tous les statuts",
    "assets.filterAllCategories": "Toutes les catégories",
    "assets.colTag": "Étiquette",
    "assets.colName": "Nom",
    "assets.colCategory": "Catégorie",
    "assets.colLocation": "Emplacement",
    "assets.colStatus": "Statut",
    "assets.colCondition": "État",
    "assets.colPurchaseDate": "Date d'achat",
    "assets.colCurrentValue": "Valeur actuelle",
    "assets.colActions": "Actions",
    "assets.noAssetsFound": "Aucun actif trouvé",
    "assets.modalEditTitle": "Modifier l'actif",
    "assets.modalAddTitle": "Ajouter un nouvel actif",
    "assets.fieldName": "Nom *",
    "assets.fieldCategory": "Catégorie",
    "assets.selectCategory": "Sélectionner une catégorie",
    "assets.fieldStatus": "Statut",
    "assets.fieldBuilding": "Bâtiment",
    "assets.selectBuilding": "Sélectionner un bâtiment",
    "assets.fieldAssignedTo": "Attribué à",
    "assets.unassigned": "Non attribué",
    "assets.fieldManufacturer": "Fabricant",
    "assets.fieldModel": "Modèle",
    "assets.fieldSerialNumber": "Numéro de série",
    "assets.fieldCondition": "État",
    "assets.fieldPurchaseDate": "Date d'achat",
    "assets.fieldPurchaseCost": "Coût d'achat ({currency})",
    "assets.fieldCurrentValue": "Valeur actuelle ({currency})",
    "assets.fieldWarrantyExpiry": "Expiration de la garantie",
    "assets.fieldNotes": "Remarques",
    "assets.cancel": "Annuler",
    "assets.saving": "Enregistrement...",
    "assets.saveAsset": "Enregistrer l'actif",
    "assets.statusActive": "Actif",
    "assets.statusInactive": "Inactif",
    "assets.statusUnderRepair": "En réparation",
    "assets.statusDisposed": "Mis au rebut",
    "assets.statusLost": "Perdu",
    "assets.conditionExcellent": "Excellent",
    "assets.conditionGood": "Bon",
    "assets.conditionFair": "Moyen",
    "assets.conditionPoor": "Mauvais",
    "inventory.txnStockIn": "Entrée de stock",
    "inventory.txnStockOut": "Sortie de stock",
    "inventory.txnAdjusted": "Ajusté",
    "inventory.partItems": "éléments",
    "inventory.partStatistics": "statistiques",
    "inventory.loadErrorSuffix": "n'ont pas pu être chargé(e)s.",
    "inventory.restartServerHint": "Veuillez redémarrer le serveur.",
    "inventory.loadErrorGeneric": "Impossible de charger les données d'inventaire. Vérifiez la connexion à l'API.",
    "inventory.transactionsLoadError": "Impossible de charger les transactions.",
    "inventory.filtersLoadError": "Impossible de charger les filtres d'inventaire. Vérifiez que le serveur est actif.",
    "inventory.pageTitle": "Gestion des stocks",
    "inventory.pageSubtitle": "Suivez les fournitures, pièces et stocks de matériel",
    "inventory.addItem": "Ajouter un article",
    "inventory.statTotalItems": "Total des articles",
    "inventory.statLowStock": "Stock faible",
    "inventory.statOutOfStock": "Rupture de stock",
    "inventory.statTotalValue": "Valeur totale",
    "inventory.tabItems": "Articles",
    "inventory.tabTransactions": "Transactions",
    "inventory.searchItemsPlaceholder": "Rechercher des articles...",
    "inventory.statusInStock": "En stock",
    "inventory.statusLowStock": "Stock faible",
    "inventory.statusOutOfStock": "Rupture de stock",
    "inventory.searchTxnPlaceholder": "Rechercher un article, un code ou une personne...",
    "inventory.filterAllMovements": "Tous les mouvements",
    "inventory.colCode": "Code",
    "inventory.colName": "Nom",
    "inventory.colCategory": "Catégorie",
    "inventory.colStock": "Stock",
    "inventory.colMin": "Min",
    "inventory.colUnitCost": "Coût unitaire",
    "inventory.colStatus": "Statut",
    "inventory.colFacility": "Établissement",
    "inventory.colActions": "Actions",
    "inventory.noItemsFound": "Aucun article en stock",
    "inventory.colDate": "Date",
    "inventory.colItem": "Article",
    "inventory.colMovement": "Mouvement",
    "inventory.colQty": "Qté",
    "inventory.colReason": "Raison / Remarques",
    "inventory.colBy": "Par",
    "inventory.colWorkOrder": "Ordre de travail",
    "inventory.noTransactionsFound": "Aucun mouvement de stock enregistré",
    "inventory.modalEditItemTitle": "Modifier l'article",
    "inventory.modalAddItemTitle": "Ajouter un article en stock",
    "inventory.fieldItemCode": "Code de l'article *",
    "inventory.itemCodePlaceholder": "ex. CLN-001",
    "inventory.fieldCategory": "Catégorie",
    "inventory.selectCategory": "Sélectionner une catégorie",
    "inventory.fieldFacility": "Établissement",
    "inventory.allFacilities": "Tous les établissements",
    "inventory.fieldUnit": "Unité",
    "inventory.unitPlaceholder": "ex. pièces, litres",
    "inventory.fieldCurrentQty": "Quantité actuelle",
    "inventory.fieldMinimumStock": "Stock minimum",
    "inventory.fieldUnitCost": "Coût unitaire ({currency})",
    "inventory.fieldStorageLocation": "Emplacement de stockage",
    "inventory.locationPlaceholder": "ex. Salle de stockage A, étagère 3",
    "inventory.cancel": "Annuler",
    "inventory.saving": "Enregistrement...",
    "inventory.saveItem": "Enregistrer l'article",
    "inventory.modalStockInTitle": "📥 Entrée de stock — {name}",
    "inventory.modalStockOutTitle": "📤 Sortie de stock — {name}",
    "inventory.currentStockLabel": "Stock actuel :",
    "inventory.unitsFallback": "unités",
    "inventory.fieldTransactionType": "Type de transaction",
    "inventory.optionStockInReceived": "📥 Entrée de stock (Reçu)",
    "inventory.optionStockOutUsed": "📤 Sortie de stock (Utilisé)",
    "inventory.optionAdjustSetQty": "🔄 Ajuster (Définir la quantité exacte)",
    "inventory.fieldQuantity": "Quantité *",
    "inventory.placeholderNewTotalQty": "Nouvelle quantité totale",
    "inventory.placeholderQuantity": "Quantité",
    "inventory.fieldReasonStockOut": "Raison de la sortie de stock *",
    "inventory.fieldNotes": "Remarques",
    "inventory.placeholderReasonOut": "ex. Utilisé pour la maintenance WO-00123",
    "inventory.placeholderReasonIn": "ex. Reçu du fournisseur",
    "inventory.stockOutAuditNote": "Chaque sortie de stock est enregistrée avec votre nom, la quantité et cette raison dans l'onglet Transactions.",
    "inventory.recording": "Enregistrement...",
    "inventory.recordTransaction": "Enregistrer la transaction",
    "maintenance.pageTitle": "Gestion de la maintenance",
    "maintenance.pageSubtitle": "maintenance préventive et corrective",
    "maintenance.pageSubtitleShort": "Calendriers de maintenance préventive et corrective",
    "maintenance.importTooltip": "Importer des calendriers depuis un tableur (.xlsx, .xls, .csv)",
    "maintenance.importing": "Lecture...",
    "maintenance.importButton": "Importer",
    "maintenance.exportTooltip": "Exporter les calendriers actuels vers un tableur",
    "maintenance.exporting": "Exportation...",
    "maintenance.exportButton": "Exporter",
    "maintenance.logWorkButton": "Enregistrer un travail",
    "maintenance.newScheduleButton": "Nouveau calendrier",
    "maintenance.statOverdue": "En retard",
    "maintenance.statDueThisMonth": "Dues ce mois-ci",
    "maintenance.statTotalSchedules": "Total des calendriers",
    "maintenance.statTotalLogs": "Total des registres",
    "maintenance.tabSchedules": "Calendriers",
    "maintenance.tabUpcoming": "À venir",
    "maintenance.tabLogs": "Registres",
    "maintenance.colTitle": "Titre",
    "maintenance.colSpace": "Espace entretenu",
    "maintenance.colAsset": "Bien",
    "maintenance.colFrequency": "Fréquence",
    "maintenance.colStartDate": "Date de début",
    "maintenance.colEndDate": "Date de fin",
    "maintenance.colAssigned": "Assigné",
    "maintenance.colLastPerformed": "Dernière intervention",
    "maintenance.colParts": "Pièces",
    "maintenance.colActions": "Actions",
    "maintenance.freqDaily": "quotidien",
    "maintenance.freqWeekly": "hebdomadaire",
    "maintenance.freqMonthly": "mensuel",
    "maintenance.freqQuarterly": "trimestriel",
    "maintenance.freqSemiAnnual": "semestriel",
    "maintenance.freqAnnual": "annuel",
    "maintenance.markDoneTooltip": "Marquer comme terminé (l'enregistre et planifie automatiquement la prochaine échéance)",
    "maintenance.logTooltip": "Enregistrer la maintenance (notes, pièces, coût)",
    "maintenance.edit": "Modifier",
    "maintenance.delete": "Supprimer",
    "maintenance.noSchedules": "Aucun calendrier pour l'instant",
    "maintenance.startingLabel": "Début : ",
    "maintenance.endingLabel": "Fin : ",
    "maintenance.assignedLabel": "Assigné : ",
    "maintenance.noUpcoming": "Aucune maintenance prévue dans les 30 prochains jours",
    "maintenance.logColDate": "Date",
    "maintenance.logColSpace": "Espace entretenu",
    "maintenance.logColAsset": "Bien",
    "maintenance.logColDescription": "Description",
    "maintenance.logColFindings": "Constatations",
    "maintenance.logColPartsUsed": "Pièces utilisées",
    "maintenance.logColCost": "Coût",
    "maintenance.logColPerformedBy": "Effectué par",
    "maintenance.noLogs": "Aucun registre de maintenance pour l'instant",
    "maintenance.editScheduleModalTitle": "Modifier le calendrier",
    "maintenance.newScheduleModalTitle": "Nouveau calendrier de maintenance",
    "maintenance.labelTitle": "Titre *",
    "maintenance.labelDescription": "Description",
    "maintenance.labelAction": "Action de maintenance",
    "maintenance.labelAsset": "Bien",
    "maintenance.labelAffectedSpace": "Espace affecté",
    "maintenance.labelFrequency": "Fréquence",
    "maintenance.labelStartDate": "Date de début *",
    "maintenance.labelEndDate": "Date de fin",
    "maintenance.labelAssignedTo": "Assigné à",
    "maintenance.labelEstimatedHours": "Heures estimées",
    "maintenance.scheduleTitlePlaceholder": "ex. Remplacement mensuel du filtre CVC",
    "maintenance.actionPlaceholder": "Expliquez quel travail aura lieu et comment l'espace pourrait être affecté",
    "maintenance.noSpecificAsset": "Aucun bien spécifique",
    "maintenance.noSpecificSpace": "Aucun espace spécifique",
    "maintenance.unassigned": "Non assigné",
    "maintenance.endDateHint": "Calculé automatiquement à partir de la date de début et de la fréquence",
    "maintenance.notifyAffectedLabel": "Envoyer un e-mail aux réservations et locations confirmées concernées avant cette action",
    "maintenance.checking": "Vérification...",
    "maintenance.previewAffected": "Aperçu des personnes concernées",
    "maintenance.affectedCountLabel": "réservation(s) ou location(s) confirmée(s) sera(ont) notifiée(s) :",
    "maintenance.rental": "Location",
    "maintenance.booking": "Réservation",
    "maintenance.noEmail": "Aucune adresse e-mail",
    "maintenance.previewHint": "Vérifiez le chevauchement avant d'enregistrer. Seules les réservations confirmées et les locations actives reçoivent des e-mails.",
    "maintenance.plannedPartsLabel": "Pièces planifiées",
    "maintenance.plannedPartsHint": "Choisissez les articles dont ce calendrier aura besoin — ils seront associés dès l'enregistrement.",
    "maintenance.noPartsPlanned": "Aucune pièce planifiée pour l'instant.",
    "maintenance.selectPart": "Sélectionner une pièce...",
    "maintenance.addPartButton": "Ajouter",
    "maintenance.partUsed": "Utilisé",
    "maintenance.partReady": "Prêt",
    "maintenance.partShort": "Insuffisant",
    "maintenance.removeFromPlan": "Retirer du plan",
    "maintenance.assetRequestsLabel": "Demandes d'articles/biens ({n})",
    "maintenance.assetRequestsHint": "Demandes déposées par l'employé assigné pour un article hors stock. L'approbation et la validation du bon de commande l'ajoutent automatiquement ici.",
    "maintenance.requestedBy": "Demandé par {name} ·",
    "maintenance.restockItem": "Réapprovisionner l'article",
    "maintenance.newAsset": "Nouveau bien",
    "maintenance.autoAddedAfterPO": "Ajouté automatiquement après approbation du bon de commande",
    "maintenance.vendorSubmissions": "Soumissions des fournisseurs ({n})",
    "maintenance.approveCreateLink": "Approuver et créer le lien fournisseur",
    "maintenance.reject": "Rejeter",
    "maintenance.copyVendorLink": "Copier le lien fournisseur",
    "maintenance.approveQuotation": "Approuver le devis sélectionné",
    "maintenance.rejectQuotation": "Rejeter le devis sélectionné",
    "maintenance.noAssetRequests": "Aucune demande d'article ou de bien pour ce calendrier.",
    "maintenance.cancel": "Annuler",
    "maintenance.saving": "Enregistrement...",
    "maintenance.saveSchedule": "Enregistrer le calendrier",
    "maintenance.createAddParts": "Créer et ajouter des pièces",
    "maintenance.logModalTitle": "Enregistrer un travail de maintenance",
    "maintenance.logLabelAsset": "Bien",
    "maintenance.logLabelSchedule": "Calendrier (le cas échéant)",
    "maintenance.logLabelDescription": "Description du travail",
    "maintenance.logLabelFindings": "Constatations / Observations",
    "maintenance.logLabelPartsUsed": "Pièces utilisées",
    "maintenance.logLabelCost": "Coût ({currency})",
    "maintenance.logLabelNextDue": "Prochaine échéance (facultatif)",
    "maintenance.selectAsset": "Sélectionner un bien",
    "maintenance.none": "Aucun",
    "maintenance.workDonePlaceholder": "Ce qui a été fait...",
    "maintenance.notEnoughStock": "Stock insuffisant",
    "maintenance.addPartUsedButton": "+ Ajouter une pièce utilisée",
    "maintenance.partsDeductionHint": "Chaque pièce sélectionnée ici est déduite du stock à l'enregistrement — bloqué si le stock est insuffisant, sauf confirmation.",
    "maintenance.notesLabel": "Notes (seulement si vous ne choisissez pas dans l'inventaire ci-dessus)",
    "maintenance.partsNotesPlaceholder": "ex. Filtre #3M-500 (hors inventaire)",
    "maintenance.nextDueHint": "Laissez vide pour continuer automatiquement selon la fréquence du calendrier à partir d'aujourd'hui",
    "maintenance.savingLog": "Enregistrement...",
    "maintenance.saveLog": "Enregistrer le registre",
    "maintenance.importModalTitle": "Importer des calendriers de maintenance",
    "maintenance.readyToImport": "prêt(s) à importer",
    "maintenance.rowsSkipped": "ligne(s) seront ignorées (titre manquant ou date illisible)",
    "maintenance.importColRow": "Ligne",
    "maintenance.importColTitle": "Titre",
    "maintenance.importColFrequency": "Fréquence",
    "maintenance.importColStartDate": "Date de début",
    "maintenance.importColAssigned": "Assigné",
    "maintenance.importColNotes": "Notes",
    "maintenance.importing2": "Importation...",
    "maintenance.importCount": "Importer {n} calendrier(s)",
    "workOrders.pageTitle": "Ordres de travail",
    "workOrders.pageSubtitle": "demandes de service et tâches",
    "workOrders.pageSubtitleShort": "Gérer les demandes de service et les tâches",
    "workOrders.newButton": "Ordre de travail",
    "workOrders.searchPlaceholder": "Rechercher des ordres de travail...",
    "workOrders.allStatus": "Tous les statuts",
    "workOrders.statusOpen": "Ouvert",
    "workOrders.statusAssigned": "Assigné",
    "workOrders.statusInProgress": "En cours",
    "workOrders.statusOnHold": "En attente",
    "workOrders.statusCompleted": "Terminé",
    "workOrders.statusCancelled": "Annulé",
    "workOrders.allPriority": "Toutes les priorités",
    "workOrders.priorityLow": "Faible",
    "workOrders.priorityMedium": "Moyenne",
    "workOrders.priorityHigh": "Élevée",
    "workOrders.priorityCritical": "Critique",
    "workOrders.colWoNumber": "N° OT",
    "workOrders.colTitle": "Titre",
    "workOrders.colType": "Type",
    "workOrders.colPriority": "Priorité",
    "workOrders.colStatus": "Statut",
    "workOrders.colAssignedTo": "Assigné à",
    "workOrders.colDueDate": "Date d'échéance",
    "workOrders.colParts": "Pièces",
    "workOrders.colActions": "Actions",
    "workOrders.partsReady": "Prêt",
    "workOrders.partsShort": "Insuffisant",
    "workOrders.view": "Voir",
    "workOrders.edit": "Modifier",
    "workOrders.delete": "Supprimer",
    "workOrders.noneFound": "Aucun ordre de travail trouvé",
    "workOrders.editModalTitle": "Modifier l'ordre de travail",
    "workOrders.createModalTitle": "Ordre de travail",
    "workOrders.labelTitle": "Titre *",
    "workOrders.labelDescription": "Description",
    "workOrders.labelType": "Type",
    "workOrders.labelPriority": "Priorité",
    "workOrders.labelStatus": "Statut",
    "workOrders.labelAssignedTo": "Assigné à",
    "workOrders.labelBuilding": "Bâtiment",
    "workOrders.labelAsset": "Bien",
    "workOrders.labelDueDate": "Date d'échéance",
    "workOrders.labelEstimatedHours": "Heures estimées",
    "workOrders.labelNotes": "Notes",
    "workOrders.typeCorrective": "corrective",
    "workOrders.typePreventive": "préventive",
    "workOrders.typeInspection": "inspection",
    "workOrders.typeInstallation": "installation",
    "workOrders.typeOther": "autre",
    "workOrders.unassigned": "Non assigné",
    "workOrders.selectBuilding": "Sélectionner un bâtiment",
    "workOrders.noAsset": "Aucun bien",
    "workOrders.cancel": "Annuler",
    "workOrders.saving": "Enregistrement...",
    "workOrders.saveButton": "Enregistrer l'ordre de travail",
    "workOrders.partsNeededLabel": "Pièces nécessaires ({n})",
    "workOrders.needHaveLabel": "Besoin {q}{unit} · Disponible {avail}{unit}",
    "workOrders.partDeducted": "Déduit",
    "workOrders.removeFromPlan": "Retirer du plan",
    "workOrders.noPartsPlanned": "Aucune pièce planifiée pour ce travail pour l'instant.",
    "workOrders.selectPart": "Sélectionner une pièce...",
    "workOrders.qtyPlaceholder": "Qté",
    "workOrders.addPartButton": "Ajouter",
    "workOrders.completeDeductHint": "Marquer cet ordre de travail comme Terminé déduit automatiquement ces pièces du stock — bloqué si le stock est insuffisant, sauf confirmation.",
    "workOrders.assetRequestsLabel": "Demandes de biens ({n})",
    "workOrders.requestedBy": "Demandé par {name} ·",
    "workOrders.restockItem": "Réapprovisionner l'article",
    "workOrders.newAsset": "Nouveau bien",
    "workOrders.autoAddedAfterPO": "Ajouté automatiquement à ce travail après approbation du bon de commande",
    "workOrders.vendorSubmissions": "Soumissions des fournisseurs ({n})",
    "workOrders.approveCreateLink": "Approuver et créer le lien fournisseur",
    "workOrders.reject": "Rejeter",
    "workOrders.copyVendorLink": "Copier le lien fournisseur",
    "workOrders.approveQuotation": "Approuver le devis sélectionné",
    "workOrders.rejectQuotation": "Rejeter le devis sélectionné",
    "workOrders.noAssetRequests": "Aucune demande de bien pour cet ordre de travail.",
    "workOrders.activityLogLabel": "Journal d'activité ({n})",
    "workOrders.noUpdates": "Aucune mise à jour pour l'instant.",
    "workOrders.commentPlaceholder": "Ajouter un commentaire...",
    "workOrders.postButton": "Publier",
    "dashboard.loading": "Chargement...",
    "dashboard.allClear": "Tout ari meza uyu munsi — nta kintu cyihutirwa gisaba ko wita kuri cyo.",
    "dashboard.attentionMessage": "Vous avez {n} élément(s) qui nécessite(nt) votre attention aujourd'hui.",
    "dashboard.groupWorkOrdersTitle": "Ordres de travail",
    "dashboard.groupWorkOrdersSubtitle": "Demandes de réparation et d'entretien",
    "dashboard.captionTotalWO": "Tous les ordres de travail jamais enregistrés, ouverts ou terminés",
    "dashboard.captionOpenWO": "Enregistrés mais personne n'a encore commencé",
    "dashboard.captionCriticalWO": "Marqués urgents — à traiter en premier",
    "dashboard.groupFacilityTitle": "Installation",
    "dashboard.groupFacilitySubtitle": "Espaces, équipements et entretien",
    "dashboard.captionAssets": "Équipements et articles suivis dans le système",
    "dashboard.captionSpaces": "Salles et espaces disponibles à la réservation maintenant",
    "dashboard.captionMaintenance": "Entretien planifié désormais en retard",
    "dashboard.groupInventoryTitle": "Stock et demandes",
    "dashboard.groupInventorySubtitle": "Niveaux de stock et approbations en attente",
    "dashboard.captionLowStock": "Articles dont le stock est bas",
    "dashboard.captionRequests": "Demandes en attente de votre approbation",
    "dashboard.captionAlerts": "Notifications que vous n'avez pas encore ouvertes",
    "dashboard.trendCaption": "Nombre d'ordres de travail reçus chaque mois (bleu) comparé à ceux terminés (vert).",
    "dashboard.statusCaption": "Tous les ordres de travail actuels, répartis selon leur état actuel.",
    "dashboard.recentCaption": "Les derniers ordres de travail enregistrés, du plus récent au plus ancien.",
    "dashboard.upcomingMaintCaption": "Entretien à venir prochainement, pour éviter toute surprise.",
    "dashboard.requestsCaption": "Articles demandés par le personnel, en attente de votre décision.",
    "dashboard.bookingsCaption": "Prochaines réservations d'espaces.",
    "settings.sectionGeneral": "Général",
    "settings.systemNameLabel": "Nom du système",
    "settings.orgNameLabel": "Nom de l'organisation",
    "settings.orgNamePlaceholder": "Mon organisation",
    "settings.contactEmailLabel": "E-mail de contact",
    "settings.notifLogoLabel": "Logo des notifications",
    "settings.removeLogo": "Supprimer le logo",
    "settings.currencyLabel": "Devise",
    "settings.timezoneLabel": "Fuseau horaire",
    "settings.sectionHomepage": "Éditeur de la page d'accueil",
    "settings.heroTitleLabel": "Titre principal",
    "settings.heroTitlePlaceholder": "Réservez des espaces premium",
    "settings.heroSubtitleLabel": "Sous-titre",
    "settings.heroSubtitlePlaceholder": "Pour chaque occasion",
    "settings.heroDescLabel": "Description principale",
    "settings.heroDescHint": "Affiché sous le titre principal sur la page d'accueil. Utilisez {currency} pour insérer le code de votre devise. Une langue laissée vide reprend le texte anglais.",
    "settings.aboutLabel": "Texte À propos",
    "settings.aboutPlaceholder": "Parlez aux visiteurs de vos installations...",
    "settings.phoneLabel": "Téléphone",
    "settings.addressLabel": "Adresse",
    "settings.publicEmailLabel": "E-mail de contact public",
    "settings.hoursLabel": "Heures d'ouverture",
    "settings.ratingStatLabel": "Statistique de note",
    "settings.ratingStatHint": "Affiché comme le chiffre « Note » sur la page d'accueil",
    "settings.availabilityStatLabel": "Statistique de disponibilité",
    "settings.availabilityStatHint": "Affiché comme le chiffre « Disponible » sur la page d'accueil",
    "settings.slideTitleLabel": "Titre du diaporama",
    "settings.slideImageLabel": "Image du diaporama",
    "settings.removeImage": "Supprimer l'image",
    "settings.slideVideoLabel": "Vidéo du diaporama (facultatif)",
    "settings.slideVideoHint": "Si définie, elle remplace l'image ci-dessus. MP4, WebM ou MOV, jusqu'à 20 Mo.",
    "settings.removingVideo": "Suppression...",
    "settings.removeVideo": "Supprimer la vidéo",
    "settings.partnersHeading": "Partenaires",
    "settings.partnersHint": "Affiché comme bande de logos sur la page d'accueil publique. Laissez un logo vide pour le cacher.",
    "settings.partnerNameLabel": "Nom du partenaire {n}",
    "settings.partnerNamePlaceholder": "p. ex. Acme Corp",
    "settings.partnerLogoLabel": "Logo du partenaire {n}",
    "settings.socialHeading": "Liens des réseaux sociaux",
    "settings.legalHeading": "Textes juridiques",
    "settings.privacyLabel": "Politique de confidentialité",
    "settings.privacyPlaceholder": "Tapez le texte de la politique de confidentialité ici...",
    "settings.termsLabel": "Conditions d'utilisation",
    "settings.termsPlaceholder": "Tapez le texte des conditions d'utilisation ici...",
    "settings.sectionSmtp": "Configuration e-mail / SMTP",
    "settings.smtpBanner": "Configurez le SMTP pour activer les e-mails de bienvenue aux nouveaux utilisateurs. Gmail : utilisez un mot de passe d'application avec smtp.gmail.com:587",
    "settings.smtpHostLabel": "Hôte SMTP",
    "settings.smtpPortLabel": "Port",
    "settings.smtpFromLabel": "Adresse d'expéditeur",
    "settings.smtpUserLabel": "Nom d'utilisateur SMTP",
    "settings.smtpPasswordLabel": "Mot de passe SMTP",
    "settings.testEmailHeading": "E-mail de test",
    "settings.testEmailPlaceholder": "Envoyer le test à...",
    "settings.sendingTest": "Envoi...",
    "settings.sendTest": "Envoyer le test",
    "settings.testFailed": "Échec du test",
    "settings.sectionDataIO": "Exportation et importation de données",
    "settings.dataIoIntro": "Exportez les données en CSV pour sauvegarde ou importez de nouveaux bâtiments, actifs et articles d'inventaire. Les importations ajoutent des données sans écraser les données existantes.",
    "settings.exportCsv": "Exporter en CSV",
    "settings.importCsv": "Importer un CSV",
    "settings.exportSuccess": "{resource} exporté avec succès.",
    "settings.exportFailed": "Échec de l'exportation.",
    "settings.importFailed": "Échec de l'importation.",
    "settings.sectionBackup": "Sauvegarde et restauration",
    "settings.createBackupNow": "Créer une sauvegarde maintenant",
    "settings.creatingBackup": "Création de la sauvegarde...",
    "settings.refresh": "Actualiser",
    "settings.backupTableBackup": "Sauvegarde",
    "settings.backupTableType": "Type",
    "settings.backupTableSize": "Taille",
    "settings.backupTableCreated": "Créée le",
    "settings.backupTableActions": "Actions",
    "settings.backupFailed": "Échec",
    "settings.download": "Télécharger",
    "settings.restore": "Restaurer",
    "settings.delete": "Supprimer",
    "settings.noBackups": "Aucune sauvegarde pour le moment — cliquez sur « Créer une sauvegarde maintenant » pour créer la première.",
    "settings.restoreModalTitle": "Restaurer à partir d'une sauvegarde ?",
    "settings.restoreModalBody": "Ceci remplacera toutes les données actuelles par le contenu de {filename} (du {date}). Une sauvegarde de sécurité de l'état actuel sera d'abord prise, mais tout ce qui a été ajouté ou modifié depuis cette sauvegarde sera perdu.",
    "settings.restoreModalConfirmLabel": "Tapez RESTORE pour confirmer :",
    "settings.cancel": "Annuler",
    "settings.restoring": "Restauration...",
    "settings.restoreNow": "Restaurer maintenant",
    "settings.sectionSecurity": "Sécurité",
    "settings.securityIntro": "Le contrôle d'accès basé sur les rôles est appliqué sur tous les points d'accès API :",
    "settings.roleAdminDesc": "accès complet : installations, utilisateurs, paramètres, toutes les données",
    "settings.roleManagerDesc": "gère les espaces de son installation assignée",
    "settings.roleTechnicianDesc": "gère les ordres de travail et les registres d'entretien",
    "settings.roleStaffDesc": "accès en lecture + soumission d'ordres de travail + réservation d'espaces",
    "settings.newUserPasswordNote": "Tous les nouveaux utilisateurs reçoivent un mot de passe temporaire par e-mail et doivent le changer dès leur première connexion.",
    "settings.pageTitle": "Paramètres du système",
    "settings.pageSubtitle": "Réservé aux administrateurs — configurer les préférences du système",
    "settings.savedBadge": "✅ Paramètres enregistrés",
    "settings.saving": "Enregistrement...",
    "settings.saveSettings": "Enregistrer les paramètres",
    "users.pageTitleManager": "Équipe — {facility}",
    "users.pageTitleAdmin": "Gestion des utilisateurs",
    "users.myFacilityFallback": "Mon installation",
    "users.subtitleManager": "Gérer les employés de votre installation",
    "users.subtitleAdmin": "Administrateur — gérer tous les utilisateurs du système",
    "users.addEmployee": "Ajouter un employé",
    "users.addUser": "Ajouter un utilisateur",
    "users.colName": "Nom",
    "users.colEmail": "E-mail",
    "users.colRole": "Rôle",
    "users.colJobTitle": "Poste",
    "users.colFacility": "Installation",
    "users.colDepartment": "Service",
    "users.colStatus": "Statut",
    "users.colJoined": "Inscrit le",
    "users.colActions": "Actions",
    "users.active": "Actif",
    "users.inactive": "Inactif",
    "users.noneFound": "Aucun utilisateur trouvé",
    "users.editModalTitle": "Modifier : {name}",
    "users.fullNameLabel": "Nom complet",
    "users.emailLabel": "Adresse e-mail",
    "users.roleLabel": "Rôle",
    "users.jobTitleLabel": "Poste",
    "users.facilityLabel": "Assigner à une installation",
    "users.departmentLabel": "Service",
    "users.phoneLabel": "Téléphone",
    "users.statusLabel": "Statut",
    "users.noFacility": "Aucune installation",
    "users.cancel": "Annuler",
    "users.saving": "Enregistrement...",
    "users.saveChanges": "Enregistrer les modifications",
    "users.createModalTitleManager": "Ajouter un employé à votre installation",
    "users.createModalTitleAdmin": "Ajouter un nouvel utilisateur",
    "users.tempPasswordNote": "Un mot de passe temporaire sera généré et envoyé par e-mail à l'utilisateur.",
    "users.willBeAddedTo": "Sera ajouté à : {facility}",
    "users.creating": "Création...",
    "users.createUser": "Créer l'utilisateur",
    "users.createdModalTitle": "Utilisateur créé",
    "users.accountCreatedFor": "Compte créé pour {name}",
    "users.welcomeEmailSent": "✅ E-mail de bienvenue envoyé à {email} avec les identifiants de connexion.",
    "users.welcomeEmailFailed": "⚠️ E-mail non envoyé ({msg}). Veuillez transmettre les identifiants manuellement.",
    "users.canChangePasswordNote": "L'utilisateur peut changer son mot de passe à tout moment depuis le menu latéral après connexion.",
    "users.done": "Terminé",
    "role.admin": "Administrateur",
    "role.manager": "Gestionnaire",
    "role.employee": "Employé",
    "developer.createSystemFailed": "Impossible de créer le système",
    "developer.openSystemFailed": "Impossible d'ouvrir ce système",
    "developer.backupHeading": "Sauvegarde et restauration",
    "developer.backupIntro": "Sauvegarde la base de données de la session en cours — la base principale de la plateforme lorsque vous êtes connecté en tant que développeur, ou la base d'un système spécifique lorsque vous y êtes via « Ouvrir ». Pour sauvegarder un autre système, ouvrez-le d'abord, puis revenez dans ses Paramètres.",
    "developer.systemsHeading": "Instances système",
    "developer.systemsIntro": "Chaque système dispose de sa propre base de données totalement isolée — ses propres utilisateurs, espaces, ordres de travail, actifs, image de marque et paramètres. Rien de saisi dans un système n'est visible dans un autre.",
    "developer.creatingSystem": "Création…",
    "developer.createSystem": "Créer",
    "developer.credsReady": "« {name} » est prêt — enregistrez ces identifiants maintenant, ils ne seront plus affichés",
    "developer.credsAdminEmail": "E-mail administrateur :",
    "developer.credsTempPassword": "Mot de passe temporaire :",
    "developer.credsSignInLink": "Lien de connexion :",
    "developer.copyPassword": "Copier le mot de passe",
    "developer.copyLink": "Copier le lien",
    "developer.dismiss": "Fermer",
    "developer.noSystems": "Aucun système pour le moment — créez le premier ci-dessus.",
    "developer.systemActive": "Actif",
    "developer.systemDeactivated": "Désactivé",
    "developer.copySignInLink": "Copier le lien de connexion",
    "developer.openButton": "Ouvrir",
    "developer.deactivateButton": "Désactiver",
    "developer.activateButton": "Activer",
    "developer.deleteSystemTitle": "Supprimer définitivement ce système et toutes ses données",
    "developer.deleteButton": "Supprimer",
    "developer.newSystemNamePlaceholder": "Nom du nouveau système (ex. Acme Co)",
    "developer.adminEmailPlaceholder": "E-mail administrateur (facultatif)",
    "developer.createBackupNow": "Créer une sauvegarde maintenant",
    "developer.creatingBackup": "Création de la sauvegarde...",
    "developer.refresh": "Actualiser",
    "developer.backupTableBackup": "Sauvegarde",
    "developer.backupTableType": "Type",
    "developer.backupTableSize": "Taille",
    "developer.backupTableCreated": "Créée le",
    "developer.backupTableActions": "Actions",
    "developer.backupFailed": "Échec",
    "developer.download": "Télécharger",
    "developer.restore": "Restaurer",
    "developer.delete": "Supprimer",
    "developer.noBackups": "Aucune sauvegarde pour le moment — cliquez sur « Créer une sauvegarde maintenant » pour créer la première.",
    "developer.restoreModalTitle": "Restaurer à partir d'une sauvegarde ?",
    "developer.restoreModalBody": "Ceci remplacera toutes les données actuelles par le contenu de {filename} (du {date}). Une sauvegarde de sécurité de l'état actuel sera d'abord prise, mais tout ce qui a été ajouté ou modifié depuis cette sauvegarde sera perdu.",
    "developer.restoreModalConfirmLabel": "Tapez RESTORE pour confirmer :",
    "developer.cancel": "Annuler",
    "developer.restoring": "Restauration...",
    "developer.restoreNow": "Restaurer maintenant",
    "messages.postingStatus": "Publication...",
    "messages.newBadge": "nouveau(x)",
    "myTasks.statusCancelled": "Annulé",
    "myTasks.requestItemHeading": "Demander un article ou un bien",
    "myTasks.newAssetTab": "Nouveau bien",
    "myTasks.restockItemTab": "Réapprovisionner un article",
    "myTasks.selectInventoryItem": "Sélectionner un article en stock...",
    "myTasks.assetNeededPlaceholder": "Bien nécessaire (ex. nouvelle perceuse)",
    "myTasks.sendRequestButton": "Envoyer la demande",
    "myTasks.partDeducted": "Déduit",
    "myTasks.partReady": "Prêt",
    "myTasks.noPartsLogged": "Rien n'a encore été enregistré comme utilisé.",
    "myTasks.autoDeductNote": "Marquer cet ordre de travail comme terminé déduit automatiquement ces articles du stock.",
    "myTasks.activityLog": "Journal d'activité",
    "myTasks.submitProgressReport": "Envoyer le rapport d'avancement",
    "myTasks.nextDue": "Prochaine échéance",
    "myTasks.logMaintenanceWork": "Enregistrer les travaux de maintenance",
    "myTasks.itemsUsedFromStock": "Articles utilisés (du stock)",
    "myTasks.needSomethingElseNote": "Besoin de quelque chose qui n'est pas en stock ? Fermez ceci et utilisez « Demander un article/bien ».",
    "myTasks.logAsDone": "Enregistrer comme terminé",
    "myTasks.requestItemAssetButton": "Demander un article/bien",
    "myTasks.logWorkButton": "Enregistrer le travail",
    "myTasks.noMaintenanceJobs": "Aucune tâche de maintenance ne vous est encore assignée",
    "myTasks.createWorkOrderButton": "Créer un ordre de travail",
    "myTasks.categoryOptional": "Catégorie (facultatif)",
    "reports.typeIncident": "Incident",
    "reports.statusNew": "Nouveau",
    "reports.statusRead": "Lu",
    "reports.statusResolved": "Résolu",
    "reports.statusDismissed": "Rejeté",
    "reports.statPending": "En attente",
    "reports.managersReply": "Réponse du responsable",
    "reports.guestBookingFallback": "Réservation invité",
    "reports.facilityReportFallback": "Rapport de l'installation",
    "reports.noEmailFallback": "Pas d'e-mail",
    "reports.facilityReportsSubtitle": "{facility} — rapports des employés",
    "reports.priorityUrgentEmoji": "🚨 Urgente",
    "reports.relatedSpaceBooking": "Réservation d'espace associée",
    "assets.fieldAssetTag": "Étiquette de l'actif (facultatif)",
    "assets.assetTagPlaceholder": "Laisser vide pour générer automatiquement",
    "assets.fieldSpace": "Pièce / Emplacement",
    "assets.selectSpace": "Pas dans une pièce précise",
    "assets.selectBuildingFirst": "Choisissez d'abord un bâtiment",
    "assets.spaceHint": "Où l'actif est conservé : bureau, magasin, parking... Ajoutez les pièces dans Espaces.",
    "assets.importButton": "Importer",
    "assets.importTitle": "Importer des actifs",
    "assets.importHelp": "Téléversez un fichier CSV ou Excel. Seule la colonne name est obligatoire ; laissez asset_tag vide et les étiquettes sont générées automatiquement. Les bâtiments, pièces et catégories sont reconnus par leur nom, et les nouvelles catégories sont créées pour vous.",
    "assets.importTemplate": "Télécharger le modèle",
    "assets.importStart": "Lancer l'importation",
    "assets.importing": "Importation…",
    "assets.importDone": "{n} actifs importés.",
    "assets.importTagsGenerated": "{n} étiquettes ont été générées automatiquement.",
    "assets.importRow": "Ligne",
    "assets.importMoreErrors": "…et {n} autres lignes avec des problèmes",
    "assets.importNoFile": "Veuillez d'abord choisir un fichier.",
    "assets.importFailed": "Échec de l'importation.",
    "publicLink.title": "Votre page publique",
    "publicLink.description": "Partagez ces liens pour que les visiteurs voient vos installations, réservent des espaces et signalent des problèmes. Votre installation apparaît dès qu'elle a au moins un espace au statut Disponible.",
    "publicLink.home": "Page publique de réservation",
    "publicLink.report": "Page de signalement",
    "publicLink.copy": "Copier le lien",
    "publicLink.copied": "Copié",
    "publicLink.open": "Ouvrir",
    "publicLink.noAvailable": "Aucun espace n'est Disponible pour le moment : les visiteurs verront une page vide. Ajoutez un espace (ou passez-en un à Disponible) ci-dessous.",
    "publicLink.loggedInNote": "Pendant que vous êtes connecté, ouvrez le lien dans une fenêtre privée ou un autre navigateur pour voir ce que voient les visiteurs.",
    "settings.smtpTrust": "Votre mot de passe e-mail reste sur ce serveur, est masqué une fois enregistré et sert uniquement à se connecter à votre fournisseur de messagerie lors de l'envoi d'un e-mail. Utilisez un mot de passe d'application ou une adresse d'envoi dédiée, jamais votre mot de passe personnel.",
  },
  rw: {
    "signup.title": "Fungura konti yawe",
    "signup.subtitle": "Fungura ikigo gishya cyangwa winjire mu itsinda ryawe.",
    "signup.tabOrg": "Ikigo gishya",
    "signup.tabJoin": "Injira mu itsinda ryanjye",
    "signup.name": "Amazina yombi",
    "signup.email": "Imeri",
    "signup.phone": "Telefoni (si ngombwa)",
    "signup.orgName": "Izina ry'ikigo",
    "signup.orgCode": "Kode y'ikigo",
    "signup.joinCode": "Kode yo kwinjiza abakozi",
    "signup.joinHint": "Baza umuyobozi wawe kode y'ikigo na kode yo kwinjiza abakozi.",
    "signup.password": "Ijambobanga",
    "signup.passwordHint": "Nibura inyuguti 12.",
    "signup.confirmPassword": "Ongera wemeze ijambobanga",
    "signup.passwordsMismatch": "Amajambobanga ntabwo ahura",
    "signup.passwordTooShort": "Ijambobanga rigomba kuba nibura inyuguti 12",
    "signup.agreePrefix": "Iyo wiyandikishije wemera",
    "signup.terms": "Amabwiriza yo gukoresha",
    "signup.and": "na",
    "signup.privacy": "Politiki y'ibanga",
    "signup.submit": "Fungura konti",
    "signup.submitting": "Biri gukorwa...",
    "signup.haveAccount": "Usanzwe ufite konti?",
    "signup.signIn": "Injira",
    "signup.checkEmailTitle": "Reba imeri yawe",
    "signup.checkEmailBody": "Twohereje link yo kwemeza kuri {email}. Yifungure kugira ngo urangize kwiyandikisha. Iyi link imara amasaha 24.",
    "signup.resend": "Ohereza imeri ukundi",
    "signup.resent": "Niba hari kwiyandikisha gutegereje iyi imeri, link nshya yoherejwe.",
    "signup.failed": "Kwiyandikisha ntibyakunze. Ongera ugerageze.",
    "signup.verifying": "Turimo kwemeza imeri yawe...",
    "signup.verifyOrg": "Ikigo cyawe \"{org}\" cyiteguye.",
    "signup.verifyJoin": "Winjiye muri \"{org}\".",
    "signup.systemCode": "Kode ya sisitemu yawe",
    "signup.systemCodeNote": "Andika iyi kode igihe winjira kuri sitasiyo cyangwa muri porogaramu.",
    "signup.continueSignIn": "Komeza winjire",
    "signup.verifyFailed": "Iyi link yo kwemeza ntiyemewe cyangwa yararangiye.",
    "signup.tryAgain": "Ongera wiyandikishe",
    "login.newHere": "Uri mushya kuri MITIFM?",
    "login.createAccount": "Fungura konti",
    "nav.billing": "Ifatabuguzi",
    "billing.title": "Ifatabuguzi",
    "billing.subtitle": "Genzura gahunda yawe n'ubwishyu.",
    "billing.statusTrialing": "Igeragezwa ry'ubuntu",
    "billing.statusActive": "Rirakora",
    "billing.statusGrace": "Ubwishyu bwatinze",
    "billing.statusReadOnly": "Ryarangiye (gusoma gusa)",
    "billing.trialLeft": "Hasigaye iminsi {days} mu igeragezwa ryawe ry'ubuntu.",
    "billing.activeUntil": "Wishyuye kugeza ku wa {date}.",
    "billing.graceLeft": "Gahunda yawe yarangiye. Ishyura mu minsi {days} kugira ngo udahinduka gusoma gusa.",
    "billing.readOnly": "Ifatabuguzi ryawe ryarangiye. Sisitemu ubu ni iyo gusoma gusa: ushobora kureba amakuru yawe ariko ntushobora kuyahindura. Hitamo gahunda kugira ngo ukomeze.",
    "billing.plan": "Gahunda",
    "billing.users": "Kugeza ku bakoresha {n}",
    "billing.unlimitedUsers": "Abakoresha batagira umubare",
    "billing.choosePlan": "Hitamo gahunda",
    "billing.monthly": "Buri kwezi",
    "billing.yearly": "Buri mwaka",
    "billing.yearlyHint": "Ihendutse cyane",
    "billing.perMonth": "/ ukwezi",
    "billing.perYear": "/ umwaka",
    "billing.currentBadge": "Gahunda ukoresha",
    "billing.payButton": "Ishyura na Mobile Money",
    "billing.phoneLabel": "Nimero ya Mobile Money (MTN cyangwa Airtel)",
    "billing.phonePlaceholder": "urugero: 0788123456",
    "billing.phoneHelp": "Tuzohereza ubusabe bwo kwishyura kuri iyi telefoni. Ubwemeze winjiza PIN yawe ya Mobile Money.",
    "billing.hostedHelp": "Uzishyura ku rubuga rwizewe rwa Flutterwave ukoresheje Mobile Money (MTN cyangwa Airtel) cyangwa ikarita ya banki. Ntitubona ikarita yawe cyangwa PIN yawe.",
    "billing.phoneInvalid": "Andika nimero nyayo ya MTN cyangwa Airtel, urugero 0788123456.",
    "billing.resultApprove": "Reba kuri telefoni yawe wemeze ubusabe bwo kwishyura (andika PIN yawe ya Mobile Money).",
    "billing.resultLate": "Turacyategereje kwemezwa. Niba wemeje ubwishyu, ifatabuguzi ryawe rizakora ubwaryo mu minota mike.",
    "billing.redirecting": "Turohereza ubusabe bwo kwishyura...",
    "billing.paymentsOff": "Kwishyura kuri interineti ntibirashoboka. Nyamuneka vugana na support@mitifm.com.",
    "billing.secureNote": "Ubwishyu bunyuzwa mu buryo butaziguye muri MTN Mobile Money na Airtel Money. Ntitubona cyangwa ngo tubike PIN yawe ya Mobile Money.",
    "billing.history": "Amateka y'ubwishyu",
    "billing.noPayments": "Nta bwishyu burakorwa.",
    "billing.colDate": "Itariki",
    "billing.colPlan": "Gahunda",
    "billing.colAmount": "Amafaranga",
    "billing.colStatus": "Imimerere",
    "billing.paid": "Byishyuwe",
    "billing.pending": "Birategereje",
    "billing.failed": "Ntibyakunze",
    "billing.checkoutFailed": "Ntitwashoboye gutangira kwishyura. Ongera ugerageze.",
    "billing.noneNeeded": "Iyi sisitemu ntikeneye ifatabuguzi.",
    "billing.resultChecking": "Turimo kwemeza ubwishyu bwawe...",
    "billing.resultSuccess": "Twakiriye ubwishyu. Murakoze!",
    "billing.resultSuccessBody": "Ifatabuguzi ryawe rirakora.",
    "billing.resultPending": "Turacyategereje ko ugitanga ubwishyu cyemeza. Uru rupapuro rwivugurura ubwarwo. Niba wemeje ubwishyu kuri telefoni yawe, bigomba gufata umunota umwe gusa.",
    "billing.resultFailed": "Ubwishyu ntibwarangiye. Ushobora kongera kugerageza.",
    "billing.resultUnknown": "Ntitwabonye ubu bwishyu.",
    "billing.backToBilling": "Subira ku ifatabuguzi",
    "billing.bannerTrial": "Igeragezwa ry'ubuntu: hasigaye iminsi {days}.",
    "billing.bannerGrace": "Gahunda yawe yarangiye. Hasigaye iminsi {days} ngo sisitemu ibe iyo gusoma gusa.",
    "billing.bannerReadOnly": "Ifatabuguzi ryarangiye: sisitemu ni iyo gusoma gusa.",
    "billing.bannerAction": "Genzura ifatabuguzi",
    "billing.bannerAskAdmin": "Saba umuyobozi wawe kurisubiza.",
    "billing.includes": "Serivisi zirimo",
    "billing.feature.maintenance": "Gusana ibikoresho mbere y'uko byangirika",
    "billing.feature.inventory": "Ububiko n'ibice by'ibikoresho",
    "billing.feature.asset_requests": "Gusaba ibikoresho ku bacuruzi no gufata ibiciro",
    "billing.feature.bookings": "Gufata ibyumba n'urupapuro rwo gufata ibyumba rusange",
    "billing.feature.data_tools": "Kwinjiza no gusohora amakuru",
    "joinCode.title": "Kwiyandikisha kw'abakozi",
    "joinCode.description": "Reka abakozi bawe bifungurire konti. Bakeneye kode y'ikigo cyawe n'iyi kode yo kwinjiza abakozi.",
    "joinCode.orgCode": "Kode y'ikigo",
    "joinCode.joinCode": "Kode yo kwinjiza abakozi",
    "joinCode.allow": "Emerera abakozi kwiyandikisha",
    "joinCode.regenerate": "Kode nshya",
    "joinCode.regenerateConfirm": "Ushaka gukora kode nshya? Iyi ishaje ntizongera gukora.",
    "joinCode.copyInvite": "Kopera link y'ubutumire",
    "joinCode.copied": "Byakopowe",
    "joinCode.unavailable": "Kwiyandikisha kw'abakozi bikorera sisitemu z'ibigo gusa.",
    "joinCode.note": "Abakozi bashya batangira bafite uburenganzira buke (Imirimo yanjye). Wahindura uruhare rwabo muri Abakoresha.",
    "joinCode.failed": "Guhindura ntibyakunze. Ongera ugerageze.",
    'language.english': 'Icyongereza',
    'language.french': 'Igifaransa',
    'language.kinyarwanda': 'Ikinyarwanda',
    'language.label': 'Ururimi',
    'nav.dashboard': 'Imbonerahamwe',
    'nav.workOrders': 'Amabwiriza y\'akazi',
    'nav.assets': 'Umutungo',
    'nav.facilities': 'Ibikorwaremezo',
    'nav.maintenance': 'Gusana no kwita ku bikorwaremezo',
    'nav.inventory': 'Ibarura ry\'ububiko',
    'nav.bookings': 'Kwamamaza',
    'nav.reportsInbox': 'Kwamamaza raporo',
    'nav.messages': 'Ubutumwa',
    'nav.myTeam': 'Itsinda ryanjye',
    'nav.users': 'Abakoresha',
    'nav.settings': 'Igenamiterere',
    'nav.myTasks': 'Inshingano zanjye',
    'nav.myReports': 'Raporo zanjye',
    'nav.developer': 'Umutegura porogaramu',
    'sidebar.profile': 'Ibikubiyemo by\'ingenzi',
    'sidebar.myWork': 'Akazi kanjye',
    'sidebar.administration': 'Ubuyobozi',
    'sidebar.changePassword': 'Hindura ijambo banga',
    'sidebar.logout': 'Sohoka',
    'sidebar.publicBooking': 'Urupapuro rwo kwiyandikisha rusange',
    'header.hello': 'Muraho',
    'header.systemTitle': 'Sisitemu yo gucunga ibikorwaremezo ya MITI',
    'header.facilityManagement': 'Gucunga ibikorwaremezo',
    'login.title': 'Injira kuri konti yawe',
    'login.subtitle': 'Injiza amakuru yawe kugira ngo ukoreshe sisitemu',
    'login.email': 'Aderesi ya imeli',
    'login.password': 'Ijambo banga',
    'login.systemCode': 'Kode ya sisitemu (si itegeko)',
    'login.systemCodeHint': "Reka ubusa keretse ikigo cyawe kigushyikirije kode ya sisitemu.",
    'login.forgotPassword': 'Wibagiwe ijambo banga?',
    'login.signIn': 'Injira',
    'login.signingIn': 'Kwamamaza...',
    'login.demoCredentials': 'Amakuru y\'igerageza',
    'login.changePassword': 'Hindura ijambo banga',
    'login.cancel': 'Hagarika',
    'login.save': 'Bika',
    'login.minimum6': 'Nibura inyuguti 6',
    'login.passwordsDoNotMatch': 'Amagambo banga ntabwo ahura',
    'login.passwordChanged': 'Ijambo banga ryahinduwe neza!',
    'login.failed': 'Kwamamaza byanze. Ongera ugerageze.',
    'role.employee': 'Umukozi',
    'account.currentPassword': 'Ijambo banga risanzwe',
    'account.newPassword': 'Ijambo banga rishya',
    'account.confirmNewPassword': 'Emeza ijambo banga rishya',
    'account.confirmPassword': 'Emeza ijambo banga',
    'account.min12': 'Nibura inyuguti 12',
    'account.reenterPassword': 'Ongera wandike ijambo banga rishya',
    'account.saving': 'Birimo kubikwa...',
    'account.passwordsDoNotMatch': 'Amagambo banga ntabwo ahura',
    'account.changeFailed': 'Kwamamaza ijambo banga byanze',
    'account.forgotTitle': 'Wibagiwe ijambo banga?',
    'account.forgotDescription': 'Injiza imeli ya konti yawe, turakohereza umurongo wo gusubiramo ijambo banga.',
    'account.sendResetLink': 'Ohereza umurongo wo gusubiramo',
    'account.sending': 'Birimo koherezwa...',
    'account.backToLogin': 'Subira kwinjira',
    'account.resetEmailFailed': 'Kwamamaza imeli yo gusubiramo byanze',
    'account.resetTitle': 'Shyiraho ijambo banga rishya',
    'account.resetDescription': 'Hitamo ijambo banga rishya rya konti yawe ya FMS.',
    'account.returnToLogin': 'Subira kwinjira',
    'account.resetPassword': 'Subiramo ijambo banga',
    'account.resetFailed': 'Kwamamaza ijambo banga byanze',
    'account.showPassword': 'Erekana ijambo banga',
    'account.hidePassword': 'Hisha ijambo banga',
    'navigation.open': 'Fungura menu y’iyobora',
    'navigation.close': 'Funga menu y’iyobora',
    'common.loading': 'Birimo gutegurwa...', 'common.quantity': 'Umubare', 'common.notes': 'Ibisobanuro byiyongereye', 'common.submitting': 'Birimo koherezwa...',
    'developer.deleteFailed': 'Gusiba iyo sisitemu ntibyakunze', 'developer.title': 'Igenzura ry’uwateguye porogaramu', 'developer.subtitle': 'Imikorere rusange ya sisitemu n’uburyo bwo kuyisana', 'developer.loadFailed': 'Kwamamaza imiterere ya sisitemu byanze', 'developer.updateFailed': 'Kwamamaza imiterere ya sisitemu byanze', 'developer.deactivateConfirm': 'Guhagarika sisitemu yose? Abakoresha na serivisi rusange bizahagarikwa kugeza wongeye kuyikoresha.', 'developer.active': 'Sisitemu irakora', 'developer.inactive': 'Sisitemu yarahagaritswe', 'developer.available': 'Abakoresha na serivisi rusange zirahari.', 'developer.developerOnly': 'Abategura porogaramu bonyine ni bo bashobora gukoresha sisitemu.', 'developer.updating': 'Birimo guhindurwa...', 'developer.deactivate': 'Hagarika sisitemu yose', 'developer.activate': 'Fungura sisitemu yose', 'developer.deactivateSystemConfirm': 'Guhagarika "{name}"? Abakoresha bayo ntibazashobora kwinjira kugeza igarutse gukora.', 'developer.deleteSystemConfirm': 'Gusiba burundu "{name}"?\n\nIbi bizasiba burundu ububiko bwayo bwose — buri mukoresha, umwanya, akazi, umutungo, ubwamamazi n’igenamiterere. Ibi NTIBISHOBORA gusubizwa inyuma.\n\nGuhagarika ahubwo bihagarika gusa kwinjira, kandi bishobora gusubizwamo nyuma.', 'developer.deleteSystemPrompt': 'Kugira ngo wemeze gusiba burundu, andika izina rya sisitemu nk’uko riri: {name}', 'developer.deleteSystemNameMismatch': 'Izina rya sisitemu ntabwo rihuye — gusiba byahagaritswe.',
    'vendor.invalidLink': 'Uyu murongo ntabwo ukora cyangwa wararangiye', 'vendor.submitFailed': 'Kwamamaza igiciro byanze', 'vendor.loading': 'Kwamamaza icyifuzo...', 'vendor.submitted': 'Igiciro cyoherejwe', 'vendor.review': 'Itsinda ry’ikikorwaremezo rizasuzuma igiciro cyawe rikuvugishe.', 'vendor.title': 'Igiciro cy’utanga serivisi', 'vendor.description': 'Tanga igiciro cyawe cy’umutungo wasabwe.', 'vendor.name': 'Izina ry’utanga serivisi *', 'vendor.email': 'Imeli y’utanga serivisi *', 'vendor.amount': 'Amafaranga y’igiciro *', 'vendor.deliveryTime': 'Igihe cyo kugeza *', 'vendor.deliveryPlaceholder': 'urugero: iminsi 5 y’akazi', 'vendor.submit': 'Ohereza igiciro',
    'reminder.endingNow': 'Birarangira ubu', 'reminder.oneMinute': 'hasigaye umunota 1', 'reminder.minutes': 'hasigaye iminota', 'reminder.bookingEnding': 'Kwamamaza kugiye kurangira', 'reminder.rentalRenewal': 'Kuvugurura ubukode birageze', 'reminder.guest': 'Umushyitsi', 'reminder.new': 'amenyesha mashya', 'reminder.title': 'Amamenyesha', 'reminder.enableDescription': 'Fungura amamenyesha ya telefoni ku kwiyandikisha, raporo, amabwiriza n’ibiciro.', 'reminder.enable': 'Fungura', 'reminder.blocked': 'Amamenyesha ya telefoni yarahagaritswe. Yafungure mu igenamiterere rya mushakisha yawe.', 'reminder.none': 'Nta menyesha rirahari', 'reminder.renewalIn': 'Kuvugurura ubukode mu', 'reminder.days': 'minsi', 'reminder.tenant': 'Ukodesha', 'reminder.noEmail': 'Nta imeli', 'reminder.ends': 'Birangira:', 'reminder.people': 'abantu', 'reminder.refreshes': 'Amamenyesha avugururwa buri masegonda 30',
    'dashboard.overview': 'Incamake y’imikorere yose y’ibikorwaremezo',
    'dashboard.facilityOverview': 'incamake y’ikikorwaremezo cyawe',
    'dashboard.unavailable': 'Imbonerahamwe ntiboneka',
    'dashboard.loadError': 'Kwamamaza amakuru y’imbonerahamwe byanze. Reba niba seriveri ikora.',
    'dashboard.refresh': 'Ongera utangire',
    'dashboard.retry': 'Ongera ugerageze',
    'dashboard.greeting': 'Twishimiye kukubona,',
    'dashboard.attention': 'Dore ibikeneye kwitabwaho uyu munsi.',
    'dashboard.totalWorkOrders': 'Amabwiriza y’akazi yose',
    'dashboard.openOrders': 'Amabwiriza afunguye',
    'dashboard.criticalPriority': 'Ibyihutirwa cyane',
    'dashboard.totalAssets': 'Umutungo wose',
    'dashboard.availableSpaces': 'Imyanya ihari',
    'dashboard.overdueMaintenance': 'Gusana kwakererewe',
    'dashboard.lowStock': 'Ububiko buke',
    'dashboard.assetRequests': 'Ibisabwa by’umutungo',
    'dashboard.unreadAlerts': 'Imenyesha ridasomwe',
    'dashboard.workOrderTrend': 'Imigendekere y’amabwiriza y’akazi (amezi 6 ashize)',
    'dashboard.created': 'Byakozwe',
    'dashboard.completed': 'Byarangiye',
    'dashboard.workOrderStatus': 'Imiterere y’amabwiriza y’akazi',
    'dashboard.recentWorkOrders': 'Amabwiriza y’akazi aheruka',
    'dashboard.viewAll': 'Reba byose',
    'dashboard.workOrderNumber': 'Nimero y’akazi',
    'dashboard.title': 'Umutwe',
    'dashboard.priority': 'Ibyihutirwa',
    'dashboard.status': 'Imiterere',
    'dashboard.noWorkOrders': 'Nta mabwiriza y’akazi ahari',
    'dashboard.upcomingMaintenance': 'Gusana gutaha',
    'dashboard.noUpcomingMaintenance': 'Nta gusana gutaha',
    'dashboard.overdue': 'Byakererewe',
    'dashboard.days': 'iminsi',
    'dashboard.general': 'Rusange',
    'dashboard.request': 'Icyifuzo',
    'dashboard.requester': 'Uwasabye',
    'dashboard.date': 'Itariki',
    'dashboard.quantity': 'Umubare',
    'dashboard.noAssetRequests': 'Nta bisabwa by’umutungo',
    'dashboard.upcomingBookings': 'Kwamamaza gutaha',
    'dashboard.untitledBooking': 'Kwamamaza kutagira umutwe',
    'dashboard.noUpcomingBookings': 'Nta kwiyandikisha gutaha',
    'status.open': 'Birakinguye',
    'status.inProgress': 'Birimo gukorwa',
    'status.completed': 'Byarangiye'
    ,'priority.low': 'Hasi',
    'priority.medium': 'Hagati',
    'priority.high': 'Hejuru',
    'priority.critical': 'Byihutirwa cyane',
    'status.assigned': 'Byahawe',
    'status.onHold': 'Byarahagaze',
    'status.cancelled': 'Byahagaritswe',
    'status.pending': 'Bitegereje',
    'status.approved': 'Byemejwe',
    'status.rejected': 'Byanze',
    "layout.logoAlt": "ikirango",
    "layout.tagline": "Gucunga Ibikorwa Remezo",
    "layout.myFacilityFallback": "Ikigo Cyanjye",
    "about.backToHome": "Garuka Ahabanza",
    "about.logoAlt": "ikirango",
    "about.facilityManagementLabel": "GUCUNGA IBIKORWAREMEZO",
    "about.footerRights": "Uburenganzira bwose burarindwa.",
    "contact.backToHome": "Garuka Ahabanza",
    "contact.logoAlt": "ikirango",
    "contact.facilityManagementLabel": "GUCUNGA IBIKORWAREMEZO",
    "contact.footerRights": "Uburenganzira bwose burarindwa.",
    "services.backToHome": "Garuka Ahabanza",
    "services.logoAlt": "ikirango",
    "services.facilityManagementLabel": "GUCUNGA IBIKORWAREMEZO",
    "services.footerRights": "Uburenganzira bwose burarindwa.",
    "legal.backToHome": "Garuka Ahabanza",
    "legal.logoAlt": "ikirango",
    "legal.facilityManagementLabel": "GUCUNGA IBIKORWAREMEZO",
    "legal.footerRights": "Uburenganzira bwose burarindwa.",
    "about.eyebrow": "Abo Turi Bo",
    "about.heroTitle": "Gucunga Ibikorwaremezo mu Buryo Bwiza",
    "about.feature1Title": "Ibikorwaremezo Bigezweho",
    "about.feature1Desc": "Ibikorwaremezo bigezweho bifite ikoranabuhanga rigezweho kugira ngo habeho ahantu heza ho gukorera.",
    "about.feature2Title": "Birinzwe kandi Bicungwa Neza",
    "about.feature2Desc": "Ubugenzuzi bw'umutekano amasaha 24 ku munsi, igenzura ry'uburenganzira bwo kwinjira, n'itsinda rya MITI rishinzwe gucunga ibikorwaremezo.",
    "about.feature3Title": "Ibikorwa Birambye",
    "about.feature3Desc": "Uburyo bukoresha ingufu neza n'imikorere ibungabunga ibidukikije kugira ngo habeho ingaruka nke ku bidukikije.",
    "about.feature4Title": "Ubufasha bw'Abahanga",
    "about.feature4Desc": "Abacunzi b'ibikorwaremezo biyemeje n'abakozi b'ubufasha kugira ngo ibyifuzo byanyu bisubizwe vuba.",
    "contact.eyebrow": "Twandikire",
    "contact.heroTitle": "Tuvugishe",
    "contact.heroSubtitle": "Ufite ibibazo ku birebana no gufata ibyumba, ibiciro, cyangwa gucunga ibikorwaremezo? Turi hano kugira ngo tubafashe.",
    "contact.phoneLabel": "Telefoni",
    "contact.emailReplyTime": "Dusubiza mu masaha 24",
    "contact.emailLabel": "Imeri",
    "contact.locationLabel": "Aho Tubarizwa",
    "contact.locationSubtitle": "Sura ikigo cyacu nyamukuru",
    "services.eyebrow": "Serivisi Zacu",
    "services.heroTitle": "Ibyo Ukeneye Byose",
    "services.heroSubtitle": "Kuva ku gufata ibyumba kugeza ku gucunga ibikorwaremezo byose, dukora byose.",
    "services.spaceBookingTitle": "Gufata Ibyumba",
    "services.spaceBookingDesc": "Fata ibyumba by'inama, imyiherero, biro n'ibindi hifashishijwe amakuru y'igihe nyacyo n'iyemeza ryihuse.",
    "services.assetManagementTitle": "Gucunga Umutungo",
    "services.assetManagementDesc": "Kurikirana kandi ucunge ibikoresho byose, imashini, n'umutungo hifashishijwe gukurikirana igihe byamara n'agaciro kazo uko kagenda kagabanuka.",
    "services.maintenanceTitle": "Kubungabunga",
    "services.maintenanceDesc": "Gutegura gahunda yo kubungabunga mbere y'uko ikibazo kibaho no gukosora, kugira ngo ikigo cyawe gikomeze gukora neza.",
    "services.workOrdersTitle": "Dosiye z'Akazi",
    "services.workOrdersDesc": "Ohereza kandi ukurikirane dosiye z'akazi, hagendewe ku byihutirwa n'amakuru agezweho igihe cyose.",
    "services.bookingManagementTitle": "Gucunga Gufata Ibyumba",
    "services.bookingManagementDesc": "Kubona neza gufata ibyumba byose hamwe n'inzira yo kwemeza, kumenya aho hahurira ibikorwa, n'ikalendari igaragaza byose.",
    "services.paymentsTitle": "Kwishyura muri {currency}",
    "services.paymentsDesc": "Ibyumba byose bikodeshwa bigenerwa igiciro muri {currency}. Igiciro kigaragara neza ku isaha, ugasobanurirwa amafaranga mbere yo gufata icyumba.",
    "legal.termsTitle": "Amabwiriza y'Ikoreshwa",
    "legal.privacyTitle": "Politika y'Ibanga",
    "legal.notFilledIn": "Iyi paji ntiyarujuzwamo amakuru.",
    "booking.typeOffice": "Ibiro",
    "booking.typeConferenceRoom": "Icyumba cy'inama",
    "booking.typeTrainingRoom": "Icyumba cy'amahugurwa",
    "booking.typeStockRoom": "Ububiko",
    "booking.typeServerRoom": "Icyumba cya seriveri",
    "booking.typeBathroom": "Ubwiherero",
    "booking.typeParking": "Ahaparika imodoka",
    "booking.typeCommonArea": "Ahantu rusange",
    "booking.typeLab": "Ivuriro/Laboratwari",
    "booking.typeOther": "Ikindi",
    "booking.endAfterStartError": "Igihe cyo kurangiza kigomba kuza nyuma y'igihe cyo gutangira",
    "booking.submitFailedError": "Kwiyandikisha ntibyagenze neza. Ongera ugerageze.",
    "booking.floorLabel": "Igorofa",
    "booking.peopleSuffix": "abantu",
    "booking.rentalSubmittedTitle": "Gusaba kukodesha byoherejwe!",
    "booking.bookingSubmittedTitle": "Gusaba kwiyandikisha byoherejwe!",
    "booking.referenceLabel": "Nimero y'ubwiyandikishe",
    "booking.depositDueLabel": "Amafaranga y'ingwate agomba kwishyurwa ubu (kwemeza ko ushishikajwe)",
    "booking.percentOfTotal": "% by'agaciro kose k'icyo gusaba",
    "booking.totalOrderValue": "Agaciro kose k'icyo gusaba:",
    "booking.managerWillConfirm": "Umuyobozi azemeza ubwiyandikishe bwawe kuri",
    "booking.doneButton": "Byarangiye",
    "booking.alreadyBookedLabel": "Byamaze kwandikwa:",
    "booking.moreSuffix": "+{n} ibindi",
    "booking.orderDetailsHeading": "📅 Amakuru y'icyo gusaba",
    "booking.orderTypeLabel": "Ubwoko bw'icyo gusaba *",
    "booking.shortBookingOption": "Kwiyandikisha gito",
    "booking.spaceRentalOption": "Gukodesha umwanya",
    "booking.rentalPeriodLabel": "Igihe cyo gukodesha *",
    "booking.month1": "Ukwezi 1",
    "booking.month3": "Amezi 3",
    "booking.month6": "Amezi 6",
    "booking.month12": "Amezi 12",
    "booking.priceUnitLabel": "Uburyo bwo kwishyura *",
    "booking.perHourOption": "Ku isaha ·",
    "booking.perDayOption": "Ku munsi ·",
    "booking.perMonthOption": "Ku kwezi ·",
    "booking.paymentMethodLabel": "Uburyo bwo kwishyura *",
    "booking.bankTransferOption": "Kohereza amafaranga kuri banki",
    "booking.mobileMoneyOption": "Mobile Money",
    "booking.cardOption": "Ikarita",
    "booking.cashOnApprovalOption": "Amafaranga akubiyeho nyuma yo kwemezwa",
    "booking.startLabel": "Itangira *",
    "booking.endLabel": "Irangira *",
    "booking.attendeesLabel": "Abazitabira",
    "booking.eventTitleLabel": "Umutwe w'ikiganiro",
    "booking.titlePlaceholder": "urugero: Inama y'itsinda, Amahugurwa",
    "booking.purposeLabel": "Intego",
    "booking.balanceDueLabel": "Amafaranga asigaye azishyurwa ugeze · Ifaranga:",
    "booking.depositRequiredNow": "Ingwate isabwa ubu ({pct}%)",
    "booking.yourDetailsHeading": "👤 Amakuru yawe",
    "booking.fullNameLabel": "Amazina yose *",
    "booking.emailLabel": "Imeyili *",
    "booking.phoneLabel": "Telefoni",
    "booking.phonePlaceholder": "+250 7XX XXX XXX",
    "booking.organisationLabel": "Ikigo / Umuntu ku giti cye",
    "booking.organisationPlaceholder": "Andika izina ry'ikigo cyangwa 'Umuntu ku giti cye'",
    "booking.notesLabel": "Ibisobanuro birenzeho",
    "booking.reviewNotice": "ℹ️ Icyifuzo cyawe kizasuzumwa n'umuyobozi w'ikigo. Uzahamagarwa kuri imeyili yawe kugira ngo bihamywe.",
    "booking.depositAgreementText": "Nemeye kwishyura ingwate ya {amt} ({pct}% by'igiteranyo) kugira ngo nemeze ko nifuza by'ukuri iki gukodesha. Amafaranga asigaye azishyurwa nk'uko bivugwa haruguru.",
    "booking.termsAgreementText": "Nemeye amabwiriza yo kwiyandikisha: amakuru natanze ni ukuri, kwishyura bigomba gukurikiza uburyo bwatoranyijwe, kandi guhagarika cyangwa gukuraho bishobora gukurikiza amabwiriza y'ikigo.",
    "booking.submittingText": "Birimo koherezwa...",
    "booking.submitButton": "Ohereza icyifuzo cyo kwiyandikisha",
    "booking.backToHome": "Subira ahabanza",
    "booking.availableSpacesLabel": "Imyanya iboneka",
    "booking.pageTitle": "Wiyandikishe umwanya wawe uyu munsi",
    "booking.pricesShownIn": "Ibiciro byose byerekanwe muri {currency}",
    "booking.allFacilitiesOption": "Ibigo byose",
    "booking.allTypesOption": "Ubwoko bwose",
    "booking.selectSpaceOption": "Hitamo umwanya",
    "booking.continueButton": "Komeza",
    "booking.loadErrorMessage": "Imyanya ntiboneka ubu. Ongera ugerageze.",
    "booking.retryButton": "Ongera ugerageze",
    "booking.noSpacesFoundHeading": "Nta mwanya wabonetse",
    "booking.adjustSearchText": "Hindura ishakisha cyangwa imyitwarire yawe.",
    "booking.chooseSpacePrompt": "Hitamo ikigo n'umwanya haruguru kugira ngo urebe amakuru maze utangire kwiyandikisha.",
    "booking.allRightsReserved": "Uburenganzira bwose burabitswe.",
    "publicBooking.navHome": "Ahabanza",
    "publicBooking.navAbout": "Ibiturutseho",
    "publicBooking.navServices": "Serivisi",
    "publicBooking.navBookSpace": "Wiyandikishe umwanya",
    "publicBooking.navReport": "Tanga ikibazo",
    "publicBooking.navContact": "Twandikire",
    "publicBooking.dashboardLink": "Ikibaho →",
    "publicBooking.loginLink": "Injira",
    "publicBooking.installAppLabel": "Shyiramo {app}",
    "publicBooking.dismissInstallAria": "Funga ubutumwa bwo gushyiramo",
    "publicBooking.installHintAndroid": "Ongeraho uburyo bwihuse bwo kugera hano uhereye ku mbonerahamwe.",
    "publicBooking.installHintIos": "Kanda kuri Gusangiza, hanyuma Ongeraho ku mbonerahamwe.",
    "publicBooking.installButton": "Shyiramo",
    "publicBooking.showSlideAria": "Erekana isilayidi {n}",
    "publicBooking.lookInsideLabel": "Reba imbere",
    "publicBooking.lookInsideHeading": "Ibyo ikigo cyawe gikeneye byose, hamwe",
    "publicBooking.featureWorkOrdersTitle": "Amabwiriza y'akazi",
    "publicBooking.featureWorkOrdersDesc": "Tanga kandi ukurikirane ibisabwa by'ubukorikori mu gihe nyacyo.",
    "publicBooking.featureBookingsTitle": "Ibyiyandikisho",
    "publicBooking.featureBookingsDesc": "Wiyandikishe imyanya kandi uyobore gahunda byoroshye.",
    "publicBooking.featureMaintenanceTitle": "Ubukorikori",
    "publicBooking.featureMaintenanceDesc": "Kwita ku bikoresho mbere y'uko byangirika bituma ikigo gikora neza.",
    "publicBooking.featureAnalyticsTitle": "Isesengura",
    "publicBooking.featureAnalyticsDesc": "Kureba neza ikoreshwa, ikiguzi, n'imikorere.",
    "publicBooking.trustedByLabel": "Baradutekaho",
    "publicBooking.ourPartnersHeading": "Abafatanyabikorwa bacu",
    "publicBooking.footerBlurb": "MITI ni urubuga rwo gucunga ibigo rukorera ibigo byinshi mu Rwanda. Wiyandikishe imyanya, ucunge ibikoresho, kandi woroshye imikorere.",
    "publicBooking.quickLinksHeading": "Amahuza y'ingenzi",
    "publicBooking.servicesHeading": "Serivisi",
    "publicBooking.svcSpaceBooking": "Kwiyandikisha imyanya",
    "publicBooking.svcAssetManagement": "Gucunga ibikoresho",
    "publicBooking.svcMaintenance": "Ubukorikori",
    "publicBooking.svcWorkOrders": "Amabwiriza y'akazi",
    "publicBooking.svcVendorManagement": "Gucunga abatanga serivisi",
    "publicBooking.contactHeading": "Twandikire",
    "publicBooking.followUsHeading": "Dukurikire",
    "publicBooking.followOnAria": "Kurikira {system} kuri {label}",
    "publicBooking.followOnTitle": "Dukurikire kuri {label}",
    "publicBooking.allRightsReserved": "Uburenganzira bwose burabitswe.",
    "publicBooking.privacyPolicyLink": "Politiki y'ibanga",
    "publicBooking.termsLink": "Amabwiriza y'ikoreshwa",
    "report.submitFailedError": "Ikibazo cyawe ntikyashoboye koherezwa.",
    "report.backToHome": "Subira ahabanza",
    "report.pageTitle": "Tanga ikibazo cy'ikigo",
    "pay.title": "Ishyura ireservasiyo yawe",
    "pay.pendingNotice": "Ishyura avansi kugira ngo ireservasiyo yawe yemezwe ako kanya. Hanyuma tuzakoherereza fagitire irimo asigaye.",
    "pay.inactive": "Iyi reservasiyo ntikora.",
    "pay.depositButton": "Ishyura avansi",
    "pay.codeTitle": "Ishyura ukoresheje kode ya MTN MoMo",
    "pay.codeSteps": "Kanda kode iri hasi, shyiramo PIN ya Mobile Money, hanyuma wandike nimero y'igikorwa iri muri SMS y'ibyemezo kugira ngo dusuzume ubwishyu bwawe.",
    "pay.claimPending": "Ubwishyu bwoherejwe, bategereje gusuzumwa:",
    "pay.claimPhone": "Numero ya MTN wishyuriyeho",
    "pay.claimRef": "Nimero y'igikorwa iri muri SMS",
    "pay.claimSubmit": "Nishyuye ({amount})",
    "pay.loading": "Birimo gukurwa...",
    "pay.invalidLink": "Iyi link yo kwishyura ntabwo ari yo.",
    "pay.invoiceNo": "Fagitire",
    "pay.space": "Umwanya",
    "pay.facility": "Ikigo",
    "pay.period": "Igihe",
    "pay.total": "Igiteranyo",
    "pay.paid": "Asanzwe yishyuwe",
    "pay.balance": "Asigaye kwishyurwa",
    "pay.downloadInvoice": "Kuramo fagitire (PDF)",
    "pay.success": "Twakiriye ubwishyu! Inyemezabwishyu yoherejwe kuri imeri yawe.",
    "pay.failed": "Ubwishyu ntibwarangiye. Ongera ugerageze.",
    "pay.timeout": "Ntituragira icyemezo. Niba wemeje ubwishyu, inyemezabwishyu yawe iraza kuri imeri vuba.",
    "pay.notConfirmed": "Iyi reservasiyo ntiremezwa.",
    "pay.fullyPaid": "Iyi reservasiyo yishyuwe yose. Murakoze!",
    "pay.onlineUnavailable": "Kwishyura kuri interineti ntibirashoboka. Nyamuneka vugana na {email}.",
    "pay.payDeposit": "Ishyura avansi: {amount}",
    "pay.payFull": "Ishyura asigaye yose: {amount}",
    "pay.phoneLabel": "Numero ya MTN cyangwa Airtel",
    "pay.starting": "Turohereza ubusabe...",
    "pay.payButton": "Ishyura na Mobile Money",
    "pay.approvePrompt": "Reba kuri telefone yawe wemeze ubwishyu ukoresheje PIN ya Mobile Money. Iyi paji ihinduka ubwayo.",
    "pay.receipts": "Inyemezabwishyu",
    "pay.startFailed": "Ntitwashoboye gutangiza ubwishyu. Ongera ugerageze.",
    "report.introText": "Ufite ireservasiyo yemejwe? Andika imeyili cyangwa telefone yayo, tuzakohereza kode yo kwemeza ko ari wowe, hanyuma utange ikibazo, usabe ubufasha, cyangwa utange igitekerezo.",
    "report.emailLabel": "Imeyili yo kwiyandikisha *",
    "report.contactLabel": "Imeyili cyangwa telefone yo kwiyandikisha *",
    "report.contactPlaceholder": "wewe@urugero.com cyangwa 07XXXXXXXX",
    "report.contactInvalid": "Andika imeyili cyangwa numero ya telefone wakoresheje mu ireservasiyo yemejwe.",
    "report.sendCode": "Ohereza kode",
    "report.resendCode": "Ongera wohereze kode",
    "report.codeLabel": "Kode y'imibare 6 *",
    "report.codeRequired": "Saba kode hanyuma wandike kode y'imibare 6 twakohereje kuri imeri.",
    "report.codeSentNotice": "Niba bihuye na ireservasiyo yemejwe, kode y'imibare 6 yoherejwe kuri imeri yakoreshejwe muri iyo reservasiyo.",
    "report.emailPlaceholder": "wewe@urugero.com",
    "report.subjectLabel": "Ingingo *",
    "report.subjectPlaceholder": "Ni iki ukeneyeho ubufasha?",
    "report.categoryLabel": "Icyiciro",
    "report.catIncident": "Ikibazo cy'ikigo",
    "report.catMaintenance": "Gusaba ubukorikori",
    "report.catSafety": "Ikibazo cy'umutekano",
    "report.catSuggestion": "Igitekerezo",
    "report.catComplaint": "Ikirego",
    "report.catOther": "Ikindi gisabwa",
    "report.priorityLabel": "Icyihutirwa",
    "report.priorityLow": "Bike",
    "report.priorityMedium": "Hagati",
    "report.priorityHigh": "Byinshi",
    "report.priorityUrgent": "Byihutirwa",
    "report.detailsLabel": "Ibisobanuro *",
    "report.detailsPlaceholder": "Sobanura ikibazo, icyo usaba, cyangwa igitekerezo...",
    "report.sendingText": "Birimo koherezwa...",
    "report.sendButton": "Ohereza ikibazo",
    "report.allRightsReserved": "Uburenganzira bwose burabitswe.",
    "bookings.statusPending": "Bitegerejwe",
    "bookings.statusConfirmed": "Byemejwe",
    "bookings.statusCancelled": "Byahagaritswe",
    "bookings.statusTerminated": "Byavanyweho",
    "bookings.paymentUnpaid": "Ntibyishyuwe",
    "bookings.paymentPartial": "Igice cyishyuwe",
    "bookings.paymentPaid": "Byishyuwe",
    "bookings.pdfHeading": "FMS Pro — Ibisabwa byo Gufata Icyumba",
    "bookings.pdfFacilityLabel": "Ikigo:",
    "bookings.pdfAllFacilities": "Byose",
    "bookings.pdfGeneratedLabel": "Byakozwe:",
    "bookings.pdfColGuest": "Umushyitsi",
    "bookings.pdfColEmail": "Imeyili",
    "bookings.pdfColSpace": "Icyumba",
    "bookings.pdfColStart": "Itangira",
    "bookings.pdfColEnd": "Irangira",
    "bookings.pdfColAttendees": "Abitabiriye",
    "bookings.pdfColStatus": "Imiterere",
    "bookings.pageTitle": "Ibisabwa byo Gufata Ibyumba",
    "bookings.pendingSuffix": "bitegerejwe",
    "bookings.allFacilities": "Ibigo byose",
    "bookings.subtitle": "— gucunga ibisabwa byo gufata ibyumba",
    "bookings.refresh": "Vugurura",
    "bookings.downloadPdf": "Kuramo PDF",
    "bookings.statTotal": "Byose",
    "bookings.statPending": "Bitegerejwe",
    "bookings.statConfirmed": "Byemejwe",
    "bookings.statCancelled": "Byahagaritswe",
    "bookings.tabAll": "Byose",
    "bookings.tabPending": "Bitegerejwe",
    "bookings.tabConfirmed": "Byemejwe",
    "bookings.tabCancelled": "Byahagaritswe",
    "bookings.colGuest": "Umushyitsi",
    "bookings.colSpace": "Icyumba",
    "bookings.colWhen": "Igihe",
    "bookings.colGuestsCount": "Abitabiriye",
    "bookings.colPurpose": "Impamvu",
    "bookings.colStatus": "Imiterere",
    "bookings.colPayment": "Ubwishyu",
    "bookings.colPublic": "Rusange",
    "bookings.colActions": "Ibikorwa",
    "bookings.badgePublic": "Rusange",
    "bookings.badgeInternal": "Imbere mu kigo",
    "bookings.actionViewDetailsTitle": "Reba amakuru arambuye",
    "bookings.actionConfirm": "Emeza",
    "bookings.actionDeclineTitle": "Anga",
    "bookings.actionCancel": "Hagarika",
    "bookings.emptyState": "Nta gisabwa cyo gufata icyumba kiracyaboneka",
    "bookings.modalDetailsTitle": "Amakuru y'Icyifuzo cyo Gufata Icyumba",
    "bookings.sectionGuestInfo": "Amakuru y'Umushyitsi",
    "bookings.labelName": "Amazina",
    "bookings.labelCompany": "Isosiyete",
    "bookings.labelEmail": "Imeyili",
    "bookings.labelPhone": "Telefoni",
    "bookings.sectionBookingDetails": "Amakuru y'Ifatwa ry'Icyumba",
    "bookings.labelSpace": "Icyumba",
    "bookings.labelAttendees": "Abitabiriye",
    "bookings.labelFrom": "Guhera",
    "bookings.labelTo": "Kugeza",
    "bookings.labelPurpose": "Impamvu",
    "bookings.labelNotes": "Inyandiko",
    "bookings.labelStatus": "Imiterere",
    "bookings.labelSource": "Inkomoko",
    "bookings.sourcePublic": "Ifatwa rusange",
    "bookings.sourceInternal": "Imbere mu kigo",
    "bookings.labelTerminationReason": "Impamvu yo Kuvanaho",
    "bookings.labelPayment": "Ubwishyu",
    "bookings.paidOfText": "byishyuwe kuri",
    "bookings.amountReceivedPlaceholder": "Amafaranga yakiriwe",
    "bookings.btnSavePayment": "Bika Ubwishyu",
    "bookings.btnClose": "Funga",
    "bookings.btnDecline": "Anga",
    "bookings.btnConfirmBooking": "Emeza Ifatwa ry'Icyumba",
    "bookings.btnDelete": "Siba",
    "bookings.labelRenewBooking": "Kongera Igihe cy'Ifatwa",
    "bookings.btnRequestRenewal": "Saba Kongera Igihe",
    "bookings.labelTerminateBooking": "Kuvanaho Ifatwa ry'Icyumba",
    "bookings.terminationReasonPlaceholder": "Impamvu yo Kuvanaho",
    "bookings.btnTerminate": "Vanaho",
    "spaces.typeOffice": "Ibiro",
    "spaces.typeConferenceRoom": "Icyumba cy'Inama",
    "spaces.typeTrainingRoom": "Icyumba cy'Amahugurwa",
    "spaces.typeStockRoom": "Ububiko",
    "spaces.typeServerRoom": "Icyumba cya Seriveri",
    "spaces.typeBathroom": "Ubwiherero",
    "spaces.typeParking": "Ahapakwa Imodoka",
    "spaces.typeCommonArea": "Ahantu Rusange",
    "spaces.typeLab": "Laboratwari",
    "spaces.typeOther": "Ikindi",
    "spaces.statusAvailable": "Kiraboneka",
    "spaces.statusOccupied": "Kirimo Gukoreshwa",
    "spaces.statusMaintenance": "Kurimo Gusanwa",
    "spaces.statusReserved": "Cyarindiwe",
    "spaces.pageTitle": "Gucunga Ibyumba",
    "spaces.subtitle": "Igabwa ry'ibyumba, uko bikoreshwa n'ifatwa ryabyo",
    "spaces.addFacility": "Ongeraho Ikigo",
    "spaces.addSpace": "Ongeraho Icyumba",
    "spaces.statTotal": "Ibyumba byose",
    "spaces.statAvailable": "Biraboneka",
    "spaces.statOccupied": "Birakoreshwa",
    "spaces.statOccupancyRate": "Igipimo cy'Ikoreshwa",
    "spaces.tabSpaces": "Ibyumba",
    "spaces.tabBuildings": "Ibigo",
    "spaces.yourFacilityLabel": "Ikigo cyawe: ",
    "spaces.manageHint": "— ushobora gucunga ibyumba by'iki kigo",
    "spaces.searchPlaceholder": "Shakisha ibyumba…",
    "spaces.allBuildings": "Ibigo byose",
    "spaces.allStatus": "Imiterere yose",
    "spaces.floorLabel": "Igorofa",
    "spaces.btnBook": "Fata Icyumba",
    "spaces.emptyState": "Nta cyumba cyabonetse",
    "spaces.floorsCount": "igorofa/amagorofa",
    "spaces.yourFacilityBadge": "Ikigo cyawe",
    "spaces.spacesCountLabel": "ibyumba",
    "spaces.managerLabel": "Umuyobozi:",
    "spaces.btnViewSpaces": "Reba Ibyumba",
    "spaces.btnEdit": "Hindura",
    "spaces.btnDelete": "Siba",
    "spaces.modalEditSpace": "Hindura Icyumba",
    "spaces.modalAddSpace": "Ongeraho Icyumba Gishya",
    "spaces.labelBuilding": "Ikigo *",
    "spaces.selectBuildingOption": "Hitamo Ikigo",
    "spaces.labelSpaceName": "Izina ry'Icyumba *",
    "spaces.labelFloor": "Igorofa",
    "spaces.labelRoomNumber": "Nimero y'Icyumba",
    "spaces.labelType": "Ubwoko",
    "spaces.labelCapacity": "Ubushobozi",
    "spaces.labelArea": "Ubuso (m²)",
    "spaces.labelPricePerHour": "Igiciro ku isaha ({currency})",
    "spaces.labelPricePerDay": "Igiciro ku munsi ({currency})",
    "spaces.labelPricePerMonth": "Igiciro ku kwezi ({currency})",
    "spaces.labelStatus": "Imiterere",
    "spaces.labelDepartment": "Ishami",
    "spaces.labelAssignedTo": "Wahawe",
    "spaces.noAssignmentOption": "Nta wahawe",
    "spaces.labelAmenities": "Ibikoresho",
    "spaces.amenitiesPlaceholder": "urugero: Projector, Ikibaho, AC",
    "spaces.btnCancel": "Hagarika",
    "spaces.savingLabel": "Birimo kubikwa…",
    "spaces.btnSaveSpace": "Bika Icyumba",
    "spaces.modalEditFacility": "Hindura Ikigo",
    "spaces.modalAddFacility": "Ongeraho Ikigo Gishya",
    "spaces.labelFacilityName": "Izina ry'Ikigo *",
    "spaces.labelAddress": "Aho Kiri / Aderesi",
    "spaces.labelFloorsCount": "Umubare w'Amagorofa (si ngombwa)",
    "spaces.labelTotalArea": "Ubuso Bwose (m²)",
    "spaces.labelOpenTime": "Ifungura saa (amasaha yo gufata)",
    "spaces.labelCloseTime": "Ifunga saa (amasaha yo gufata)",
    "spaces.hoursHint": "Ntibihutirwa. Abashyitsi bashobora gufata ibibanza muri aya masaha gusa. Siga ubusa niba nta mbibi.",
    "spaces.labelFacilityManager": "Umuyobozi w'Ikigo *",
    "spaces.requiredHint": "(birakenewe)",
    "spaces.selectManagerOption": "Hitamo Umuyobozi",
    "spaces.managerHint": "Ni abayobozi n'abakuriye sisitemu gusa bashobora gushyirwaho nk'abayobozi b'ikigo.",
    "spaces.btnUpdateFacility": "Vugurura Ikigo",
    "spaces.btnSaveFacility": "Bika Ikigo",
    "spaces.modalBookSpace": "Gufata Icyumba",
    "spaces.labelMeetingTitle": "Umutwe w'Inama",
    "spaces.meetingTitlePlaceholder": "urugero: Inama y'Itsinda",
    "spaces.labelOrderType": "Ubwoko bw'Icyifuzo",
    "spaces.orderTypeBooking": "Ifatwa rigufi",
    "spaces.orderTypeRental": "Ukodesha Icyumba",
    "spaces.labelRentalPeriod": "Igihe cyo Gukodesha",
    "spaces.rentalMonths1": "Ukwezi 1",
    "spaces.rentalMonths3": "Amezi 3",
    "spaces.rentalMonths6": "Amezi 6",
    "spaces.rentalMonths12": "Amezi 12",
    "spaces.labelPriceUnit": "Igipimo cy'Igiciro",
    "spaces.perHourOption": "Ku isaha ·",
    "spaces.perDayOption": "Ku munsi ·",
    "spaces.perMonthOption": "Ku kwezi ·",
    "spaces.labelStartDateTime": "Itariki n'Isaha yo Gutangira *",
    "spaces.labelEndDateTime": "Itariki n'Isaha yo Kurangiza *",
    "spaces.labelAttendees": "Abitabiriye",
    "spaces.labelPurpose": "Impamvu",
    "spaces.depositRequiredLabel": "Amafaranga yo Kwemeza Asabwa ({pct}%)",
    "spaces.depositAgreementText": "Uwasabye yemeye kwishyura avansi ya {amount} ({pct}% by'igiteranyo) kugira ngo yemeze ko ashaka koko mbere y'uko iyi fatwa yemezwa. Kurikirana avansi n'amafaranga asigaye binyuze mu miterere y'ubwishyu bw'iyi fatwa.",
    "spaces.btnConfirmBooking": "Emeza Ifatwa",
    "assets.pageTitle": "Gucunga Ibikoresho",
    "assets.pageSubtitleFacility": "— gukurikirana ibikoresho n'ikiciro byabyo",
    "assets.pageSubtitle": "Kurikirana ibikoresho, ikiciro cyabyo n'igabanuka ry'agaciro",
    "assets.addAsset": "Ongeramo Ikikoresho",
    "assets.statTotalAssets": "Igiteranyo cy'Ibikoresho",
    "assets.statTotalValue": "Agaciro Rusange",
    "assets.searchPlaceholder": "Shakisha ukoresheje izina, itagi, cyangwa nomero ya serivise...",
    "assets.filterAllStatus": "Imiterere Yose",
    "assets.filterAllCategories": "Ibyiciro Byose",
    "assets.colTag": "Itagi",
    "assets.colName": "Izina",
    "assets.colCategory": "Icyiciro",
    "assets.colLocation": "Ahantu",
    "assets.colStatus": "Imiterere",
    "assets.colCondition": "Uko Bimeze",
    "assets.colPurchaseDate": "Itariki yo Kugura",
    "assets.colCurrentValue": "Agaciro Ubu",
    "assets.colActions": "Ibikorwa",
    "assets.noAssetsFound": "Nta kikoresho cyabonetse",
    "assets.modalEditTitle": "Hindura Ikikoresho",
    "assets.modalAddTitle": "Ongeramo Ikikoresho Gishya",
    "assets.fieldName": "Izina *",
    "assets.fieldCategory": "Icyiciro",
    "assets.selectCategory": "Hitamo icyiciro",
    "assets.fieldStatus": "Imiterere",
    "assets.fieldBuilding": "Inyubako",
    "assets.selectBuilding": "Hitamo inyubako",
    "assets.fieldAssignedTo": "Yahawe",
    "assets.unassigned": "Ntawe wahawe",
    "assets.fieldManufacturer": "Uwakoze",
    "assets.fieldModel": "Model",
    "assets.fieldSerialNumber": "Nomero ya Serivise",
    "assets.fieldCondition": "Uko Bimeze",
    "assets.fieldPurchaseDate": "Itariki yo Kugura",
    "assets.fieldPurchaseCost": "Igiciro cyo Kugura ({currency})",
    "assets.fieldCurrentValue": "Agaciro Ubu ({currency})",
    "assets.fieldWarrantyExpiry": "Igihe Garanti Irangira",
    "assets.fieldNotes": "Inyandiko",
    "assets.cancel": "Hagarika",
    "assets.saving": "Kubika...",
    "assets.saveAsset": "Bika Ikikoresho",
    "assets.statusActive": "Birakora",
    "assets.statusInactive": "Ntibikora",
    "assets.statusUnderRepair": "Birasanwa",
    "assets.statusDisposed": "Byavuyeho",
    "assets.statusLost": "Byabuze",
    "assets.conditionExcellent": "Byiza cyane",
    "assets.conditionGood": "Byiza",
    "assets.conditionFair": "Biringaniye",
    "assets.conditionPoor": "Bibi",
    "inventory.txnStockIn": "Kwinjiza mu Bubiko",
    "inventory.txnStockOut": "Gusohora mu Bubiko",
    "inventory.txnAdjusted": "Byahinduwe",
    "inventory.partItems": "ibintu",
    "inventory.partStatistics": "imibare",
    "inventory.loadErrorSuffix": "ntibyashobotse kuboneka.",
    "inventory.restartServerHint": "Nyamuneka ongera utangire seriveri.",
    "inventory.loadErrorGeneric": "Ibyerekeye ububiko ntibyashobotse kuboneka. Reba niba API ihuza neza.",
    "inventory.transactionsLoadError": "Ibikorwa by'ubucuruzi ntibyashobotse kuboneka.",
    "inventory.filtersLoadError": "Muyunguruzi z'ububiko ntizashobotse kuboneka. Reba niba seriveri ikora.",
    "inventory.pageTitle": "Gucunga Ububiko",
    "inventory.pageSubtitle": "Kurikirana ibikoresho, ibice n'ububiko bw'ibikoresho",
    "inventory.addItem": "Ongeramo Igikoresho",
    "inventory.statTotalItems": "Igiteranyo cy'Ibintu",
    "inventory.statLowStock": "Ububiko Buke",
    "inventory.statOutOfStock": "Ububiko Bwarangiye",
    "inventory.statTotalValue": "Agaciro Rusange",
    "inventory.tabItems": "Ibikoresho",
    "inventory.tabTransactions": "Ibikorwa",
    "inventory.searchItemsPlaceholder": "Shakisha ibikoresho...",
    "inventory.statusInStock": "Birahari",
    "inventory.statusLowStock": "Ububiko Buke",
    "inventory.statusOutOfStock": "Ububiko Bwarangiye",
    "inventory.searchTxnPlaceholder": "Shakisha igikoresho, kode, cyangwa umuntu...",
    "inventory.filterAllMovements": "Ibikorwa Byose",
    "inventory.colCode": "Kode",
    "inventory.colName": "Izina",
    "inventory.colCategory": "Icyiciro",
    "inventory.colStock": "Ububiko",
    "inventory.colMin": "Ntoya",
    "inventory.colUnitCost": "Igiciro kya Buri Kimwe",
    "inventory.colStatus": "Imiterere",
    "inventory.colFacility": "Ikigo",
    "inventory.colActions": "Ibikorwa",
    "inventory.noItemsFound": "Nta gikoresho kiri mu bubiko",
    "inventory.colDate": "Itariki",
    "inventory.colItem": "Igikoresho",
    "inventory.colMovement": "Igikorwa",
    "inventory.colQty": "Ingano",
    "inventory.colReason": "Impamvu / Inyandiko",
    "inventory.colBy": "Uwabikoze",
    "inventory.colWorkOrder": "Itegeko ry'Akazi",
    "inventory.noTransactionsFound": "Nta gikorwa cy'ububiko cyanditswe",
    "inventory.modalEditItemTitle": "Hindura Igikoresho",
    "inventory.modalAddItemTitle": "Ongeramo Igikoresho mu Bubiko",
    "inventory.fieldItemCode": "Kode y'Igikoresho *",
    "inventory.itemCodePlaceholder": "urugero: CLN-001",
    "inventory.fieldCategory": "Icyiciro",
    "inventory.selectCategory": "Hitamo icyiciro",
    "inventory.fieldFacility": "Ikigo",
    "inventory.allFacilities": "Ibigo Byose",
    "inventory.fieldUnit": "Igipimo",
    "inventory.unitPlaceholder": "urugero: ibice, litiro",
    "inventory.fieldCurrentQty": "Ingano Iriho",
    "inventory.fieldMinimumStock": "Ububiko Buke Bwemewe",
    "inventory.fieldUnitCost": "Igiciro kya Buri Kimwe ({currency})",
    "inventory.fieldStorageLocation": "Aho Bibikwa",
    "inventory.locationPlaceholder": "urugero: Icyumba A, Igikapu 3",
    "inventory.cancel": "Hagarika",
    "inventory.saving": "Kubika...",
    "inventory.saveItem": "Bika Igikoresho",
    "inventory.modalStockInTitle": "📥 Kwinjiza mu Bubiko — {name}",
    "inventory.modalStockOutTitle": "📤 Gusohora mu Bubiko — {name}",
    "inventory.currentStockLabel": "Ububiko buriho:",
    "inventory.unitsFallback": "ibice",
    "inventory.fieldTransactionType": "Ubwoko bw'Igikorwa",
    "inventory.optionStockInReceived": "📥 Kwinjiza (Byakiriwe)",
    "inventory.optionStockOutUsed": "📤 Gusohora (Byakoreshejwe)",
    "inventory.optionAdjustSetQty": "🔄 Guhindura (Shyiramo ingano nyayo)",
    "inventory.fieldQuantity": "Ingano *",
    "inventory.placeholderNewTotalQty": "Ingano nshya rusange",
    "inventory.placeholderQuantity": "Ingano",
    "inventory.fieldReasonStockOut": "Impamvu yo Gusohora mu Bubiko *",
    "inventory.fieldNotes": "Inyandiko",
    "inventory.placeholderReasonOut": "urugero: Byakoreshejwe mu gusana WO-00123",
    "inventory.placeholderReasonIn": "urugero: Byakiriwe uturutse ku mugurisha",
    "inventory.stockOutAuditNote": "Buri gusohora mu bubiko bwanditswe hamwe n'izina ryawe, ingano, n'impamvu muri tabu y'Ibikorwa.",
    "inventory.recording": "Kwandika...",
    "inventory.recordTransaction": "Andika Igikorwa",
    "maintenance.pageTitle": "Gucunga Ubusanamurwa",
    "maintenance.pageSubtitle": "ubusanamurwa bwo kwirinda n'ubwo gukosora",
    "maintenance.pageSubtitleShort": "Gahunda z'ubusanamurwa bwo kwirinda n'ubwo gukosora",
    "maintenance.importTooltip": "Twinjize gahunda uhereye kuri tabulo (.xlsx, .xls, .csv)",
    "maintenance.importing": "Birasomwa...",
    "maintenance.importButton": "Twinjize",
    "maintenance.exportTooltip": "Ohereza gahunda zihari muri tabulo",
    "maintenance.exporting": "Birimo koherezwa...",
    "maintenance.exportButton": "Ohereza",
    "maintenance.logWorkButton": "Andika akazi",
    "maintenance.newScheduleButton": "Gahunda nshya",
    "maintenance.statOverdue": "Byarengeje igihe",
    "maintenance.statDueThisMonth": "Bikwiye uku kwezi",
    "maintenance.statTotalSchedules": "Igiteranyo cya gahunda",
    "maintenance.statTotalLogs": "Igiteranyo cy'inyandiko",
    "maintenance.tabSchedules": "Gahunda",
    "maintenance.tabUpcoming": "Biza vuba",
    "maintenance.tabLogs": "Inyandiko",
    "maintenance.colTitle": "Umutwe",
    "maintenance.colSpace": "Ahantu hitabwaho",
    "maintenance.colAsset": "Igikoresho",
    "maintenance.colFrequency": "Inshuro",
    "maintenance.colStartDate": "Itariki itangira",
    "maintenance.colEndDate": "Itariki irangira",
    "maintenance.colAssigned": "Uwagenewe",
    "maintenance.colLastPerformed": "Igihe cyanyuma cyakozwe",
    "maintenance.colParts": "Ibice",
    "maintenance.colActions": "Ibikorwa",
    "maintenance.freqDaily": "buri munsi",
    "maintenance.freqWeekly": "buri cyumweru",
    "maintenance.freqMonthly": "buri kwezi",
    "maintenance.freqQuarterly": "buri gihembwe",
    "maintenance.freqSemiAnnual": "kabiri mu mwaka",
    "maintenance.freqAnnual": "buri mwaka",
    "maintenance.markDoneTooltip": "Shyira ko byarangiye (bibika kandi bigena itariki ikurikira)",
    "maintenance.logTooltip": "Andika ubusanamurwa (inyandiko, ibice, igiciro)",
    "maintenance.edit": "Hindura",
    "maintenance.delete": "Siba",
    "maintenance.noSchedules": "Nta gahunda irahari",
    "maintenance.startingLabel": "Itangira: ",
    "maintenance.endingLabel": "Irangira: ",
    "maintenance.assignedLabel": "Uwagenewe: ",
    "maintenance.noUpcoming": "Nta busanamurwa buteganyijwe mu minsi 30 iri imbere",
    "maintenance.logColDate": "Itariki",
    "maintenance.logColSpace": "Ahantu hitabwaho",
    "maintenance.logColAsset": "Igikoresho",
    "maintenance.logColDescription": "Ibisobanuro",
    "maintenance.logColFindings": "Ibyabonetse",
    "maintenance.logColPartsUsed": "Ibice byakoreshejwe",
    "maintenance.logColCost": "Igiciro",
    "maintenance.logColPerformedBy": "Uwakoze",
    "maintenance.noLogs": "Nta nyandiko y'ubusanamurwa irahari",
    "maintenance.editScheduleModalTitle": "Hindura gahunda",
    "maintenance.newScheduleModalTitle": "Gahunda nshya y'ubusanamurwa",
    "maintenance.labelTitle": "Umutwe *",
    "maintenance.labelDescription": "Ibisobanuro",
    "maintenance.labelAction": "Igikorwa cy'ubusanamurwa",
    "maintenance.labelAsset": "Igikoresho",
    "maintenance.labelAffectedSpace": "Ahantu hagizweho ingaruka",
    "maintenance.labelFrequency": "Inshuro",
    "maintenance.labelStartDate": "Itariki itangira *",
    "maintenance.labelEndDate": "Itariki irangira",
    "maintenance.labelAssignedTo": "Uwagenewe",
    "maintenance.labelEstimatedHours": "Amasaha ateganyijwe",
    "maintenance.scheduleTitlePlaceholder": "urugero: Guhindura filteri ya HVAC buri kwezi",
    "maintenance.actionPlaceholder": "Sobanura akazi kazakorwa n'uko ahantu hashobora kugirwaho ingaruka",
    "maintenance.noSpecificAsset": "Nta gikoresho cyihariye",
    "maintenance.noSpecificSpace": "Nta hantu hihariye",
    "maintenance.unassigned": "Ntawe wagenewe",
    "maintenance.endDateHint": "Byabazwe byikoresha hashingiwe ku itariki itangira n'inshuro",
    "maintenance.notifyAffectedLabel": "Ohereza imeyili ku mabwiriza n'ukodesha byemejwe bigizweho ingaruga mbere y'iki gikorwa",
    "maintenance.checking": "Birimo kugenzurwa...",
    "maintenance.previewAffected": "Reba abagizweho ingaruka",
    "maintenance.affectedCountLabel": "ukwemeza(n) cyangwa ukodesha(n) bizamenyeshwa:",
    "maintenance.rental": "Ukodesha",
    "maintenance.booking": "Ukwiyandikisha",
    "maintenance.noEmail": "Nta meyili ihari",
    "maintenance.previewHint": "Reba aho bihurira mbere yo kubika. Gusa ukwemeza n'ukodesha bikora ni byo bimenyeshwa binyuze kuri imeyili.",
    "maintenance.plannedPartsLabel": "Ibice biteganyijwe",
    "maintenance.plannedPartsHint": "Hitamo ibikoresho gahunda izakenera — bizongerwaho ako kanya ubitse.",
    "maintenance.noPartsPlanned": "Nta gice giteganyijwe kugeza ubu.",
    "maintenance.selectPart": "Hitamo igice...",
    "maintenance.addPartButton": "Ongeraho",
    "maintenance.partUsed": "Byakoreshejwe",
    "maintenance.partReady": "Byiteguye",
    "maintenance.partShort": "Birabuze",
    "maintenance.removeFromPlan": "Kuraho muri gahunda",
    "maintenance.assetRequestsLabel": "Ibisabwe by'ibikoresho ({n})",
    "maintenance.assetRequestsHint": "Ibisabwe n'umukozi wagenewe ku kintu kitari mu bubiko. Kwemeza no kwemeza PO byongera ako kanya hano.",
    "maintenance.requestedBy": "Byasabwe na {name} ·",
    "maintenance.restockItem": "Kongera mu bubiko",
    "maintenance.newAsset": "Igikoresho gishya",
    "maintenance.autoAddedAfterPO": "Byongewemo ako kanya nyuma yo kwemeza PO",
    "maintenance.vendorSubmissions": "Ibyoherejwe n'abacuruzi ({n})",
    "maintenance.approveCreateLink": "Emeza wongere ushyireho ihuza ry'umucuruzi",
    "maintenance.reject": "Anga",
    "maintenance.copyVendorLink": "Koporora ihuza ry'umucuruzi",
    "maintenance.approveQuotation": "Emeza igiciro cyatoranijwe",
    "maintenance.rejectQuotation": "Anga igiciro cyatoranijwe",
    "maintenance.noAssetRequests": "Nta kisabwe cy'ikikoresho kuri iyi gahunda.",
    "maintenance.cancel": "Kureka",
    "maintenance.saving": "Birabikwa...",
    "maintenance.saveSchedule": "Bika gahunda",
    "maintenance.createAddParts": "Kurema no kongeraho ibice",
    "maintenance.logModalTitle": "Andika akazi k'ubusanamurwa",
    "maintenance.logLabelAsset": "Igikoresho",
    "maintenance.logLabelSchedule": "Gahunda (niba ihari)",
    "maintenance.logLabelDescription": "Ibisobanuro by'akazi",
    "maintenance.logLabelFindings": "Ibyabonetse / Ibyitegererejwe",
    "maintenance.logLabelPartsUsed": "Ibice byakoreshejwe",
    "maintenance.logLabelCost": "Igiciro ({currency})",
    "maintenance.logLabelNextDue": "Igihe gikurikira (si itegeko)",
    "maintenance.selectAsset": "Hitamo igikoresho",
    "maintenance.none": "Nta na kimwe",
    "maintenance.workDonePlaceholder": "Icyakozwe...",
    "maintenance.notEnoughStock": "Nta bihagije mu bubiko",
    "maintenance.addPartUsedButton": "+ Ongeraho igice cyakoreshejwe",
    "maintenance.partsDeductionHint": "Buri gice gitoranijwe hano gikurwa mu bubiko igihe ubitse — birabujijwe niba bibuze keretse ubyemeje.",
    "maintenance.notesLabel": "Inyandiko (gusa niba utatoranya mu bubiko hejuru)",
    "maintenance.partsNotesPlaceholder": "urugero: Filteri #3M-500 (itari mu bubiko)",
    "maintenance.nextDueHint": "Reka bibe ubusa kugira ngo bikomeze byikoresha hashingiwe ku nshuro ya gahunda uhereye uyu munsi",
    "maintenance.savingLog": "Birabikwa...",
    "maintenance.saveLog": "Bika inyandiko",
    "maintenance.importModalTitle": "Twinjize gahunda z'ubusanamurwa",
    "maintenance.readyToImport": "biteguye kwinjizwa",
    "maintenance.rowsSkipped": "umurongo(mirongo) uzasimburwa (umutwe cyangwa itariki bibuze)",
    "maintenance.importColRow": "Umurongo",
    "maintenance.importColTitle": "Umutwe",
    "maintenance.importColFrequency": "Inshuro",
    "maintenance.importColStartDate": "Itariki itangira",
    "maintenance.importColAssigned": "Uwagenewe",
    "maintenance.importColNotes": "Inyandiko",
    "maintenance.importing2": "Birimo kwinjizwa...",
    "maintenance.importCount": "Injiza gahunda {n}",
    "workOrders.pageTitle": "Amategeko y'Akazi",
    "workOrders.pageSubtitle": "ibisabwa by'ubufasha n'imirimo",
    "workOrders.pageSubtitleShort": "Gucunga ibisabwa by'ubufasha n'imirimo",
    "workOrders.newButton": "Itegeko ry'akazi",
    "workOrders.searchPlaceholder": "Shakisha amategeko y'akazi...",
    "workOrders.allStatus": "Imiterere yose",
    "workOrders.statusOpen": "Bifunguye",
    "workOrders.statusAssigned": "Byagenewe",
    "workOrders.statusInProgress": "Birimo gukorwa",
    "workOrders.statusOnHold": "Byahagaritswe by'agateganyo",
    "workOrders.statusCompleted": "Byarangiye",
    "workOrders.statusCancelled": "Byahagaritswe",
    "workOrders.allPriority": "Ibyihutirwa byose",
    "workOrders.priorityLow": "Byoroheje",
    "workOrders.priorityMedium": "Bya rugero",
    "workOrders.priorityHigh": "Byihutirwa",
    "workOrders.priorityCritical": "Byihutirwa cyane",
    "workOrders.colWoNumber": "Nomero y'itegeko",
    "workOrders.colTitle": "Umutwe",
    "workOrders.colType": "Ubwoko",
    "workOrders.colPriority": "Icyihutirwa",
    "workOrders.colStatus": "Imiterere",
    "workOrders.colAssignedTo": "Uwagenewe",
    "workOrders.colDueDate": "Itariki ntarengwa",
    "workOrders.colParts": "Ibice",
    "workOrders.colActions": "Ibikorwa",
    "workOrders.partsReady": "Byiteguye",
    "workOrders.partsShort": "Birabuze",
    "workOrders.view": "Reba",
    "workOrders.edit": "Hindura",
    "workOrders.delete": "Siba",
    "workOrders.noneFound": "Nta tegeko ry'akazi ryabonetse",
    "workOrders.editModalTitle": "Hindura itegeko ry'akazi",
    "workOrders.createModalTitle": "Itegeko ry'akazi",
    "workOrders.labelTitle": "Umutwe *",
    "workOrders.labelDescription": "Ibisobanuro",
    "workOrders.labelType": "Ubwoko",
    "workOrders.labelPriority": "Icyihutirwa",
    "workOrders.labelStatus": "Imiterere",
    "workOrders.labelAssignedTo": "Uwagenewe",
    "workOrders.labelBuilding": "Inyubako",
    "workOrders.labelAsset": "Igikoresho",
    "workOrders.labelDueDate": "Itariki ntarengwa",
    "workOrders.labelEstimatedHours": "Amasaha ateganyijwe",
    "workOrders.labelNotes": "Inyandiko",
    "workOrders.typeCorrective": "gukosora",
    "workOrders.typePreventive": "kwirinda",
    "workOrders.typeInspection": "igenzura",
    "workOrders.typeInstallation": "gushyiraho",
    "workOrders.typeOther": "ikindi",
    "workOrders.unassigned": "Ntawe wagenewe",
    "workOrders.selectBuilding": "Hitamo inyubako",
    "workOrders.noAsset": "Nta kikoresho",
    "workOrders.cancel": "Kureka",
    "workOrders.saving": "Birabikwa...",
    "workOrders.saveButton": "Bika itegeko ry'akazi",
    "workOrders.partsNeededLabel": "Ibice bikenewe ({n})",
    "workOrders.needHaveLabel": "Bikenewe {q}{unit} · Birahari {avail}{unit}",
    "workOrders.partDeducted": "Byakuwe mu bubiko",
    "workOrders.removeFromPlan": "Kuraho muri gahunda",
    "workOrders.noPartsPlanned": "Nta gice giteganyijwe kuri uyu murimo kugeza ubu.",
    "workOrders.selectPart": "Hitamo igice...",
    "workOrders.qtyPlaceholder": "Umubare",
    "workOrders.addPartButton": "Ongeraho",
    "workOrders.completeDeductHint": "Gushyira iri tegeko ry'akazi ko Ryarangiye bikura ibi mu bubiko ako kanya — birabujijwe niba bibuze keretse ubyemeje.",
    "workOrders.assetRequestsLabel": "Ibisabwe by'ibikoresho ({n})",
    "workOrders.requestedBy": "Byasabwe na {name} ·",
    "workOrders.restockItem": "Kongera mu bubiko",
    "workOrders.newAsset": "Igikoresho gishya",
    "workOrders.autoAddedAfterPO": "Byongewemo ako kanya kuri uyu murimo nyuma yo kwemeza PO",
    "workOrders.vendorSubmissions": "Ibyoherejwe n'abacuruzi ({n})",
    "workOrders.approveCreateLink": "Emeza kandi ushyireho ihuza ry'umucuruzi",
    "workOrders.reject": "Anga",
    "workOrders.copyVendorLink": "Koporora ihuza ry'umucuruzi",
    "workOrders.approveQuotation": "Emeza igiciro cyatoranijwe",
    "workOrders.rejectQuotation": "Anga igiciro cyatoranijwe",
    "workOrders.noAssetRequests": "Nta kisabwe cy'ikikoresho kuri iri tegeko ry'akazi.",
    "workOrders.activityLogLabel": "Urutonde rw'ibikorwa ({n})",
    "workOrders.noUpdates": "Nta vyavuguruwe kugeza ubu.",
    "workOrders.commentPlaceholder": "Ongeraho igitekerezo...",
    "workOrders.postButton": "Ohereza",
    "dashboard.loading": "Birimo gutangira...",
    "dashboard.allClear": "Ibintu byose biri meza uyu munsi — nta kintu cyihutirwa gisaba ko ukigenzura.",
    "dashboard.attentionMessage": "Ufite ibintu {n} bisaba ko uyu munsi ubitaho.",
    "dashboard.groupWorkOrdersTitle": "Amabwiriza y'akazi",
    "dashboard.groupWorkOrdersSubtitle": "Ibyifuzo by'imisanamiterere n'ubufasha",
    "dashboard.captionTotalWO": "Amabwiriza y'akazi yose yanditswe, afunguye cyangwa yarangiye",
    "dashboard.captionOpenWO": "Yanditswe ariko ntawatangiye kuyakoraho",
    "dashboard.captionCriticalWO": "Byashyizweho ko ari ibyihutirwa — bigomba gukorwaho mbere",
    "dashboard.groupFacilityTitle": "Ikorwaremezo",
    "dashboard.groupFacilitySubtitle": "Ibyumba, ibikoresho n'isuku",
    "dashboard.captionAssets": "Ibikoresho bikurikiranwa muri sisitemu",
    "dashboard.captionSpaces": "Ibyumba n'ahantu bishobora gufatirwa ubu",
    "dashboard.captionMaintenance": "Isuku yateganyijwe ariko yarangije igihe",
    "dashboard.groupInventoryTitle": "Ububiko n'Ibyifuzo",
    "dashboard.groupInventorySubtitle": "Urugero rw'ububiko n'ibyifuzo bitegerejwe kwemezwa",
    "dashboard.captionLowStock": "Ibintu bisigaye bike mu bubiko",
    "dashboard.captionRequests": "Ibyifuzo bitegereje ko ubyemeza",
    "dashboard.captionAlerts": "Amatangazo utaragaragaza",
    "dashboard.trendCaption": "Umubare w'amabwiriza y'akazi yinjiye buri kwezi (ubururu) ugereranyije n'uwarangije (icyatsi).",
    "dashboard.statusCaption": "Amabwiriza y'akazi yose ariho ubu, yashyizwe mu byiciro bikurikije aho ageze.",
    "dashboard.recentCaption": "Amabwiriza y'akazi yanditswe vuba aha, atangirana n'ayashya.",
    "dashboard.upcomingMaintCaption": "Isuku igiye kuza vuba, kugira ngo ntacyakugusha mu buryo utiteze.",
    "dashboard.requestsCaption": "Ibintu abakozi basabye bitegereje icyemezo cyawe.",
    "dashboard.bookingsCaption": "Gufata ibyumba bigiye kuza.",
    "settings.sectionGeneral": "Ibisanzwe",
    "settings.systemNameLabel": "Izina ry'uburyo",
    "settings.orgNameLabel": "Izina ry'umuryango",
    "settings.orgNamePlaceholder": "Umuryango wanjye",
    "settings.contactEmailLabel": "Imeli y'itumanaho",
    "settings.notifLogoLabel": "Ikirango cy'amatangazo",
    "settings.removeLogo": "Gukuraho ikirango",
    "settings.currencyLabel": "Ifaranga",
    "settings.timezoneLabel": "Isaha y'akarere",
    "settings.sectionHomepage": "Guhindura urupapuro rw'itangiriro",
    "settings.heroTitleLabel": "Umutwe w'ingenzi",
    "settings.heroTitlePlaceholder": "Fata Ibyumba Byiza",
    "settings.heroSubtitleLabel": "Agatwe",
    "settings.heroSubtitlePlaceholder": "Ku Buryo Bwose",
    "settings.heroDescLabel": "Ibisobanuro by'ingenzi",
    "settings.heroDescHint": "Bigaragara munsi y'umutwe w'ingenzi ku rupapuro rw'itangiriro. Koresha {currency} kugira ngo wongeremo kode y'ifaranga. Ururimi rusigaye ubusa rukoresha inyandiko y'icyongereza.",
    "settings.aboutLabel": "Inyandiko ku Byerekeye",
    "settings.aboutPlaceholder": "Bwira abasura ibijyanye n'ibikorwaremezo byawe...",
    "settings.phoneLabel": "Telefoni",
    "settings.addressLabel": "Aderesi",
    "settings.publicEmailLabel": "Imeli rusange y'itumanaho",
    "settings.hoursLabel": "Amasaha y'akazi",
    "settings.ratingStatLabel": "Inyito y'amanota",
    "settings.ratingStatHint": "Bigaragara nk'umubare wa « Amanota » ku rupapuro rw'itangiriro",
    "settings.availabilityStatLabel": "Inyito y'uko bihagaze",
    "settings.availabilityStatHint": "Bigaragara nk'umubare wa « Biraboneka » ku rupapuro rw'itangiriro",
    "settings.slideTitleLabel": "Umutwe w'ishusho igenda",
    "settings.slideImageLabel": "Ifoto y'ishusho igenda",
    "settings.removeImage": "Gukuraho ifoto",
    "settings.slideVideoLabel": "Videwo y'ishusho igenda (bitegetswe)",
    "settings.slideVideoHint": "Niba ishyizweho, izakoreshwa aho kuba ifoto iri hejuru. MP4, WebM, cyangwa MOV, kugeza kuri MB 20.",
    "settings.removingVideo": "Birimo gukurwaho...",
    "settings.removeVideo": "Gukuraho videwo",
    "settings.partnersHeading": "Abafatanyabikorwa",
    "settings.partnersHint": "Bigaragara nk'umurongo w'ibirango ku rupapuro rusange rw'itangiriro. Siga umwanya w'ikirango ubusa kugira ngo wuhishe.",
    "settings.partnerNameLabel": "Izina ry'umufatanyabikorwa {n}",
    "settings.partnerNamePlaceholder": "urugero: Acme Corp",
    "settings.partnerLogoLabel": "Ikirango cy'umufatanyabikorwa {n}",
    "settings.socialHeading": "Imiyoboro y'imbuga nkoranyambaga",
    "settings.legalHeading": "Inyandiko z'amategeko",
    "settings.privacyLabel": "Politiki y'ibanga",
    "settings.privacyPlaceholder": "Andika inyandiko ya politiki y'ibanga hano...",
    "settings.termsLabel": "Amabwiriza y'ikoreshwa",
    "settings.termsPlaceholder": "Andika inyandiko y'amabwiriza y'ikoreshwa hano...",
    "settings.sectionSmtp": "Igenamiterere rya Imeli / SMTP",
    "settings.smtpBanner": "Shyiraho SMTP kugira ngo wemeze imeli zo kwakira abakoresha bashya. Gmail: koresha 'App Password' na smtp.gmail.com:587",
    "settings.smtpHostLabel": "Seriveri ya SMTP",
    "settings.smtpPortLabel": "Umuyoboro (Port)",
    "settings.smtpFromLabel": "Aderesi y'uwohereza",
    "settings.smtpUserLabel": "Izina ry'ukoresha rya SMTP",
    "settings.smtpPasswordLabel": "Ijambo banga rya SMTP",
    "settings.testEmailHeading": "Gupima Imeli",
    "settings.testEmailPlaceholder": "Ohereza igerageza kuri...",
    "settings.sendingTest": "Birimo koherezwa...",
    "settings.sendTest": "Ohereza Igerageza",
    "settings.testFailed": "Igerageza ntiryagenze neza",
    "settings.sectionDataIO": "Kohereza no Kwinjiza Amakuru",
    "settings.dataIoIntro": "Sohora amakuru muri CSV kugira ngo ubike cyangwa winjize inyubako, ibikoresho, n'ibintu by'ububiko bishya. Kwinjiza amakuru kongera amakuru mashya ntibisimbuza ayariho.",
    "settings.exportCsv": "Sohora muri CSV",
    "settings.importCsv": "Injiza CSV",
    "settings.exportSuccess": "{resource} byoherejwe neza.",
    "settings.exportFailed": "Kohereza ntibyagenze neza.",
    "settings.importFailed": "Kwinjiza ntibyagenze neza.",
    "settings.sectionBackup": "Kubika no Kugarura",
    "settings.createBackupNow": "Kubika Nonaha",
    "settings.creatingBackup": "Kubika birimo gukorwa...",
    "settings.refresh": "Vugurura",
    "settings.backupTableBackup": "Ikibitswe",
    "settings.backupTableType": "Ubwoko",
    "settings.backupTableSize": "Ingano",
    "settings.backupTableCreated": "Yabitswe",
    "settings.backupTableActions": "Ibikorwa",
    "settings.backupFailed": "Byananiranye",
    "settings.download": "Kuramo",
    "settings.restore": "Garura",
    "settings.delete": "Siba",
    "settings.noBackups": "Nta kibitswe kiriho — kanda kuri « Kubika Nonaha » kugira ngo ukore icya mbere.",
    "settings.restoreModalTitle": "Garura ukoresheje icyabitswe?",
    "settings.restoreModalBody": "Ibi bizasimbuza amakuru yose ariho ubu n'ibiri muri {filename} (byo ku {date}). Hazabanza gufatwa ikibitswe cy'ubwirinzi cy'ibiriho ubu, ariko ibyongeweho cyangwa byahinduwe nyuma y'icyo gihe bizabura.",
    "settings.restoreModalConfirmLabel": "Andika RESTORE kugira ngo wemeze:",
    "settings.cancel": "Hagarika",
    "settings.restoring": "Birimo gugarurwa...",
    "settings.restoreNow": "Garura Nonaha",
    "settings.sectionSecurity": "Umutekano",
    "settings.securityIntro": "Igenzura ry'uburenganzira rishingiye ku nshingano rikoreshwa kuri serivisi zose za API:",
    "settings.roleAdminDesc": "uburenganzira bwuzuye: ibikorwaremezo, abakoresha, igenamiterere, amakuru yose",
    "settings.roleManagerDesc": "acunga ibyumba by'ikorwaremezo ryahawe",
    "settings.roleTechnicianDesc": "acunga amabwiriza y'akazi n'inyandiko z'isuku",
    "settings.roleStaffDesc": "uburenganzira bwo gusoma + gutanga amabwiriza y'akazi + gufata ibyumba",
    "settings.newUserPasswordNote": "Abakoresha bashya bose bahabwa ijambo banga ry'igihe gito binyuze kuri imeli kandi bagomba kurihindura ubwo binjira ku nshuro ya mbere.",
    "settings.pageTitle": "Igenamiterere rya Sisitemu",
    "settings.pageSubtitle": "Abayobozi gusa — shyiraho ibikunzwe muri sisitemu yose",
    "settings.savedBadge": "✅ Igenamiterere ryabitswe",
    "settings.saving": "Birimo kubikwa...",
    "settings.saveSettings": "Bika Igenamiterere",
    "users.pageTitleManager": "Itsinda — {facility}",
    "users.pageTitleAdmin": "Gucunga Abakoresha",
    "users.myFacilityFallback": "Ikorwaremezo ryanjye",
    "users.subtitleManager": "Gucunga abakozi bo mu ikorwaremezo ryawe",
    "users.subtitleAdmin": "Umuyobozi — gucunga abakoresha bose ba sisitemu",
    "users.addEmployee": "Ongeraho Umukozi",
    "users.addUser": "Ongeraho Ukoresha",
    "users.colName": "Izina",
    "users.colEmail": "Imeli",
    "users.colRole": "Inshingano",
    "users.colJobTitle": "Akazi",
    "users.colFacility": "Ikorwaremezo",
    "users.colDepartment": "Ishami",
    "users.colStatus": "Imimerere",
    "users.colJoined": "Yinjiye",
    "users.colActions": "Ibikorwa",
    "users.active": "Arakora",
    "users.inactive": "Ntaragira",
    "users.noneFound": "Nta mukoresha wabonetse",
    "users.editModalTitle": "Hindura: {name}",
    "users.fullNameLabel": "Amazina yombi",
    "users.emailLabel": "Aderesi ya imeli",
    "users.roleLabel": "Inshingano",
    "users.jobTitleLabel": "Akazi",
    "users.facilityLabel": "Shyira mu ikorwaremezo",
    "users.departmentLabel": "Ishami",
    "users.phoneLabel": "Telefoni",
    "users.statusLabel": "Imimerere",
    "users.noFacility": "Nta ikorwaremezo",
    "users.cancel": "Hagarika",
    "users.saving": "Birimo kubikwa...",
    "users.saveChanges": "Bika Impinduka",
    "users.createModalTitleManager": "Ongeraho Umukozi mu Ikorwaremezo Ryawe",
    "users.createModalTitleAdmin": "Ongeraho Ukoresha Mushya",
    "users.tempPasswordNote": "Ijambo banga ry'igihe gito rizakorwa rikoherezwe kuri imeli y'ukoresha.",
    "users.willBeAddedTo": "Azongerwa muri: {facility}",
    "users.creating": "Birimo kurema...",
    "users.createUser": "Rema Ukoresha",
    "users.createdModalTitle": "Ukoresha Yaremwe",
    "users.accountCreatedFor": "Konti yaremewe {name}",
    "users.welcomeEmailSent": "✅ Imeli yo kwakira yoherejwe kuri {email} ifite amakuru yo kwinjira.",
    "users.welcomeEmailFailed": "⚠️ Imeli ntiyoherejwe ({msg}). Hereza amakuru yo kwinjira ku bundi buryo.",
    "users.canChangePasswordNote": "Ukoresha ashobora guhindura ijambo banga igihe cyose kuva mu gace k'ibumoso nyuma yo kwinjira.",
    "users.done": "Byarangiye",
    "role.admin": "Umuyobozi",
    "role.manager": "Umuyobora",
    "role.employee": "Umukozi",
    "developer.createSystemFailed": "Birananiye kurema sisitemu",
    "developer.openSystemFailed": "Birananiye gufungura iyo sisitemu",
    "developer.backupHeading": "Kubika no Kugarura",
    "developer.backupIntro": "Ibika isanduku y'amakuru ya iyi gahunda uri muri yo ubu — isanduku nyamukuru ya sisitemu rusange igihe winjiye nka umutekinisiye, cyangwa isanduku ya sisitemu runaka igihe urimo kuyikoresha unyuze kuri « Fungura ». Niba ushaka kubika indi sisitemu, yifungure mbere, hanyuma usubire mu Igenamiterere ryayo.",
    "developer.systemsHeading": "Sisitemu Zitandukanye",
    "developer.systemsIntro": "Buri sisitemu ifite isanduku y'amakuru yihariye itandukanye na izindi — abakoresha, ibyumba, amabwiriza y'akazi, ibikoresho, ikirango n'igenamiterere byayo. Ikintu cyinjijwe muri sisitemu imwe ntikigaragara muri indi.",
    "developer.creatingSystem": "Birimo kuremwa…",
    "developer.createSystem": "Rema",
    "developer.credsReady": "« {name} » iteguye — bika aya makuru nonaha, ntazongera kugaragazwa",
    "developer.credsAdminEmail": "Imeli y'umuyobozi:",
    "developer.credsTempPassword": "Ijambo banga ry'igihe gito:",
    "developer.credsSignInLink": "Umuyoboro wo kwinjira:",
    "developer.copyPassword": "Koporora ijambo banga",
    "developer.copyLink": "Koporora umuyoboro",
    "developer.dismiss": "Funga",
    "developer.noSystems": "Nta sisitemu iriho — rema iya mbere hejuru.",
    "developer.systemActive": "Irakora",
    "developer.systemDeactivated": "Yahagaritswe",
    "developer.copySignInLink": "Koporora umuyoboro wo kwinjira",
    "developer.openButton": "Fungura",
    "developer.deactivateButton": "Hagarika",
    "developer.activateButton": "Tangiza",
    "developer.deleteSystemTitle": "Siba burundu iyi sisitemu n'amakuru yayo yose",
    "developer.deleteButton": "Siba",
    "developer.newSystemNamePlaceholder": "Izina rya sisitemu nshya (urugero: Acme Co)",
    "developer.adminEmailPlaceholder": "Imeli y'umuyobozi (si ngombwa)",
    "developer.createBackupNow": "Kubika Nonaha",
    "developer.creatingBackup": "Kubika birimo gukorwa...",
    "developer.refresh": "Vugurura",
    "developer.backupTableBackup": "Ikibitswe",
    "developer.backupTableType": "Ubwoko",
    "developer.backupTableSize": "Ingano",
    "developer.backupTableCreated": "Yabitswe",
    "developer.backupTableActions": "Ibikorwa",
    "developer.backupFailed": "Byananiranye",
    "developer.download": "Kuramo",
    "developer.restore": "Garura",
    "developer.delete": "Siba",
    "developer.noBackups": "Nta kibitswe kiriho — kanda kuri « Kubika Nonaha » kugira ngo ukore icya mbere.",
    "developer.restoreModalTitle": "Garura ukoresheje icyabitswe?",
    "developer.restoreModalBody": "Ibi bizasimbuza amakuru yose ariho ubu n'ibiri muri {filename} (byo ku {date}). Hazabanza gufatwa ikibitswe cy'ubwirinzi cy'ibiriho ubu, ariko ibyongeweho cyangwa byahinduwe nyuma y'icyo gihe bizabura.",
    "developer.restoreModalConfirmLabel": "Andika RESTORE kugira ngo wemeze:",
    "developer.cancel": "Hagarika",
    "developer.restoring": "Birimo gugarurwa...",
    "developer.restoreNow": "Garura Nonaha",
    "messages.postingStatus": "Kohereza...",
    "messages.newBadge": "bishya",
    "myTasks.statusCancelled": "Byahagaritswe",
    "myTasks.requestItemHeading": "Saba ikintu cyangwa igikoresho",
    "myTasks.newAssetTab": "Igikoresho gishya",
    "myTasks.restockItemTab": "Ongera ibicuruzwa",
    "myTasks.selectInventoryItem": "Hitamo ikintu kiri mu bubiko...",
    "myTasks.assetNeededPlaceholder": "Igikoresho gikenewe (urugero: enjeniyi nshya)",
    "myTasks.sendRequestButton": "Ohereza icyifuzo",
    "myTasks.partDeducted": "Byakuwemo",
    "myTasks.partReady": "Biteguye",
    "myTasks.noPartsLogged": "Nta kintu cyanditswe nk'icyakoreshejwe.",
    "myTasks.autoDeductNote": "Gushyira iri bwiriza ry'akazi ku 'Byarangiye' bikuramo ibi mu bubiko byikoresha.",
    "myTasks.activityLog": "Ibikorwa byanditswe",
    "myTasks.submitProgressReport": "Ohereza raporo y'aho bigeze",
    "myTasks.nextDue": "Igihe gikurikira",
    "myTasks.logMaintenanceWork": "Andika akazi ko kubungabunga",
    "myTasks.itemsUsedFromStock": "Ibintu byakoreshejwe (mu bubiko)",
    "myTasks.needSomethingElseNote": "Ukeneye ikintu kitari mu bubiko? Funga iki hanyuma ukoreshe 'Saba ikintu/igikoresho'.",
    "myTasks.logAsDone": "Andika ko byarangiye",
    "myTasks.requestItemAssetButton": "Saba ikintu/igikoresho",
    "myTasks.logWorkButton": "Andika akazi",
    "myTasks.noMaintenanceJobs": "Nta kazi ko kubungabunga kakwegewe",
    "myTasks.createWorkOrderButton": "Kurema ibwiriza ry'akazi",
    "myTasks.categoryOptional": "Icyiciro (si ngombwa)",
    "reports.typeIncident": "Impanuka",
    "reports.statusNew": "Bishya",
    "reports.statusRead": "Byasomwe",
    "reports.statusResolved": "Byakemuwe",
    "reports.statusDismissed": "Byanzwe",
    "reports.statPending": "Bitegereje",
    "reports.managersReply": "Igisubizo cy'umuyobozi",
    "reports.guestBookingFallback": "Kwiyandikisha k'umushyitsi",
    "reports.facilityReportFallback": "Raporo y'ikigo",
    "reports.noEmailFallback": "Nta imeli",
    "reports.facilityReportsSubtitle": "{facility} — raporo z'abakozi",
    "reports.priorityUrgentEmoji": "🚨 Byihutirwa",
    "reports.relatedSpaceBooking": "Kwiyandikisha ku mwanya bijyanye",
    "assets.fieldAssetTag": "Itagi ry'Ikikoresho (ntibikenewe)",
    "assets.assetTagPlaceholder": "Siga ubusa kugira ngo ikorwe yonyine",
    "assets.fieldSpace": "Icyumba / Aho giherereye",
    "assets.selectSpace": "Nta cyumba kihariye",
    "assets.selectBuildingFirst": "Banza uhitemo inyubako",
    "assets.spaceHint": "Aho ikikoresho kibitswe: ibiro, ububiko, parikingi... Ongeramo ibyumba muri Spaces.",
    "assets.importButton": "Kwinjiza",
    "assets.importTitle": "Kwinjiza ibikoresho",
    "assets.importHelp": "Shyiramo dosiye ya CSV cyangwa Excel. Inkingi ya name gusa ni yo ngombwa; siga asset_tag ubusa maze amatagi akorwe yonyine. Inyubako, ibyumba n'ibyiciro bihuzwa hakoreshejwe amazina, kandi ibyiciro bishya birakorwa.",
    "assets.importTemplate": "Kuramo icyitegererezo",
    "assets.importStart": "Tangira kwinjiza",
    "assets.importing": "Kwinjiza…",
    "assets.importDone": "Ibikoresho {n} byinjijwe.",
    "assets.importTagsGenerated": "Amatagi {n} yakozwe yonyine.",
    "assets.importRow": "Umurongo",
    "assets.importMoreErrors": "…n'indi mirongo {n} ifite ibibazo",
    "assets.importNoFile": "Banza uhitemo dosiye.",
    "assets.importFailed": "Kwinjiza byanze.",
    "publicLink.title": "Urupapuro rwawe rusange",
    "publicLink.description": "Sangiza abantu ayo masano kugira ngo babone inyubako zawe, bakodeshe ibyumba kandi batange raporo y'ibibazo. Inyubako yawe igaragara iyo ifite nibura icyumba kimwe gifite imiterere ya Available.",
    "publicLink.home": "Urupapuro rusange rwo gukodesha",
    "publicLink.report": "Urupapuro rwo gutanga raporo",
    "publicLink.copy": "Gukopera ihuza",
    "publicLink.copied": "Byakopowe",
    "publicLink.open": "Gufungura",
    "publicLink.noAvailable": "Nta cyumba kiragira imiterere ya Available, bityo abashyitsi babona urupapuro rutarimo ikintu. Ongeramo icyumba (cyangwa ushyire kimwe kuri Available) hasi.",
    "publicLink.loggedInNote": "Mu gihe winjiye muri sisitemu, fungura iryo huza mu idirishya ry'ibanga cyangwa muri indi mushakashatsi kugira ngo ubone ibyo abashyitsi babona.",
    "settings.smtpTrust": "Ijambo ry'ibanga ry'imeli yawe rigumya kuri iyi seriveri, rihishwa rimaze kubikwa, kandi rikoreshwa gusa mu guhuza n'utanga imeli yawe igihe sisitemu yohereje imeli. Koresha 'app password' cyangwa aderesi yihariye yo kohereza, ntukoreshe ijambo ry'ibanga ryawe bwite.",
  }
};

const DEFAULT_LANGUAGE = 'en';

const kinyarwandaUiText = {
  'Add': 'Ongeraho',
  'Add Asset': 'Ongeraho umutungo',
  'Add New': 'Ongeraho',
  'All Categories': 'Ibyiciro byose',
  'All Priority': 'Ibyihutirwa byose',
  'All Status': 'Imiterere yose',
  'Assets': 'Umutungo',
  'Available': 'Birahari',
  'Bookings': 'Kwamamaza',
  'Building': 'Inyubako',
  'Cancel': 'Hagarika',
  'Category': 'Icyiciro',
  'Change Password': 'Hindura ijambo banga',
  'Close': 'Funga',
  'Completed': 'Byarangiye',
  'Condition': 'Imiterere',
  'Confirm': 'Emeza',
  'Create': 'Kora',
  'Dashboard': 'Imbonerahamwe',
  'Date': 'Itariki',
  'Delete': 'Siba',
  'Description': 'Ibisobanuro',
  'Download': 'Kuramo',
  'Due Date': 'Itariki ntarengwa',
  'Edit': 'Hindura',
  'Email': 'Imeli',
  'Email Address': 'Aderesi ya imeli',
  'Error': 'Ikosa',
  'Facilities': 'Ibikorwaremezo',
  'Filter': 'Shungura',
  'Loading...': 'Birimo gutegurwa...',
  'Location': 'Aho biherereye',
  'Low': 'Hasi',
  'Maintenance': 'Gusana no kwita ku bikorwaremezo',
  'Manage': 'Gucunga',
  'Messages': 'Ubutumwa',
  'Name': 'Izina',
  'New Password': 'Ijambo banga rishya',
  'No data available': 'Nta makuru ahari',
  'Notes': 'Ibisobanuro byiyongereye',
  'Open': 'Birakinguye',
  'Password': 'Ijambo banga',
  'Priority': 'Ibyihutirwa',
  'Refresh': 'Ongera utangire',
  'Reports': 'Raporo',
  'Retry': 'Ongera ugerageze',
  'Save': 'Bika',
  'Search': 'Shakisha',
  'Select': 'Hitamo',
  'Settings': 'Igenamiterere',
  'Space': 'Umwanya',
  'Spaces': 'Imyanya',
  'Status': 'Imiterere',
  'Submit': 'Ohereza',
  'Success': 'Byagenze neza',
  'Title': 'Umutwe',
  'Type': 'Ubwoko',
  'Update': 'Hindura',
  'Upload': 'Ohereza dosiye',
  'Users': 'Abakoresha',
  'View': 'Reba',
  'Work Orders': 'Amabwiriza y\'akazi'
};

const frenchUiText = {
  'Add': 'Ajouter',
  'Add Asset': 'Ajouter un actif',
  'Add New': 'Ajouter',
  'All Categories': 'Toutes les catégories',
  'All Priority': 'Toutes les priorités',
  'All Status': 'Tous les statuts',
  'Assets': 'Actifs',
  'Available': 'Disponible',
  'Bookings': 'Réservations',
  'Building': 'Bâtiment',
  'Cancel': 'Annuler',
  'Category': 'Catégorie',
  'Change Password': 'Changer le mot de passe',
  'Close': 'Fermer',
  'Completed': 'Terminé',
  'Condition': 'État',
  'Confirm': 'Confirmer',
  'Create': 'Créer',
  'Dashboard': 'Tableau de bord',
  'Date': 'Date',
  'Delete': 'Supprimer',
  'Description': 'Description',
  'Download': 'Télécharger',
  'Due Date': 'Date d\'échéance',
  'Edit': 'Modifier',
  'Email': 'E-mail',
  'Email Address': 'Adresse e-mail',
  'Error': 'Erreur',
  'Facilities': 'Installations',
  'Filter': 'Filtrer',
  'Loading...': 'Chargement...',
  'Location': 'Emplacement',
  'Low': 'Faible',
  'Maintenance': 'Maintenance',
  'Manage': 'Gérer',
  'Messages': 'Messages',
  'Name': 'Nom',
  'New Password': 'Nouveau mot de passe',
  'No data available': 'Aucune donnée disponible',
  'Notes': 'Notes',
  'Open': 'Ouvert',
  'Password': 'Mot de passe',
  'Priority': 'Priorité',
  'Refresh': 'Actualiser',
  'Reports': 'Rapports',
  'Retry': 'Réessayer',
  'Save': 'Enregistrer',
  'Search': 'Rechercher',
  'Select': 'Sélectionner',
  'Settings': 'Paramètres',
  'Space': 'Espace',
  'Spaces': 'Espaces',
  'Status': 'Statut',
  'Submit': 'Envoyer',
  'Success': 'Réussi',
  'Title': 'Titre',
  'Type': 'Type',
  'Update': 'Mettre à jour',
  'Upload': 'Téléverser',
  'Users': 'Utilisateurs',
  'View': 'Voir',
  'Work Orders': 'Ordres de travail'
};

Object.assign(kinyarwandaUiText, {
  'Actions': 'Ibikorwa', 'Active': 'Birakora', 'Add Facility': 'Ongeraho ikikorwaremezo', 'Add Space': 'Ongeraho umwanya',
  'Add New Asset': 'Ongeraho umutungo mushya', 'Add Work Order': 'Ongeraho ibwiriza ry’akazi', 'Assigned To': 'Byahawe',
  'Asset Management': 'Imicungire y’umutungo', 'Available Spaces': 'Imyanya ihari', 'Back to login': 'Subira kwinjira',
  'Booking Details': 'Amakuru yo kwiyandikisha', 'Building': 'Inyubako', 'Category': 'Icyiciro', 'Current Value': 'Agaciro k’ubu',
  'Delete this asset?': 'Gusiba uyu mutungo?', 'Edit Asset': 'Hindura umutungo', 'Edit Space': 'Hindura umwanya',
  'Edit Work Order': 'Hindura ibwiriza ry’akazi', 'Event Title': 'Umutwe w’igikorwa', 'Floor': 'Etaje',
  'Manage service requests and tasks': 'Cunga ibyifuzo bya serivisi n’inshingano', 'No assets found': 'Nta mutungo wabonetse',
  'No work orders found': 'Nta mabwiriza y’akazi yabonetse', 'Order Type': 'Ubwoko bw’icyifuzo', 'Payment method': 'Uburyo bwo kwishyura',
  'Purchase Date': 'Itariki yaguriweho', 'Purpose': 'Impamvu', 'Room Number': 'Nimero y’icyumba',
  'Search assets...': 'Shakisha umutungo...', 'Search spaces...': 'Shakisha imyanya...', 'Search work orders...': 'Shakisha amabwiriza y’akazi...',
  'Select building': 'Hitamo inyubako', 'Select category': 'Hitamo icyiciro', 'Space Management': 'Imicungire y’imyanya',
  'Total Assets': 'Umutungo wose', 'Total Spaces': 'Imyanya yose', 'Total Value': 'Agaciro kose',
  'Work Order': 'Ibwiriza ry’akazi', 'Write to everyone...': 'Andikira buri wese...', 'Subject (optional)': 'Umutwe (si ngombwa)',
  'Write a private message...': 'Andika ubutumwa bwihariye...', 'Public Board': 'Ubutumwa rusange', 'Private Messages': 'Ubutumwa bwihariye',
  'Post an announcement': 'Ohereza itangazo', 'Post publicly': 'Ohereza ku bose', 'Send privately': 'Ohereza mu ibanga',
  'Conversations': 'Ibiganiro', 'No conversations yet.': 'Nta biganiro birahari.', 'No public announcements yet.': 'Nta matangazo rusange arahri.',
  'Choose a user to message': 'Hitamo ukoresha wo koherereza ubutumwa', 'Attach document, photo, or video': 'Ongeraho inyandiko, ifoto cyangwa videwo',
  'Preview': 'Reba mbere', 'Open in system': 'Fungura muri sisitemu', 'All Buildings': 'Inyubako zose',
  'All Categories': 'Ibyiciro byose', 'All Priority': 'Ibyihutirwa byose', 'All Status': 'Imiterere yose'
});

Object.assign(frenchUiText, {
  'Actions': 'Actions', 'Active': 'Actif', 'Add Facility': 'Ajouter une installation', 'Add Space': 'Ajouter un espace',
  'Add New Asset': 'Ajouter un nouvel actif', 'Add Work Order': 'Ajouter un ordre de travail', 'Assigned To': 'Attribué à',
  'Asset Management': 'Gestion des actifs', 'Available Spaces': 'Espaces disponibles', 'Back to login': 'Retour à la connexion',
  'Booking Details': 'Détails de la réservation', 'Building': 'Bâtiment', 'Category': 'Catégorie', 'Current Value': 'Valeur actuelle',
  'Delete this asset?': 'Supprimer cet actif ?', 'Edit Asset': 'Modifier l’actif', 'Edit Space': 'Modifier l’espace',
  'Edit Work Order': 'Modifier l’ordre de travail', 'Event Title': 'Titre de l’événement', 'Floor': 'Étage',
  'Manage service requests and tasks': 'Gérez les demandes de service et les tâches', 'No assets found': 'Aucun actif trouvé',
  'No work orders found': 'Aucun ordre de travail trouvé', 'Order Type': 'Type de demande', 'Payment method': 'Mode de paiement',
  'Purchase Date': 'Date d’achat', 'Purpose': 'Objet', 'Room Number': 'Numéro de salle',
  'Search assets...': 'Rechercher des actifs...', 'Search spaces...': 'Rechercher des espaces...', 'Search work orders...': 'Rechercher des ordres de travail...',
  'Select building': 'Sélectionner un bâtiment', 'Select category': 'Sélectionner une catégorie', 'Space Management': 'Gestion des espaces',
  'Total Assets': 'Total des actifs', 'Total Spaces': 'Total des espaces', 'Total Value': 'Valeur totale',
  'Work Order': 'Ordre de travail', 'Write to everyone...': 'Écrire à tout le monde...', 'Subject (optional)': 'Objet (facultatif)',
  'Write a private message...': 'Écrire un message privé...', 'Public Board': 'Tableau public', 'Private Messages': 'Messages privés',
  'Post an announcement': 'Publier une annonce', 'Post publicly': 'Publier publiquement', 'Send privately': 'Envoyer en privé',
  'Conversations': 'Conversations', 'No conversations yet.': 'Aucune conversation.', 'No public announcements yet.': 'Aucune annonce publique.',
  'Choose a user to message': 'Choisir un utilisateur', 'Attach document, photo, or video': 'Joindre un document, une photo ou une vidéo',
  'Preview': 'Aperçu', 'Open in system': 'Ouvrir dans le système', 'All Buildings': 'Tous les bâtiments',
  'All Categories': 'Toutes les catégories', 'All Priority': 'Toutes les priorités', 'All Status': 'Tous les statuts'
});


// --- Additional strings covering the rest of the app (auto-expanded coverage) ---
Object.assign(kinyarwandaUiText, {
  '(required)': '(bikenewe)',
  ', employee:': ', umukozi:',
  ', manager:': ', umuyobozi:',
  '1 month': 'Ukwezi 1',
  '12 months': 'Amezi 12',
  '3 months': 'Amezi 3',
  '6 months': 'Amezi 6',
  'About Us': 'Abo turi bo',
  'Add Item': 'Ongeraho ikintu',
  'Add a comment...': 'Ongeraho igitekerezo...',
  'Additional Notes': 'Ibisobanuro by\'inyongera',
  'Adjust your search or filters.': 'Hindura ishakisha cyangwa muyungurura.',
  'Admin': 'Umuyobozi mukuru',
  'Admin-only — configure system-wide preferences': 'Ab\'abayobozi bonyine — Shyiraho amahitamo ya sisitemu yose',
  'Affected Space': 'Umwanya ugizweho ingaruka',
  'All Facilities': 'Ibikorwaremezo byose',
  'All Types': 'Ubwoko bwose',
  'All facilities': 'Ibikorwaremezo byose',
  'All new users receive a temporary password via email and are forced to change it on first login.': 'Abakoresha bashya bose bahabwa ijambo banga ry\'igihe gito binyuze kuri imeli kandi bagomba kurihindura ubwo binjira bwa mbere.',
  'All prices shown in Rwandan Francs (RWF)': 'Ibiciro byose byerekanwa mu mafaranga y\'u Rwanda (RWF)',
  'Amenities': 'Ibikoresho biboneka',
  'Amount Due on Arrival': 'Amafaranga yishyurwa mu kugera',
  'Amount received': 'Amafaranga yakiriwe',
  'App password or SMTP password': 'Ijambo banga rya porogaramu cyangwa irya SMTP',
  'Approve & create vendor link': 'Emeza kandi ukore umuyoboro w\'umucuruzi',
  'Approve selected quotation': 'Emeza igiciro cyatoranyijwe',
  'Area (m²)': 'Ubuso (m²)',
  'Asset': 'Umutungo',
  'Asset Tag *': 'Ikimenyetso cy\'umutungo *',
  'Asset or material needed': 'Umutungo cyangwa ibikoresho bikenewe',
  'Assign to Facility': 'Shyira ku ikorwaremezo',
  'Assigned:': 'Byahawe:',
  'Attendees': 'Abitabiriye',
  'Bank transfer': 'Kohereza amafaranga muri banki',
  'Book': 'Iyandikishe',
  'Book Your Space Today': 'Iyandikishe umwanya wawe uyu munsi',
  'Book a Space': 'Iyandikishe umwanya',
  'Booked a space? Use the same email from your booking to report an issue, request help, or send a suggestion.': 'Waba warahaye umwanya? Koresha imeli imwe wakoresheje mu kwiyandikisha kugira ngo utange ikibazo, usabe ubufasha, cyangwa wohereze igitekerezo.',
  'Booking Email *': 'Imeli yo kwiyandikisha *',
  'Booking Reference': 'Nimero y\'iyandikisha',
  'Booking Request Details': 'Amakuru y\'icyifuzo cyo kwiyandikisha',
  'Booking and Location': 'Iyandikisha n\'aho biherereye',
  'Brief description of the issue': 'Incamake y\'ikibazo',
  'Building *': 'Inyubako *',
  'Building:': 'Inyubako:',
  'Capacity': 'Ubushobozi',
  'Card': 'Ikarita',
  'Cash on approval': 'Amafaranga y\'ibiganza igihe byemejwe',
  'Choose a facility and space above to view details and start a booking.': 'Hitamo ikorwaremezo n\'umwanya hejuru kugira ngo urebe amakuru kandi utangire kwiyandikisha.',
  'Choose the renewal start and end times.': 'Hitamo igihe cy\'itangira n\'irangiza ry\'ivugurura.',
  'Code': 'Kode',
  'Company': 'Ikigo',
  'Complaint': 'Ikirego',
  'Confirm Booking': 'Emeza iyandikisha',
  'Contact': 'Aho twavugana',
  'Contact Email': 'Imeli yo kuvugana',
  'Contact Us': 'Twandikire',
  'Continue': 'Komeza',
  'Copy vendor link': 'Koporora umuyoboro w\'umucuruzi',
  'Corrective': 'Igikosora',
  'Cost': 'Igiciro',
  'Cost (RWF)': 'Igiciro (RWF)',
  'Creating...': 'Birimo kurema...',
  'Current Qty': 'Umubare uriho',
  'Current Value (RWF)': 'Agaciro k\'ubu (RWF)',
  'Data Export & Import': 'Kohereza no kwinjiza amakuru',
  'Deactivate this facility?': 'Kuzimya iki kikorwaremezo?',
  'Decline': 'Kwanga',
  'Delete booking': 'Siba iyandikisha',
  'Delete this announcement?': 'Siba iri tangazo?',
  'Delete this message?': 'Siba ubu butumwa?',
  'Department': 'Ishami',
  'Describe the issue — location, what happened, when it started...': 'Sobanura ikibazo — aho kibereye, ibyabaye, n\'igihe cyatangiriye...',
  'Describe the issue, request, or suggestion...': 'Sobanura ikibazo, icyifuzo, cyangwa igitekerezo...',
  'Description *': 'Ibisobanuro *',
  'Description:': 'Ibisobanuro:',
  'Detail': 'Birambuye',
  'Details *': 'Ibisobanuro birambuye *',
  'Dismiss install prompt': 'Ivanaho ubutumire bwo kwinjiza porogaramu',
  'Done': 'Byarangiye',
  'Download PDF': 'Kuramo PDF',
  'Due Date:': 'Itariki ntarengwa:',
  'Due This Month': 'Bikwiye uku kwezi',
  'Email *': 'Imeli *',
  'Email / SMTP Configuration': 'Igenamiterere rya Imeli / SMTP',
  'Email Address *': 'Aderesi ya imeli *',
  'Email affected confirmed bookings and rentals before this action': 'Ohereza imeli ku biyandikisha n\'ubukode byemejwe bigizweho ingaruka mbere y\'iki gikorwa',
  'End *': 'Irangiza *',
  'End Date & Time *': 'Itariki n\'isaha y\'irangiza *',
  'Ending Date': 'Itariki y\'irangiza',
  'Enter a termination reason.': 'Andika impamvu yo guhagarika.',
  'Enter organisation name or Personal': 'Andika izina ry\'ikigo cyangwa \'Umuntu ku giti cye\'',
  'Estimated Hours': 'Amasaha ateganyijwe',
  'Everything You Need': 'Ibyo ukeneye byose',
  'Explain what work will take place and how the space may be affected': 'Sobanura akazi kazakorwa n\'uko umwanya ushobora kugirwaho ingaruka',
  'Export CSV': 'Kohereza CSV',
  'FACILITY MANAGEMENT': 'IMICUNGIRE Y\'IBIKORWAREMEZO',
  'Facility': 'Ikorwaremezo',
  'Facility Issue': 'Ikibazo cy\'ikorwaremezo',
  'Facility Manager *': 'Umuyobozi w\'ikorwaremezo *',
  'Facility Name *': 'Izina ry\'ikorwaremezo *',
  'Findings': 'Ibyabonetse',
  'Findings / Observations': 'Ibyabonetse / Ibyitegerejwe',
  'Follow us': 'Dukurikire',
  'Frequency': 'Inshuro',
  'From': 'Kuva',
  'From Address': 'Aderesi yohereza',
  'Full Name': 'Amazina yombi',
  'Full Name *': 'Amazina yombi *',
  'Get In Touch': 'Tuvugishe',
  'Guests': 'Abashyitsi',
  'Hold': 'Guhagarara',
  'How can we help?': 'Twagufasha dute?',
  'I agree to the booking terms: the information provided is accurate, payment is due according to the selected method, and cancellation or termination may be subject to facility policy.': 'Nemeye amabwiriza yo kwiyandikisha: amakuru natanze ni ukuri, kwishyura bikorwa hakurikijwe uburyo bwatoranyijwe, kandi guhagarika cyangwa gukuraho bishobora gukurikiza amabwiriza y\'ikorwaremezo.',
  'Import CSV': 'Kwinjiza CSV',
  'In Stock': 'Birahari mu bubiko',
  'In progress': 'Birimo gukorwa',
  'Inactive': 'Ntibikora',
  'Inspection': 'Igenzura',
  'Install': 'Shyiramo',
  'Install MITI FMS': 'Shyiramo MITI FMS',
  'Internal': 'Imbere mu kigo',
  'Inventory Management': 'Imicungire y\'ububiko',
  'Item Code *': 'Kode y\'ikintu *',
  'Job Title': 'Umwanya w\'akazi',
  'Joined': 'Yinjiye',
  'Last Performed': 'Igihe cyanyuma cyakorewe',
  'Location / Address': 'Aho biherereye / Aderesi',
  'Log Maintenance Work': 'Andika akazi ko gusana',
  'Log Work': 'Andika akazi',
  'Log maintenance': 'Andika igikorwa cyo gusana',
  'Login': 'Kwinjira',
  'MITI FACILITY MANAGEMENT': 'MITI IMICUNGIRE Y\'IBIKORWAREMEZO',
  'MITI FM logo': 'Ikirango cya MITI FM',
  'MITI FMS': 'MITI FMS',
  'Maintained Space': 'Umwanya usanwa',
  'Maintenance Action': 'Igikorwa cyo gusana',
  'Maintenance Management': 'Imicungire yo gusana',
  'Maintenance Request': 'Icyifuzo cyo gusana',
  'Manager': 'Umuyobozi',
  'Manager will confirm your booking at': 'Umuyobozi azemeza iyandikisha ryawe kuri',
  'Manager:': 'Umuyobozi:',
  'Managing Facilities Smarter': 'Gucunga ibikorwaremezo mu buryo bwiza',
  'Manufacturer': 'Uwakoze',
  'Mark Complete': 'Emeza ko byarangiye',
  'Meeting Title': 'Umutwe w\'inama',
  'Message *': 'Ubutumwa *',
  'Message sent! We will get back to you soon.': 'Ubutumwa bwoherejwe! Tuzagusubiza vuba.',
  'Min': 'Ntoya',
  'Minimum Stock': 'Ubwinshi buke bwemewe',
  'Mobile Money': 'Mobile Money',
  'Model': 'Ubwoko',
  'My Organization': 'Ikigo cyanjye',
  'Name *': 'Izina *',
  'New Report': 'Raporo nshya',
  'New Schedule': 'Gahunda nshya',
  'New system name': 'Izina rishya rya sisitemu',
  'Next Service Due': 'Serivisi ikurikira igomba gukorwa',
  'No activity yet.': 'Nta gikorwa kirabaho.',
  'No asset': 'Nta mutungo',
  'No asset requests for this work order.': 'Nta cyifuzo cy\'umutungo kuri iri bwiriza ry\'akazi.',
  'No assignment': 'Nta gikorwa yahawe',
  'No eligible registered bookings found.': 'Nta biyandikisha byemewe byabonetse.',
  'No facility': 'Nta kikorwaremezo',
  'No inventory items': 'Nta bikoresho mu bubiko',
  'No maintenance logs yet': 'Nta makuru yo gusana arahari',
  'No schedules yet': 'Nta gahunda irahari',
  'No spaces found': 'Nta mwanya wabonetse',
  'No specific asset': 'Nta mutungo runaka',
  'No specific space': 'Nta mwanya runaka',
  'No updates yet.': 'Nta bivuguruwe birahari.',
  'None': 'Nta na kimwe',
  'Notification Logo': 'Ikirango cy\'imenyesha',
  'Number of Floors (optional)': 'Umubare w\'amagorofa (si ngombwa)',
  'Occupancy Rate': 'Igipimo cyo kuba mu mwanya',
  'Occupied': 'Birimo abantu',
  'On hold': 'Byahagaze',
  'Only developers can register additional system instances. Tenant data isolation must be enabled before production use.': 'Abakora sisitemu gusa ni bo bashobora kwandikisha izindi nyandiko za sisitemu. Igabanywa ry\'amakuru y\'abakiriya rigomba gushyirwaho mbere yo gukoreshwa mu buryo bwukuri.',
  'Only managers and admins can be assigned as facility managers.': 'Ni abayobozi n\'abayobozi bakuru gusa bashobora guhabwa umurimo wo kuyobora ikorwaremezo.',
  'Order Type *': 'Ubwoko bw\'icyifuzo *',
  'Organisation / Personal': 'Ikigo / Umuntu ku giti cye',
  'Organization Name': 'Izina ry\'ikigo',
  'Other': 'Ikindi',
  'Other Request': 'Ikindi cyifuzo',
  'Our Services': 'Serivisi zacu',
  'Out of Stock': 'Byashize mu bubiko',
  'Parts Used': 'Ibice byakoreshejwe',
  'Payment': 'Kwishyura',
  'Payment due on arrival · Currency: RWF (Rwandan Franc)': 'Kwishyura bikorwa mu kugera · Ifaranga: RWF (Ifaranga ry\'u Rwanda)',
  'Payment method *': 'Uburyo bwo kwishyura *',
  'Performed By': 'Byakozwe na',
  'Permanently delete this space? Its bookings will also be deleted. This cannot be undone.': 'Gusiba burundu uyu mwanya? N\'ibyandikishijwe byawo nabyo bizasibwa. Ibi ntibishobora gusubizwaho.',
  'Permanently delete this user? This cannot be undone.': 'Gusiba burundu uyu mukoresha? Ibi ntibishobora gusubizwaho.',
  'Phone': 'Telefoni',
  'Please select an image file.': 'Hitamo dosiye y\'ifoto.',
  'Please select an image smaller than 5 MB.': 'Hitamo ifoto iri munsi ya MB 5.',
  'Port': 'Port',
  'Post': 'Ohereza',
  'Preview the overlap before saving. Only confirmed bookings and active rentals receive email notices.': 'Reba aho bihuriye mbere yo kubika. Ni ibyandikishijwe byemejwe n\'ubukode bukora gusa bibona imenyesha rya imeli.',
  'Price per day (RWF)': 'Igiciro ku munsi (RWF)',
  'Price per hour (RWF)': 'Igiciro ku isaha (RWF)',
  'Price per month (RWF)': 'Igiciro ku kwezi (RWF)',
  'Price unit *': 'Igipimo cy\'igiciro *',
  'Priority:': 'Ibyihutirwa:',
  'Privacy Policy': 'Politiki y\'ibanga',
  'Public': 'Rusange',
  'Purchase Cost (RWF)': 'Igiciro cyo kugura (RWF)',
  'Put on Hold': 'Hagarika by\'agateganyo',
  'Quantity *': 'Umubare *',
  'Quick Links': 'Imiyoboro yihuse',
  'Quotation:': 'Igiciro:',
  'Reason for termination': 'Impamvu yo guhagarika',
  'Recording...': 'Birimo kwandikwa...',
  'Refresh messages': 'Ongera utangire ubutumwa',
  'Reject': 'Kwanga',
  'Reject selected quotation': 'Anga igiciro cyatoranyijwe',
  'Renew booking': 'Vugurura iyandikisha',
  'Rental period *': 'Igihe cy\'ubukode *',
  'Reply': 'Subiza',
  'Reply to Employee': 'Subiza umukozi',
  'Report Type': 'Ubwoko bwa raporo',
  'Report a Facility Concern': 'Tanga ikibazo ku ikorwaremezo',
  'Request an asset': 'Saba umutungo',
  'Request renewal': 'Saba kuvugurura',
  'Resume': 'Komeza',
  'Resume Work': 'Komeza akazi',
  'Review': 'Suzuma',
  'Role': 'Uruhare',
  'Role-based access control': 'Igenzura ry\'uburenganzira hashingiwe ku ruhare',
  'Room allocation, occupancy & bookings': 'Igabana ry\'ibyumba, kuba muri byo n\'ibiyandikisha',
  'SMTP Host': 'Seriveri ya SMTP',
  'SMTP Password': 'Ijambo banga rya SMTP',
  'SMTP Username': 'Izina ry\'ukoresha rya SMTP',
  'Safety Issue': 'Ikibazo cy\'umutekano',
  'Save & Reply': 'Bika hanyuma usubize',
  'Save Settings': 'Bika igenamiterere',
  'Save payment': 'Bika kwishyura',
  'Schedule (if applicable)': 'Gahunda (niba bikwiye)',
  'Search by name, tag, serial...': 'Shakisha ukurikije izina, ikimenyetso, cyangwa nomero...',
  'Search items...': 'Shakisha ibintu...',
  'Security': 'Umutekano',
  'Select a manager': 'Hitamo umuyobozi',
  'Select a registered booking': 'Hitamo icyandikishijwe',
  'Select a space': 'Hitamo umwanya',
  'Select a vendor quotation before approval.': 'Hitamo igiciro cy\'umucuruzi mbere yo kwemeza.',
  'Select asset': 'Hitamo umutungo',
  'Send Message': 'Ohereza ubutumwa',
  'Send Report': 'Ohereza raporo',
  'Send test to...': 'Ohereza ikizamini kuri...',
  'Send us a Message': 'Twoherereze ubutumwa',
  'Serial Number': 'Nomero yihariye',
  'Services': 'Serivisi',
  'Short booking': 'Iyandikisha rigufi',
  'Source': 'Inkomoko',
  'Space / Facility': 'Umwanya / Ikorwaremezo',
  'Space Name *': 'Izina ry\'umwanya *',
  'Space rental': 'Ubukode bw\'umwanya',
  'Staff': 'Abakozi',
  'Start': 'Itangira',
  'Start *': 'Itangira *',
  'Start Date & Time *': 'Itariki n\'isaha y\'itangira *',
  'Start Work': 'Tangira akazi',
  'Starting Date': 'Itariki y\'itangira',
  'Starting Date *': 'Itariki y\'itangira *',
  'Status:': 'Imiterere:',
  'Stay connected with your facility team': 'Komeza uvugane n\'itsinda ry\'ikorwaremezo ryawe',
  'Stock': 'Ububiko',
  'Stock In': 'Kwinjiza mu bubiko',
  'Stock Out': 'Gusohora mu bubiko',
  'Storage Location': 'Aho babika',
  'Subject': 'Umutwe',
  'Subject *': 'Umutwe *',
  'Submit Booking Request': 'Ohereza icyifuzo cyo kwiyandikisha',
  'Submit Report': 'Ohereza raporo',
  'Submit a Report': 'Ohereza raporo',
  'Submit and track your reports to the facility manager': 'Ohereza kandi ukurikirane raporo zawe ku muyobozi w\'ikorwaremezo',
  'Submitted': 'Byoherejwe',
  'Suggestion': 'Igitekerezo',
  'System Name': 'Izina rya sisitemu',
  'System Settings': 'Igenamiterere rya sisitemu',
  'System instances': 'Kopi za sisitemu',
  'Tag': 'Ikimenyetso',
  'Technician': 'Umuhanga mu bikorwa',
  'Tell us about your needs...': 'Tubwire ibyo ukeneye...',
  'Terminate': 'Hagarika',
  'Terminate booking': 'Hagarika iyandikisha',
  'Terminate this booking? This action cannot be undone.': 'Hagarika iri yandikisha? Ibi ntibishobora gusubizwaho.',
  'Termination reason': 'Impamvu yo guhagarika',
  'Terms of Service': 'Amabwiriza yo gukoresha',
  'Test Email': 'Imeli y\'ikizamini',
  'This document will open in a separate browser tab.': 'Iyi nyandiko izafungura mu ndanganturo indi.',
  'Title *': 'Umutwe *',
  'To': 'Kuri',
  'Total Area (m²)': 'Ubuso bwose (m²)',
  'Total Items': 'Ibintu byose',
  'Total Logs': 'Inyandiko zose',
  'Total Schedules': 'Gahunda zose',
  'Transaction Type': 'Ubwoko bw\'igikorwa',
  'Type:': 'Ubwoko:',
  'Unassigned': 'Ntacyo yahawe',
  'Unit': 'Igipimo',
  'Unit Cost': 'Igiciro ku kimwe',
  'Unit Cost (RWF)': 'Igiciro ku kimwe (RWF)',
  'Update Status': 'Vugurura imiterere',
  'Urgent': 'Byihutirwa',
  'User Created': 'Ukoresha yaremwe',
  'Vendor:': 'Umucuruzi:',
  'View detail': 'Reba birambuye',
  'View details': 'Reba ibisobanuro',
  'Warranty Expiry': 'Igihe ubwishingizi burangira',
  'What changed? What was done?': 'Ni iki cyahindutse? Ni iki cyakozwe?',
  'What do you need help with?': 'Ni iki ukeneyeho ubufasha?',
  'What needs attention? *': 'Ni iki gikeneye kwitabwaho? *',
  'What was done...': 'Ibyakozwe...',
  'When': 'Ryari',
  'Why is it needed?': 'Kubera iki bikenewe?',
  'Work Description': 'Ibisobanuro by\'akazi',
  'Your Name *': 'Izina ryawe *',
  'Your response will be visible to the employee...': 'Igisubizo cyawe kizabonwa n\'umukozi...',
  'bytes': 'bayite',
  'e.g. CLN-001': 'urugero: CLN-001',
  'e.g. Filter #3M-500': 'urugero: Filter #3M-500',
  'e.g. HVAC-001': 'urugero: HVAC-001',
  'e.g. Monthly HVAC Filter Replacement': 'urugero: Guhindura filiteri ya HVAC buri kwezi',
  'e.g. Projector, Whiteboard, AC': 'urugero: Projegiteri, Ikibaho cyera, AC',
  'e.g. Storage Room A, Shelf 3': 'urugero: Icyumba cy\'ububiko A, Etajere 3',
  'e.g. Team Meeting, Training Session': 'urugero: Inama y\'itsinda, Amahugurwa',
  'e.g. Team Standup': 'urugero: Inama ngufi y\'itsinda',
  'e.g. pieces, litres': 'urugero: ibice, amalitro',
  'is enforced on all API endpoints:': 'rikurikizwa kuri buri gice cya API:',
  'with login credentials.': 'hamwe n\'amakuru yo kwinjira.',
  '© 2026 MITI FMS. All rights reserved.': '© 2026 MITI FMS. Uburenganzira bwose burarinzwe.',
  '— You can manage spaces in this facility': '— Ushobora gucunga imyanya muri iki kikorwaremezo',
  '— full access: facilities, users, settings, all data': '— uburenganzira bwuzuye: ibikorwaremezo, abakoresha, igenamiterere, amakuru yose',
  '— manage spaces within their assigned facility': '— gucunga imyanya yo mu ikorwaremezo bahawe',
  '— manage work orders and maintenance logs': '— gucunga amabwiriza y\'akazi n\'inyandiko zo gusana',
  '— read access + submit work orders + book spaces': '— uburenganzira bwo gusoma + kohereza amabwiriza y\'akazi + kwiyandikisha imyanya',
  '✅ Settings saved': '✅ Igenamiterere ryabitswe',
  '✓ Replied': '✓ Byasubijwe',
  '✓ Sent': '✓ Byoherejwe',
  '👤 Guest Information': '👤 Amakuru y\'umushyitsi',
  '👤 Your Details': '👤 Amakuru yawe',
  '📅 Booking Details': '📅 Amakuru y\'iyandikisha',
  '📅 Order Details': '📅 Amakuru y\'icyifuzo',
  '📤 Stock Out (Used)': '📤 Gusohora mu bubiko (Byakoreshejwe)',
  '📥 Stock In (Received)': '📥 Kwinjiza mu bubiko (Byakiriwe)',
  '🔄 Adjust (Set exact qty)': '🔄 Hindura (Shyiraho umubare nyawo)',
  '🕐 Mon–Fri: 8am – 6pm': '🕐 Ku wa mbere - Ku wa gatanu: 8h00 – 18h00',
  '🚨 Urgent': '🚨 Byihutirwa',
});

Object.assign(frenchUiText, {
  '(required)': '(obligatoire)',
  ', employee:': ', employé :',
  ', manager:': ', gestionnaire :',
  '1 month': '1 mois',
  '12 months': '12 mois',
  '3 months': '3 mois',
  '6 months': '6 mois',
  'About Us': 'À propos de nous',
  'Add Item': 'Ajouter un article',
  'Add a comment...': 'Ajouter un commentaire...',
  'Additional Notes': 'Notes supplémentaires',
  'Adjust your search or filters.': 'Ajustez votre recherche ou vos filtres.',
  'Admin': 'Administrateur',
  'Admin-only — configure system-wide preferences': 'Réservé à l\'administrateur — configurez les préférences globales du système',
  'Affected Space': 'Espace concerné',
  'All Facilities': 'Toutes les installations',
  'All Types': 'Tous les types',
  'All facilities': 'Toutes les installations',
  'All new users receive a temporary password via email and are forced to change it on first login.': 'Tous les nouveaux utilisateurs reçoivent un mot de passe temporaire par e-mail et doivent le changer lors de leur première connexion.',
  'All prices shown in Rwandan Francs (RWF)': 'Tous les prix sont affichés en Francs Rwandais (RWF)',
  'Amenities': 'Commodités',
  'Amount Due on Arrival': 'Montant dû à l\'arrivée',
  'Amount received': 'Montant reçu',
  'App password or SMTP password': 'Mot de passe d\'application ou mot de passe SMTP',
  'Approve & create vendor link': 'Approuver et créer le lien fournisseur',
  'Approve selected quotation': 'Approuver le devis sélectionné',
  'Area (m²)': 'Superficie (m²)',
  'Asset': 'Actif',
  'Asset Tag *': 'Étiquette de l\'actif *',
  'Asset or material needed': 'Actif ou matériel nécessaire',
  'Assign to Facility': 'Attribuer à une installation',
  'Assigned:': 'Attribué :',
  'Attendees': 'Participants',
  'Bank transfer': 'Virement bancaire',
  'Book': 'Réserver',
  'Book Your Space Today': 'Réservez votre espace aujourd\'hui',
  'Book a Space': 'Réserver un espace',
  'Booked a space? Use the same email from your booking to report an issue, request help, or send a suggestion.': 'Vous avez réservé un espace ? Utilisez le même e-mail que lors de votre réservation pour signaler un problème, demander de l\'aide ou envoyer une suggestion.',
  'Booking Email *': 'E-mail de réservation *',
  'Booking Reference': 'Référence de réservation',
  'Booking Request Details': 'Détails de la demande de réservation',
  'Booking and Location': 'Réservation et emplacement',
  'Brief description of the issue': 'Brève description du problème',
  'Building *': 'Bâtiment *',
  'Building:': 'Bâtiment :',
  'Capacity': 'Capacité',
  'Card': 'Carte',
  'Cash on approval': 'Espèces à l\'approbation',
  'Choose a facility and space above to view details and start a booking.': 'Choisissez une installation et un espace ci-dessus pour voir les détails et commencer une réservation.',
  'Choose the renewal start and end times.': 'Choisissez les heures de début et de fin du renouvellement.',
  'Code': 'Code',
  'Company': 'Entreprise',
  'Complaint': 'Plainte',
  'Confirm Booking': 'Confirmer la réservation',
  'Contact': 'Contact',
  'Contact Email': 'E-mail de contact',
  'Contact Us': 'Contactez-nous',
  'Continue': 'Continuer',
  'Copy vendor link': 'Copier le lien du fournisseur',
  'Corrective': 'Corrective',
  'Cost': 'Coût',
  'Cost (RWF)': 'Coût (RWF)',
  'Creating...': 'Création en cours...',
  'Current Qty': 'Quantité actuelle',
  'Current Value (RWF)': 'Valeur actuelle (RWF)',
  'Data Export & Import': 'Exportation et importation de données',
  'Deactivate this facility?': 'Désactiver cette installation ?',
  'Decline': 'Refuser',
  'Delete booking': 'Supprimer la réservation',
  'Delete this announcement?': 'Supprimer cette annonce ?',
  'Delete this message?': 'Supprimer ce message ?',
  'Department': 'Département',
  'Describe the issue — location, what happened, when it started...': 'Décrivez le problème — lieu, ce qui s\'est passé, quand cela a commencé...',
  'Describe the issue, request, or suggestion...': 'Décrivez le problème, la demande ou la suggestion...',
  'Description *': 'Description *',
  'Description:': 'Description :',
  'Detail': 'Détail',
  'Details *': 'Détails *',
  'Dismiss install prompt': 'Ignorer l\'invite d\'installation',
  'Done': 'Terminé',
  'Download PDF': 'Télécharger le PDF',
  'Due Date:': 'Date d\'échéance :',
  'Due This Month': 'À échéance ce mois-ci',
  'Email *': 'E-mail *',
  'Email / SMTP Configuration': 'Configuration E-mail / SMTP',
  'Email Address *': 'Adresse e-mail *',
  'Email affected confirmed bookings and rentals before this action': 'Envoyer un e-mail aux réservations et locations confirmées concernées avant cette action',
  'End *': 'Fin *',
  'End Date & Time *': 'Date et heure de fin *',
  'Ending Date': 'Date de fin',
  'Enter a termination reason.': 'Entrez une raison de résiliation.',
  'Enter organisation name or Personal': 'Entrez le nom de l\'organisation ou Personnel',
  'Estimated Hours': 'Heures estimées',
  'Everything You Need': 'Tout ce dont vous avez besoin',
  'Explain what work will take place and how the space may be affected': 'Expliquez les travaux qui auront lieu et comment l\'espace pourrait être affecté',
  'Export CSV': 'Exporter en CSV',
  'FACILITY MANAGEMENT': 'GESTION DES INSTALLATIONS',
  'Facility': 'Installation',
  'Facility Issue': 'Problème d\'installation',
  'Facility Manager *': 'Responsable de l\'installation *',
  'Facility Name *': 'Nom de l\'installation *',
  'Findings': 'Constatations',
  'Findings / Observations': 'Constatations / Observations',
  'Follow us': 'Suivez-nous',
  'Frequency': 'Fréquence',
  'From': 'De',
  'From Address': 'Adresse d\'expéditeur',
  'Full Name': 'Nom complet',
  'Full Name *': 'Nom complet *',
  'Get In Touch': 'Contactez-nous',
  'Guests': 'Invités',
  'Hold': 'En attente',
  'How can we help?': 'Comment pouvons-nous vous aider ?',
  'I agree to the booking terms: the information provided is accurate, payment is due according to the selected method, and cancellation or termination may be subject to facility policy.': 'J\'accepte les conditions de réservation : les informations fournies sont exactes, le paiement est dû selon le mode sélectionné, et l\'annulation ou la résiliation peut être soumise à la politique de l\'installation.',
  'Import CSV': 'Importer CSV',
  'In Stock': 'En stock',
  'In progress': 'En cours',
  'Inactive': 'Inactif',
  'Inspection': 'Inspection',
  'Install': 'Installer',
  'Install MITI FMS': 'Installer MITI FMS',
  'Internal': 'Interne',
  'Inventory Management': 'Gestion des stocks',
  'Item Code *': 'Code de l\'article *',
  'Job Title': 'Titre du poste',
  'Joined': 'A rejoint',
  'Last Performed': 'Dernière exécution',
  'Location / Address': 'Emplacement / Adresse',
  'Log Maintenance Work': 'Enregistrer les travaux de maintenance',
  'Log Work': 'Enregistrer le travail',
  'Log maintenance': 'Enregistrer la maintenance',
  'Login': 'Connexion',
  'MITI FACILITY MANAGEMENT': 'MITI GESTION DES INSTALLATIONS',
  'MITI FM logo': 'Logo MITI FM',
  'MITI FMS': 'MITI FMS',
  'Maintained Space': 'Espace entretenu',
  'Maintenance Action': 'Action de maintenance',
  'Maintenance Management': 'Gestion de la maintenance',
  'Maintenance Request': 'Demande de maintenance',
  'Manager': 'Gestionnaire',
  'Manager will confirm your booking at': 'Le gestionnaire confirmera votre réservation à',
  'Manager:': 'Gestionnaire :',
  'Managing Facilities Smarter': 'Gérer les installations plus intelligemment',
  'Manufacturer': 'Fabricant',
  'Mark Complete': 'Marquer comme terminé',
  'Meeting Title': 'Titre de la réunion',
  'Message *': 'Message *',
  'Message sent! We will get back to you soon.': 'Message envoyé ! Nous vous répondrons bientôt.',
  'Min': 'Min',
  'Minimum Stock': 'Stock minimum',
  'Mobile Money': 'Mobile Money',
  'Model': 'Modèle',
  'My Organization': 'Mon organisation',
  'Name *': 'Nom *',
  'New Report': 'Nouveau rapport',
  'New Schedule': 'Nouveau calendrier',
  'New system name': 'Nouveau nom du système',
  'Next Service Due': 'Prochain service prévu',
  'No activity yet.': 'Aucune activité pour le moment.',
  'No asset': 'Aucun actif',
  'No asset requests for this work order.': 'Aucune demande d\'actif pour cet ordre de travail.',
  'No assignment': 'Aucune affectation',
  'No eligible registered bookings found.': 'Aucune réservation enregistrée éligible trouvée.',
  'No facility': 'Aucune installation',
  'No inventory items': 'Aucun article en stock',
  'No maintenance logs yet': 'Aucun journal de maintenance pour le moment',
  'No schedules yet': 'Aucun calendrier pour le moment',
  'No spaces found': 'Aucun espace trouvé',
  'No specific asset': 'Aucun actif spécifique',
  'No specific space': 'Aucun espace spécifique',
  'No updates yet.': 'Aucune mise à jour pour le moment.',
  'None': 'Aucun',
  'Notification Logo': 'Logo de notification',
  'Number of Floors (optional)': 'Nombre d\'étages (facultatif)',
  'Occupancy Rate': 'Taux d\'occupation',
  'Occupied': 'Occupé',
  'On hold': 'En attente',
  'Only developers can register additional system instances. Tenant data isolation must be enabled before production use.': 'Seuls les développeurs peuvent enregistrer des instances système supplémentaires. L\'isolation des données des locataires doit être activée avant l\'utilisation en production.',
  'Only managers and admins can be assigned as facility managers.': 'Seuls les gestionnaires et administrateurs peuvent être désignés comme responsables d\'installation.',
  'Order Type *': 'Type de commande *',
  'Organisation / Personal': 'Organisation / Personnel',
  'Organization Name': 'Nom de l\'organisation',
  'Other': 'Autre',
  'Other Request': 'Autre demande',
  'Our Services': 'Nos services',
  'Out of Stock': 'En rupture de stock',
  'Parts Used': 'Pièces utilisées',
  'Payment': 'Paiement',
  'Payment due on arrival · Currency: RWF (Rwandan Franc)': 'Paiement dû à l’arrivée · Devise : RWF (Franc rwandais)',
  'Payment method *': 'Mode de paiement *',
  'Performed By': 'Effectué par',
  'Permanently delete this space? Its bookings will also be deleted. This cannot be undone.': 'Supprimer définitivement cet espace ? Ses réservations seront également supprimées. Cette action est irréversible.',
  'Permanently delete this user? This cannot be undone.': 'Supprimer définitivement cet utilisateur ? Cette action est irréversible.',
  'Phone': 'Téléphone',
  'Please select an image file.': 'Veuillez sélectionner un fichier image.',
  'Please select an image smaller than 5 MB.': 'Veuillez sélectionner une image de moins de 5 Mo.',
  'Port': 'Port',
  'Post': 'Publier',
  'Preview the overlap before saving. Only confirmed bookings and active rentals receive email notices.': 'Prévisualisez le chevauchement avant d\'enregistrer. Seules les réservations confirmées et les locations actives reçoivent des avis par e-mail.',
  'Price per day (RWF)': 'Prix par jour (RWF)',
  'Price per hour (RWF)': 'Prix par heure (RWF)',
  'Price per month (RWF)': 'Prix par mois (RWF)',
  'Price unit *': 'Unité de prix *',
  'Priority:': 'Priorité :',
  'Privacy Policy': 'Politique de confidentialité',
  'Public': 'Public',
  'Purchase Cost (RWF)': 'Coût d\'achat (RWF)',
  'Put on Hold': 'Mettre en attente',
  'Quantity *': 'Quantité *',
  'Quick Links': 'Liens rapides',
  'Quotation:': 'Devis :',
  'Reason for termination': 'Raison de la résiliation',
  'Recording...': 'Enregistrement en cours...',
  'Refresh messages': 'Actualiser les messages',
  'Reject': 'Rejeter',
  'Reject selected quotation': 'Rejeter le devis sélectionné',
  'Renew booking': 'Renouveler la réservation',
  'Rental period *': 'Période de location *',
  'Reply': 'Répondre',
  'Reply to Employee': 'Répondre à l\'employé',
  'Report Type': 'Type de rapport',
  'Report a Facility Concern': 'Signaler un problème lié à l\'installation',
  'Request an asset': 'Demander un actif',
  'Request renewal': 'Demander le renouvellement',
  'Resume': 'Reprendre',
  'Resume Work': 'Reprendre le travail',
  'Review': 'Examiner',
  'Role': 'Rôle',
  'Role-based access control': 'Contrôle d\'accès basé sur les rôles',
  'Room allocation, occupancy & bookings': 'Attribution des salles, occupation et réservations',
  'SMTP Host': 'Hôte SMTP',
  'SMTP Password': 'Mot de passe SMTP',
  'SMTP Username': 'Nom d\'utilisateur SMTP',
  'Safety Issue': 'Problème de sécurité',
  'Save & Reply': 'Enregistrer et répondre',
  'Save Settings': 'Enregistrer les paramètres',
  'Save payment': 'Enregistrer le paiement',
  'Schedule (if applicable)': 'Calendrier (le cas échéant)',
  'Search by name, tag, serial...': 'Rechercher par nom, étiquette, numéro de série...',
  'Search items...': 'Rechercher des articles...',
  'Security': 'Sécurité',
  'Select a manager': 'Sélectionner un gestionnaire',
  'Select a registered booking': 'Sélectionner une réservation enregistrée',
  'Select a space': 'Sélectionner un espace',
  'Select a vendor quotation before approval.': 'Sélectionnez un devis de fournisseur avant l\'approbation.',
  'Select asset': 'Sélectionner un actif',
  'Send Message': 'Envoyer le message',
  'Send Report': 'Envoyer le rapport',
  'Send test to...': 'Envoyer un test à...',
  'Send us a Message': 'Envoyez-nous un message',
  'Serial Number': 'Numéro de série',
  'Services': 'Services',
  'Short booking': 'Réservation courte',
  'Source': 'Source',
  'Space / Facility': 'Espace / Installation',
  'Space Name *': 'Nom de l\'espace *',
  'Space rental': 'Location d\'espace',
  'Staff': 'Personnel',
  'Start': 'Début',
  'Start *': 'Début *',
  'Start Date & Time *': 'Date et heure de début *',
  'Start Work': 'Commencer le travail',
  'Starting Date': 'Date de début',
  'Starting Date *': 'Date de début *',
  'Status:': 'Statut :',
  'Stay connected with your facility team': 'Restez connecté avec votre équipe d\'installation',
  'Stock': 'Stock',
  'Stock In': 'Entrée de stock',
  'Stock Out': 'Sortie de stock',
  'Storage Location': 'Emplacement de stockage',
  'Subject': 'Sujet',
  'Subject *': 'Sujet *',
  'Submit Booking Request': 'Soumettre la demande de réservation',
  'Submit Report': 'Soumettre le rapport',
  'Submit a Report': 'Soumettre un rapport',
  'Submit and track your reports to the facility manager': 'Soumettez et suivez vos rapports auprès du responsable de l\'installation',
  'Submitted': 'Soumis',
  'Suggestion': 'Suggestion',
  'System Name': 'Nom du système',
  'System Settings': 'Paramètres du système',
  'System instances': 'Instances du système',
  'Tag': 'Étiquette',
  'Technician': 'Technicien',
  'Tell us about your needs...': 'Parlez-nous de vos besoins...',
  'Terminate': 'Résilier',
  'Terminate booking': 'Résilier la réservation',
  'Terminate this booking? This action cannot be undone.': 'Résilier cette réservation ? Cette action est irréversible.',
  'Termination reason': 'Raison de la résiliation',
  'Terms of Service': 'Conditions d\'utilisation',
  'Test Email': 'E-mail de test',
  'This document will open in a separate browser tab.': 'Ce document s\'ouvrira dans un nouvel onglet du navigateur.',
  'Title *': 'Titre *',
  'To': 'À',
  'Total Area (m²)': 'Superficie totale (m²)',
  'Total Items': 'Total des articles',
  'Total Logs': 'Total des journaux',
  'Total Schedules': 'Total des calendriers',
  'Transaction Type': 'Type de transaction',
  'Type:': 'Type :',
  'Unassigned': 'Non attribué',
  'Unit': 'Unité',
  'Unit Cost': 'Coût unitaire',
  'Unit Cost (RWF)': 'Coût unitaire (RWF)',
  'Update Status': 'Mettre à jour le statut',
  'Urgent': 'Urgent',
  'User Created': 'Utilisateur créé',
  'Vendor:': 'Fournisseur :',
  'View detail': 'Voir le détail',
  'View details': 'Voir les détails',
  'Warranty Expiry': 'Expiration de la garantie',
  'What changed? What was done?': 'Qu\'est-ce qui a changé ? Qu\'a-t-on fait ?',
  'What do you need help with?': 'Avec quoi avez-vous besoin d\'aide ?',
  'What needs attention? *': 'Qu\'est-ce qui nécessite de l\'attention ? *',
  'What was done...': 'Ce qui a été fait...',
  'When': 'Quand',
  'Why is it needed?': 'Pourquoi est-ce nécessaire ?',
  'Work Description': 'Description du travail',
  'Your Name *': 'Votre nom *',
  'Your response will be visible to the employee...': 'Votre réponse sera visible par l\'employé...',
  'bytes': 'octets',
  'e.g. CLN-001': 'ex. CLN-001',
  'e.g. Filter #3M-500': 'ex. Filtre #3M-500',
  'e.g. HVAC-001': 'ex. HVAC-001',
  'e.g. Monthly HVAC Filter Replacement': 'ex. Remplacement mensuel du filtre CVC',
  'e.g. Projector, Whiteboard, AC': 'ex. Projecteur, Tableau blanc, Climatiseur',
  'e.g. Storage Room A, Shelf 3': 'ex. Salle de stockage A, Étagère 3',
  'e.g. Team Meeting, Training Session': 'ex. Réunion d\'équipe, Session de formation',
  'e.g. Team Standup': 'ex. Réunion debout de l\'équipe',
  'e.g. pieces, litres': 'ex. pièces, litres',
  'is enforced on all API endpoints:': 'est appliqué à tous les points de terminaison de l\'API :',
  'with login credentials.': 'avec les identifiants de connexion.',
  '© 2026 MITI FMS. All rights reserved.': '© 2026 MITI FMS. Tous droits réservés.',
  '— You can manage spaces in this facility': '— Vous pouvez gérer les espaces de cette installation',
  '— full access: facilities, users, settings, all data': '— accès complet : installations, utilisateurs, paramètres, toutes les données',
  '— manage spaces within their assigned facility': '— gère les espaces au sein de son installation assignée',
  '— manage work orders and maintenance logs': '— gère les ordres de travail et les journaux de maintenance',
  '— read access + submit work orders + book spaces': '— accès en lecture + soumission d\'ordres de travail + réservation d\'espaces',
  '✅ Settings saved': '✅ Paramètres enregistrés',
  '✓ Replied': '✓ Répondu',
  '✓ Sent': '✓ Envoyé',
  '👤 Guest Information': '👤 Informations sur l’invité',
  '👤 Your Details': '👤 Vos informations',
  '📅 Booking Details': '📅 Détails de la réservation',
  '📅 Order Details': '📅 Détails de la commande',
  '📤 Stock Out (Used)': '📤 Sortie de stock (Utilisé)',
  '📥 Stock In (Received)': '📥 Entrée de stock (Reçu)',
  '🔄 Adjust (Set exact qty)': '🔄 Ajuster (Définir la quantité exacte)',
  '🕐 Mon–Fri: 8am – 6pm': '🕐 Lun–Ven : 8h – 18h',
  '🚨 Urgent': '🚨 Urgent',
});

// --- Delete-system feature + broader coverage pass (added to close translation gaps) ---
Object.assign(kinyarwandaUiText, {
  "+250 7XX XXX XXX": "+250 7XX XXX XXX",
  "About text": "Umwandiko ku byerekeye",
  "Activate": "Gukoresha",
  "Add a progress report for this status change:": "Ongeraho raporo y’aho bigeze kuri iri hinduka ry’imiterere:",
  "Address": "Aderesi",
  "Admin email (optional)": "Imeli y’umuyobozi (ntabwo ari ngombwa)",
  "Admin email:": "Imeli y’umuyobozi:",
  "Assigned": "Yahawe",
  "Book Premium Spaces": "Fata Imyanya y’Icyubahiro",
  "Booking failed. Please try again.": "Kwandikisha ubwo bwifuzo byanze. Ongera ugerageze.",
  "Copy link": "Koporora umuyoboro",
  "Copy password": "Koporora ijambo banga",
  "Copy sign-in link": "Koporora umuyoboro wo kwinjira",
  "Copy this vendor link:": "Koporora uyu muyoboro w’umucuruzi:",
  "Could not create system": "Ntibishoboye gushiraho sisitemu",
  "Could not create vendor link": "Ntibishoboye gukora umuyoboro w’umucuruzi",
  "Could not delete announcement": "Ntibishoboye gusiba itangazo",
  "Could not delete message": "Ntibishoboye gusiba ubutumwa",
  "Could not delete that system": "Ntibishoboye gusiba iyo sisitemu",
  "Could not download attachment": "Ntibishoboye kuramo umugereka",
  "Could not open attachment": "Ntibishoboye gufungura umugereka",
  "Could not open that system": "Ntibishoboye gufungura iyo sisitemu",
  "Could not post announcement": "Ntibishoboye gutangaza itangazo",
  "Could not send message": "Ntibishoboye kohereza ubutumwa",
  "Could not submit your report.": "Ntibishoboye kohereza raporo yawe.",
  "Could not update announcement": "Ntibishoboye kuvugurura itangazo",
  "Could not update message": "Ntibishoboye kuvugurura ubutumwa",
  "Could not update system status": "Ntibishoboye kuvugurura imiterere ya sisitemu",
  "Create failed": "Gushiraho byanze",
  "Critical": "Byihutirwa cyane",
  "Currency": "Ifaranga rikoreshwa",
  "Deactivate": "Guhagarika",
  "Delete booking request for": "Siba ubusabe bwo kwamamaza bwa",
  "Delete failed": "Gusiba byanze",
  "Delete maintenance schedule": "Siba gahunda yo kubungabunga",
  "Dismiss": "Funga",
  "Employee": "Umukozi",
  "End time must be after start": "Igihe cyo kurangiza kigomba kuza nyuma y’igihe cyo gutangira",
  "Export failed.": "Kohereza hanze byanze.",
  "Facility Management": "Gucunga Ibikorwaremezo",
  "For Every Occasion": "Ku Buryo Bwose",
  "General": "Rusange",
  "Guest": "Umushyitsi",
  "Hero heading": "Umutwe nyamukuru",
  "Hero subheading": "Agatwe gakurikira",
  "High": "Hejuru",
  "Homepage Editor": "Guhindura Ipaji Nyamukuru",
  "Import failed.": "Kwinjiza byanze.",
  "Inventory data could not be loaded. Please check the API connection.": "Amakuru y’ubukorikori ntiyashoboye gutangazwa. Reba ukuntu API ihuza.",
  "Inventory filters could not be loaded. Check that the backend is running.": "Muyunguruzi z’ubukorikori ntizashoboye gutangazwa. Reba niba seriveri ikora.",
  "Its activity history will also be deleted. This cannot be undone.": "Amateka y’ibikorwa byayo nayo azasibwa. Ibi ntibishobora gusubizwa inyuma.",
  "Kigali, Rwanda": "Kigali, Rwanda",
  "Legal Text": "Amagambo y’amategeko",
  "Low Stock": "Ububiko buke",
  "Maintenance logs will be preserved.": "Inyandiko z’ibikorwa byo kubungabunga zizabikwa.",
  "Medium": "Hagati",
  "Messages could not be loaded. Check that the backend is running.": "Ubutumwa ntibwashoboye gutangazwa. Reba niba seriveri ikora.",
  "My Reports": "Raporo Zanjye",
  "My Tasks": "Imirimo Yanjye",
  "New system name (e.g. Acme Co)": "Izina rya sisitemu nshya (urugero: Acme Co)",
  "No systems yet — create the first one above.": "Nta sisitemu irahari — kora iya mbere hejuru.",
  "Notification logo preview": "Kureba ikirango cy’imenyesha",
  "Overdue": "Byarengeje igihe",
  "Payment update failed": "Kuvugurura kwishyura byanze",
  "Permanent deletion failed": "Gusiba burundu byanze",
  "Permanently delete": "Siba burundu",
  "Permanently delete this system and all of its data": "Siba burundu iyi sisitemu n’amakuru yayo yose",
  "Progress report failed": "Raporo y’aho bigeze byanze",
  "Public contact email": "Imeli y’itumanaho rusange",
  "Quantity": "Ingano",
  "Quotation review failed": "Isuzuma ry’igiciro byanze",
  "Remove image": "Kuraho ifoto",
  "Remove logo": "Kuraho ikirango",
  "Renewal failed": "Kongera igihe byanze",
  "Request review failed": "Isuzuma ry’icyifuzo byanze",
  "Save failed": "Kubika byanze",
  "Saving...": "Kubika...",
  "Sending...": "Kohereza...",
  "Sign-in link:": "Umuyoboro wo kwinjira:",
  "Social Media Links": "Imiyoboro y’Imbuga Nkoranyambaga",
  "Submit failed": "Kohereza byanze",
  "Submitting...": "Birimo koherezwa...",
  "System name did not match — deletion cancelled.": "Izina rya sisitemu ntabwo rihuye — gusiba byahagaritswe.",
  "Tell visitors about your facilities...": "Bwira abasura ibyerekeye ibikorwaremezo byawe...",
  "Temp password:": "Ijambo banga ry’agateganyo:",
  "Termination failed": "Gukatiza byanze",
  "Ticket could not be generated": "Tike ntiyashoboye gukorwa",
  "Timezone": "Agace k’isaha",
  "Transaction failed": "Igikorwa cy’ubucuruzi cyanze",
  "Type the Privacy Policy text here...": "Andika hano umwandiko w’amabwiriza y’ibanga...",
  "Type the Terms of Service text here...": "Andika hano umwandiko w’amabwiriza yo gukoresha...",
  "Update failed": "Kuvugurura byanze",
  "WO #": "Nomero y’akazi",
  "info@example.com": "info@example.com",
  "john@example.com": "john@example.com",
  "noreply@yourorg.com": "noreply@yourorg.com",
  "this guest": "uyu mushyitsi",
  "you@example.com": "you@example.com",
  "your@email.com": "your@email.com"
});

Object.assign(frenchUiText, {
  "+250 7XX XXX XXX": "+250 7XX XXX XXX",
  "About text": "Texte à propos",
  "Activate": "Activer",
  "Add a progress report for this status change:": "Ajoutez un rapport d’avancement pour ce changement de statut :",
  "Address": "Adresse",
  "Admin email (optional)": "E-mail de l’administrateur (facultatif)",
  "Admin email:": "E-mail de l’administrateur :",
  "Assigned": "Assigné",
  "Book Premium Spaces": "Réservez des espaces premium",
  "Booking failed. Please try again.": "Échec de la réservation. Veuillez réessayer.",
  "Copy link": "Copier le lien",
  "Copy password": "Copier le mot de passe",
  "Copy sign-in link": "Copier le lien de connexion",
  "Copy this vendor link:": "Copiez ce lien fournisseur :",
  "Could not create system": "Impossible de créer le système",
  "Could not create vendor link": "Impossible de créer le lien fournisseur",
  "Could not delete announcement": "Impossible de supprimer l’annonce",
  "Could not delete message": "Impossible de supprimer le message",
  "Could not delete that system": "Impossible de supprimer ce système",
  "Could not download attachment": "Impossible de télécharger la pièce jointe",
  "Could not open attachment": "Impossible d’ouvrir la pièce jointe",
  "Could not open that system": "Impossible d’ouvrir ce système",
  "Could not post announcement": "Impossible de publier l’annonce",
  "Could not send message": "Impossible d’envoyer le message",
  "Could not submit your report.": "Impossible d’envoyer votre rapport.",
  "Could not update announcement": "Impossible de mettre à jour l’annonce",
  "Could not update message": "Impossible de mettre à jour le message",
  "Could not update system status": "Impossible de mettre à jour l’état du système",
  "Create failed": "Échec de la création",
  "Critical": "Critique",
  "Currency": "Devise",
  "Deactivate": "Désactiver",
  "Delete booking request for": "Supprimer la demande de réservation pour",
  "Delete failed": "Échec de la suppression",
  "Delete maintenance schedule": "Supprimer le programme de maintenance",
  "Dismiss": "Fermer",
  "Employee": "Employé",
  "End time must be after start": "L’heure de fin doit être après celle de début",
  "Export failed.": "Échec de l’exportation.",
  "Facility Management": "Gestion des installations",
  "For Every Occasion": "Pour chaque occasion",
  "General": "Général",
  "Guest": "Invité",
  "Hero heading": "Titre principal",
  "Hero subheading": "Sous-titre principal",
  "High": "Élevée",
  "Homepage Editor": "Éditeur de la page d’accueil",
  "Import failed.": "Échec de l’importation.",
  "Inventory data could not be loaded. Please check the API connection.": "Les données d’inventaire n’ont pas pu être chargées. Vérifiez la connexion à l’API.",
  "Inventory filters could not be loaded. Check that the backend is running.": "Les filtres d’inventaire n’ont pas pu être chargés. Vérifiez que le serveur fonctionne.",
  "Its activity history will also be deleted. This cannot be undone.": "Son historique d’activité sera également supprimé. Cette action est irréversible.",
  "Kigali, Rwanda": "Kigali, Rwanda",
  "Legal Text": "Texte juridique",
  "Low Stock": "Stock faible",
  "Maintenance logs will be preserved.": "Les journaux de maintenance seront conservés.",
  "Medium": "Moyenne",
  "Messages could not be loaded. Check that the backend is running.": "Les messages n’ont pas pu être chargés. Vérifiez que le serveur fonctionne.",
  "My Reports": "Mes rapports",
  "My Tasks": "Mes tâches",
  "New system name (e.g. Acme Co)": "Nom du nouveau système (ex. Acme Co)",
  "No systems yet — create the first one above.": "Aucun système pour l’instant — créez le premier ci-dessus.",
  "Notification logo preview": "Aperçu du logo de notification",
  "Overdue": "En retard",
  "Payment update failed": "Échec de la mise à jour du paiement",
  "Permanent deletion failed": "Échec de la suppression définitive",
  "Permanently delete": "Supprimer définitivement",
  "Permanently delete this system and all of its data": "Supprimer définitivement ce système et toutes ses données",
  "Progress report failed": "Échec du rapport d’avancement",
  "Public contact email": "E-mail de contact public",
  "Quantity": "Quantité",
  "Quotation review failed": "Échec de l’examen du devis",
  "Remove image": "Supprimer l’image",
  "Remove logo": "Supprimer le logo",
  "Renewal failed": "Échec du renouvellement",
  "Request review failed": "Échec de l’examen de la demande",
  "Save failed": "Échec de l’enregistrement",
  "Saving...": "Enregistrement...",
  "Sending...": "Envoi...",
  "Sign-in link:": "Lien de connexion :",
  "Social Media Links": "Liens des réseaux sociaux",
  "Submit failed": "Échec de l’envoi",
  "Submitting...": "Envoi en cours...",
  "System name did not match — deletion cancelled.": "Le nom du système ne correspond pas — suppression annulée.",
  "Tell visitors about your facilities...": "Parlez de vos installations à vos visiteurs...",
  "Temp password:": "Mot de passe temporaire :",
  "Termination failed": "Échec de la résiliation",
  "Ticket could not be generated": "Le ticket n’a pas pu être généré",
  "Timezone": "Fuseau horaire",
  "Transaction failed": "Échec de la transaction",
  "Type the Privacy Policy text here...": "Saisissez ici le texte de la politique de confidentialité...",
  "Type the Terms of Service text here...": "Saisissez ici le texte des conditions d’utilisation...",
  "Update failed": "Échec de la mise à jour",
  "WO #": "N° OT",
  "info@example.com": "info@example.com",
  "john@example.com": "john@example.com",
  "noreply@yourorg.com": "noreply@yourorg.com",
  "this guest": "cet invité",
  "you@example.com": "you@example.com",
  "your@email.com": "your@email.com"
});

// --- Additional dialog strings found on second pass ---
Object.assign(kinyarwandaUiText, {
  "Choose the renewal start and end times.": "Hitamo igihe cyo gutangira n’igihe cyo kurangiza kongera igihe.",
  "Enter a termination reason.": "Andika impamvu yo gukatiza.",
  "Please select an image file.": "Hitamo dosiye y’ifoto.",
  "Please select an image smaller than 5 MB.": "Hitamo ifoto iri munsi ya MB 5.",
  "Select a vendor quotation before approval.": "Hitamo igiciro cy’umucuruzi mbere yo kwemeza.",
  "Message sent! We will get back to you soon.": "Ubutumwa bwoherejwe! Tuzagusubiza vuba."
});

Object.assign(frenchUiText, {
  "Choose the renewal start and end times.": "Choisissez les heures de début et de fin du renouvellement.",
  "Enter a termination reason.": "Indiquez un motif de résiliation.",
  "Please select an image file.": "Veuillez sélectionner un fichier image.",
  "Please select an image smaller than 5 MB.": "Veuillez sélectionner une image de moins de 5 Mo.",
  "Select a vendor quotation before approval.": "Sélectionnez un devis du fournisseur avant l’approbation.",
  "Message sent! We will get back to you soon.": "Message envoyé ! Nous vous répondrons bientôt."
});

// --- WorkOrders.js vendor-link / quotation dialog strings ---
Object.assign(kinyarwandaUiText, {
  "Request approved. Vendor link and QR code copied:": "Icyifuzo cyemejwe. Umuyoboro w’umucuruzi na kode ya QR byakoporowe:",
  "Request approved. Vendor link copied:": "Icyifuzo cyemejwe. Umuyoboro w’umucuruzi wakoporowe:",
  "Select and copy the vendor link:": "Hitamo hanyuma ukoporore umuyoboro w’umucuruzi:",
  "Vendor link copied:": "Umuyoboro w’umucuruzi wakoporowe:",
  "Quotation approved. Purchase order": "Igiciro cyemejwe. Inyandiko yo kugura",
  "created.": "yaremwe.",
  "Permanently delete": "Siba burundu"
});

Object.assign(frenchUiText, {
  "Request approved. Vendor link and QR code copied:": "Demande approuvée. Lien fournisseur et code QR copiés :",
  "Request approved. Vendor link copied:": "Demande approuvée. Lien fournisseur copié :",
  "Select and copy the vendor link:": "Sélectionnez et copiez le lien fournisseur :",
  "Vendor link copied:": "Lien fournisseur copié :",
  "Quotation approved. Purchase order": "Devis approuvé. Bon de commande",
  "created.": "yaremwe.",
  "Permanently delete": "Supprimer définitivement"
});

// --- MyTasks.js asset-request error skeleton ---
Object.assign(kinyarwandaUiText, {
  "Asset request failed": "Icyifuzo cy’umutungo cyanze",
  "network error": "ikosa ry’umuyoboro",
  "Please verify the backend is running and the work order is valid.": "Reba niba seriveri ikora kandi ko akazi ari byo."
});

Object.assign(frenchUiText, {
  "Asset request failed": "Échec de la demande de matériel",
  "network error": "erreur réseau",
  "Please verify the backend is running and the work order is valid.": "Veuillez vérifier que le serveur fonctionne et que l’ordre de travail est valide."
});

// --- PublicBooking.js marketing copy (data-array strings, second audit pass) ---
Object.assign(kinyarwandaUiText, {
  "24/7 security monitoring, access control, and the MITI facility management team.": "Kugenzura umutekano 24/7, kugenzura injira, n’ikipe ya MITI icunga ibikorwaremezo.",
  "All space rentals priced in Rwandan Francs. Clear pricing per hour with amount shown before booking.": "Ubukode bw’imyanya bwose buhabwa igiciro mu Mafaranga y’u Rwanda. Igiciro gisobanutse ku isaha, ingano igaragara mbere yo kwiyandikisha.",
  "Book conference rooms, training halls, offices and more with real-time availability and instant confirmation.": "Fata ibyumba by’inama, ibyumba by’amahugurwa, biro n’ibindi hakoreshejwe ubuhari mu gihe nyacyo n’iyemeza ryihuse.",
  "Booking Management": "Gucunga Kwiyandikisha",
  "Dedicated facility managers and support staff to ensure your needs are met promptly.": "Abayobozi b’ibikorwaremezo n’abakozi bafasha bihariye kugira ngo ibyo ukeneye bikorwe vuba.",
  "Energy-efficient systems and eco-friendly practices to minimize environmental impact.": "Uburyo bukoresha ingufu neza n’imikorere ibungabunga ibidukikije kugira ngo hagabanywe ingaruka ku bidukikije.",
  "Full visibility of all space bookings with approval workflow, conflict detection, and calendar view.": "Kubona neza ibyifuzo byo kwiyandikisha byose hamwe n’inzira yo kwemeza, kumenya ibihuriranye, no kureba kalendari.",
  "Modern Infrastructure": "Ibikorwaremezo Bigezweho",
  "Mon–Fri, 8am–6pm": "Kuwa Mbere–Kuwa Gatanu, 8h–18h",
  "Preventive and corrective maintenance scheduling to keep your facility running at peak performance.": "Gutegura gahunda yo kubungabunga mbere y’uko ikibazo kibaho no gukosora, kugira ngo ikigo cyawe gikomeze gukora neza.",
  "Professional Support": "Ubufasha Bunoze",
  "RWF Payments": "Kwishyura muri RWF",
  "Secure & Managed": "Byizewe kandi Bicungwa",
  "Space Booking": "Kwiyandikisha Umwanya",
  "State-of-the-art facilities equipped with the latest technology for a productive environment.": "Ibikorwaremezo bigezweho bifite ikoranabuhanga rigezweho kugira ngo habeho ahantu heza ho gukorera.",
  "Submit and track service requests with priority management and real-time status updates.": "Ohereza kandi ukurikirane ibisabwa by’serivisi hamwe no gucunga ibyihutirwa n’amakuru agezweho mu gihe nyacyo.",
  "Sustainable Operations": "Imikorere Irambye",
  "Track and manage all facility equipment, machinery, and resources with lifecycle and depreciation tracking.": "Kurikirana no gucunga ibikoresho byose, imashini, n’umutungo hamwe no gukurikirana igihe cyabyo n’agaciro kabyo.",
  "Visit our main facility": "Sura ikigo cyacu nyamukuru",
  "We reply within 24 hours": "Dusubiza mu masaha 24",
  "Conference rooms, training halls, offices and more — all in one professional platform. Book instantly, pay in RWF.": "Ibyumba by’inama, ibyumba by’amahugurwa, biro n’ibindi — byose ahantu hamwe ha kigenzura. Iyandikishe ako kanya, wishyure muri RWF.",
  "From space bookings to full facility management, we cover it all.": "Kuva ku kwiyandikisha umwanya kugeza ku gucunga ibikorwaremezo byuzuye, dukora byose.",
  "Have questions about booking, pricing, or facility management? We're here to help.": "Ufite ibibazo ku byerekeye kwiyandikisha, ibiciro, cyangwa gucunga ibikorwaremezo? Turi hano kugufasha.",
  "MITI facility management platform serving businesses across Rwanda. Book spaces, manage assets, and streamline operations.": "Urubuga rwa MITI rucunga ibikorwaremezo rukorera ibigo mu Rwanda hose. Iyandikishe imyanya, ucunge umutungo, kandi worohereze imikorere.",
  "Home": "Ahabanza",
  "About": "Ibyerekeye",
  "Report": "Tanga Raporo",
  "Follow Us": "Dukurikire",
  "Report an Issue": "Tanga Ikibazo",
  "Vendor Management": "Gucunga Abacuruzi"
});

Object.assign(frenchUiText, {
  "24/7 security monitoring, access control, and the MITI facility management team.": "Surveillance de sécurité 24h/24 et 7j/7, contrôle d’accès, et l’équipe de gestion des installations MITI.",
  "All space rentals priced in Rwandan Francs. Clear pricing per hour with amount shown before booking.": "Toutes les locations d’espaces sont tarifées en Francs Rwandais. Prix clair par heure, montant affiché avant la réservation.",
  "Book conference rooms, training halls, offices and more with real-time availability and instant confirmation.": "Réservez des salles de conférence, salles de formation, bureaux et plus, avec disponibilité en temps réel et confirmation instantanée.",
  "Booking Management": "Gestion des réservations",
  "Dedicated facility managers and support staff to ensure your needs are met promptly.": "Des responsables d’installations et du personnel de soutien dédiés pour répondre rapidement à vos besoins.",
  "Energy-efficient systems and eco-friendly practices to minimize environmental impact.": "Des systèmes économes en énergie et des pratiques écologiques pour réduire l’impact environnemental.",
  "Full visibility of all space bookings with approval workflow, conflict detection, and calendar view.": "Visibilité complète de toutes les réservations d’espaces avec flux d’approbation, détection de conflits et vue calendrier.",
  "Modern Infrastructure": "Infrastructure moderne",
  "Mon–Fri, 8am–6pm": "Lun–Ven, 8h–18h",
  "Preventive and corrective maintenance scheduling to keep your facility running at peak performance.": "Planification de la maintenance préventive et corrective pour maintenir votre installation à son meilleur niveau.",
  "Professional Support": "Assistance professionnelle",
  "RWF Payments": "Paiements en RWF",
  "Secure & Managed": "Sécurisé et géré",
  "Space Booking": "Réservation d’espace",
  "State-of-the-art facilities equipped with the latest technology for a productive environment.": "Des installations à la pointe de la technologie pour un environnement productif.",
  "Submit and track service requests with priority management and real-time status updates.": "Soumettez et suivez les demandes de service avec gestion des priorités et mises à jour en temps réel.",
  "Sustainable Operations": "Opérations durables",
  "Track and manage all facility equipment, machinery, and resources with lifecycle and depreciation tracking.": "Suivez et gérez tous les équipements, machines et ressources avec suivi du cycle de vie et de la dépréciation.",
  "Visit our main facility": "Visitez notre installation principale",
  "We reply within 24 hours": "Nous répondons sous 24 heures",
  "Conference rooms, training halls, offices and more — all in one professional platform. Book instantly, pay in RWF.": "Salles de conférence, salles de formation, bureaux et plus — tout sur une seule plateforme professionnelle. Réservez instantanément, payez en RWF.",
  "From space bookings to full facility management, we cover it all.": "De la réservation d’espaces à la gestion complète des installations, nous nous occupons de tout.",
  "Have questions about booking, pricing, or facility management? We're here to help.": "Des questions sur la réservation, les prix ou la gestion des installations ? Nous sommes là pour vous aider.",
  "MITI facility management platform serving businesses across Rwanda. Book spaces, manage assets, and streamline operations.": "La plateforme de gestion des installations MITI au service des entreprises à travers le Rwanda. Réservez des espaces, gérez les actifs et simplifiez les opérations.",
  "Home": "Accueil",
  "About": "À propos",
  "Report": "Signaler",
  "Follow Us": "Suivez-nous",
  "Report an Issue": "Signaler un problème",
  "Vendor Management": "Gestion des fournisseurs"
});

// --- Final system-wide audit pass: remaining object-literal + status labels ---
Object.assign(kinyarwandaUiText, {
  "No booking requests yet": "Nta bisabwa byo kwiyandikisha birahari",
  "Each system gets its own separate, fully isolated database — its own users, spaces, work orders, assets, branding and settings. Nothing entered in one system is visible from another.": "Buri sisitemu ifite ububiko bwayo bwihariye, butandukanye burundu — abakoresha bayo, imyanya, akazi, umutungo, ikirango n’igenamiterere byayo. Ikintu cyinjijwe muri sisitemu imwe ntikigaragara muri indi.",
  "Due today": "Bikwiye kuboneka uyu munsi",
  "No date": "Nta tariki",
  "No upcoming maintenance in the next 30 days": "Nta gukorwa ku bikorwaremezo biteganyijwe mu minsi 30 iri imbere",
  "In Progress": "Birimo Gukorwa",
  "No tasks assigned to you yet": "Nta mirimo urahabwa",
  "On Hold": "Byahagaritswe by’agateganyo",
  "In Review": "Birimo Gusuzumwa",
  "No reports received yet": "Nta raporo zaboneste",
  "No reports submitted yet": "Nta raporo zoherejwe",
  "Total Reports": "Raporo Zose",
  "Total Submitted": "Byose Byoherejwe",
  "Configure SMTP to enable welcome emails for new users. Gmail: use App Password with smtp.gmail.com:587": "Shyiraho SMTP kugira ngo wemerere kohereza imeli zo kwakira abakoresha bashya. Gmail: koresha 'App Password' hamwe na smtp.gmail.com:587",
  "Export records as CSV for backup or import new buildings, assets, and inventory items. Imports add records and do not overwrite existing data.": "Sohora amakuru muri CSV kugira ngo ubike cyangwa winjize inyubako, umutungo, n’ibintu by’ubukorikori bishya. Kwinjiza kongera amakuru ntibisimbura ayahari.",
  "A temporary password will be generated and emailed to the user.": "Ijambo banga ry’agateganyo rizakorwa rikoherezwa umukoresha kuri imeli.",
  "The user can change their password anytime from the sidebar after logging in.": "Umukoresha ashobora guhindura ijambo banga rye igihe icyo ari cyo cyose uhereye ku gice k’impande nyuma yo kwinjira."
});

Object.assign(frenchUiText, {
  "No booking requests yet": "Aucune demande de réservation pour l’instant",
  "Each system gets its own separate, fully isolated database — its own users, spaces, work orders, assets, branding and settings. Nothing entered in one system is visible from another.": "Chaque système dispose de sa propre base de données totalement isolée — ses propres utilisateurs, espaces, ordres de travail, actifs, image de marque et paramètres. Rien saisi dans un système n’est visible depuis un autre.",
  "Due today": "À faire aujourd’hui",
  "No date": "Aucune date",
  "No upcoming maintenance in the next 30 days": "Aucune maintenance prévue dans les 30 prochains jours",
  "In Progress": "En cours",
  "No tasks assigned to you yet": "Aucune tâche ne vous a encore été assignée",
  "On Hold": "En attente",
  "In Review": "En cours d’examen",
  "No reports received yet": "Aucun rapport reçu pour l’instant",
  "No reports submitted yet": "Aucun rapport envoyé pour l’instant",
  "Total Reports": "Total des rapports",
  "Total Submitted": "Total envoyé",
  "Configure SMTP to enable welcome emails for new users. Gmail: use App Password with smtp.gmail.com:587": "Configurez le SMTP pour activer les e-mails de bienvenue aux nouveaux utilisateurs. Gmail : utilisez un mot de passe d’application avec smtp.gmail.com:587",
  "Export records as CSV for backup or import new buildings, assets, and inventory items. Imports add records and do not overwrite existing data.": "Exportez les données en CSV pour sauvegarde ou importez de nouveaux bâtiments, actifs et articles d’inventaire. Les importations ajoutent des données sans écraser les données existantes.",
  "A temporary password will be generated and emailed to the user.": "Un mot de passe temporaire sera généré et envoyé par e-mail à l’utilisateur.",
  "The user can change their password anytime from the sidebar after logging in.": "L’utilisateur peut changer son mot de passe à tout moment depuis le menu latéral après connexion."
});

// Hour/day/month booking pricing + deposit-to-confirm-interest feature
Object.assign(kinyarwandaUiText, {
  "Order Type": "Ubwoko bw'icyifuzo",
  "Rental period": "Igihe cy'ubukode",
  "Price unit": "Igipimo cy'igiciro",
  "Per hour · ": "Ku isaha · ",
  "Per day · ": "Ku munsi · ",
  "Per month · ": "Ku kwezi · ",
  "Total": "Igiteranyo",
  "hr": "isaha",
  "hour": "isaha",
  "day(s)": "iminsi",
  "month(s)": "amezi",
  "rental": "ubukode",
  "booking": "iyandikisha",
  "Deposit required to confirm interest (": "Amafaranga y'ingwate akenewe kugira ngo hemezwe ubushake (",
  "The booker agrees to pay a deposit of ": "Uwasabye yemeye kwishyura amafaranga y'ingwate angana na ",
  "% of the total) to confirm genuine interest before this booking is approved. Track the deposit and remaining balance via the booking's payment status.": "% by'igiteranyo) kugira ngo hemezwe ubushake nyabwo mbere y'uko iyi reservasiyo yemezwa. Kurikirana amafaranga y'ingwate n'amasigaye binyuze mu mimerere y'ubwishyu bw'iyandikisha.",
  "Deposit Due Now (to confirm interest)": "Amafaranga y'ingwate agomba kwishyurwa ubu (kwemeza ubushake)",
  "Deposit required now (": "Amafaranga y'ingwate akenewe ubu (",
  "I agree to pay a deposit of ": "Nemeye kwishyura amafaranga y'ingwate angana na ",
  "% of the total) to confirm I am genuinely interested in this ": "% by'igiteranyo) kugira ngo nemeze ubushake nyabwo kuri iyi ",
  ". The remaining balance is due as described above.": ". Amasigaye agomba kwishyurwa nk'uko byasobanuwe haruguru.",
  "% of the total order value": "% by'agaciro k'igiteranyo cy'icyifuzo",
  "Total Order Value: ": "Agaciro k'Igiteranyo cy'Icyifuzo: ",
  "That file is": "Iri dosiye ni",
  "Attachments must be 10 MB or smaller.": "Imigereka igomba kuba 10 MB cyangwa munsi yayo."
});
Object.assign(frenchUiText, {
  "Order Type": "Type de commande",
  "Rental period": "Période de location",
  "Price unit": "Unité de prix",
  "Per hour · ": "Par heure · ",
  "Per day · ": "Par jour · ",
  "Per month · ": "Par mois · ",
  "Total": "Total",
  "hr": "h",
  "hour": "heure",
  "day(s)": "jour(s)",
  "month(s)": "mois",
  "rental": "location",
  "booking": "réservation",
  "Deposit required to confirm interest (": "Acompte requis pour confirmer l’intérêt (",
  "The booker agrees to pay a deposit of ": "Le demandeur accepte de verser un acompte de ",
  "% of the total) to confirm genuine interest before this booking is approved. Track the deposit and remaining balance via the booking's payment status.": "% du total) pour confirmer un intérêt réel avant l’approbation de cette réservation. Suivez l’acompte et le solde restant via le statut de paiement de la réservation.",
  "Deposit Due Now (to confirm interest)": "Acompte à payer maintenant (pour confirmer l’intérêt)",
  "Deposit required now (": "Acompte requis maintenant (",
  "I agree to pay a deposit of ": "J’accepte de verser un acompte de ",
  "% of the total) to confirm I am genuinely interested in this ": "% du total) pour confirmer mon intérêt réel pour cette ",
  ". The remaining balance is due as described above.": ". Le solde restant est dû comme décrit ci-dessus.",
  "% of the total order value": "% de la valeur totale de la commande",
  "Total Order Value: ": "Valeur totale de la commande : ",
  "That file is": "Ce fichier fait",
  "Attachments must be 10 MB or smaller.": "Les pièces jointes doivent faire 10 Mo maximum."
});

Object.assign(kinyarwandaUiText, {
  'System Issue': 'Ikibazo cya sisitemu',
  'System Issues': 'Ibibazo bya sisitemu',
  'Software issues reported by staff': 'Ibibazo bya porogaramu byatanzwe n\'abakozi',
  'Report System Issue': 'Tanga ikibazo cya sisitemu',
  'Report a System Issue': 'Tanga ikibazo cya sisitemu',
  'Brief description of the software issue': 'Incamake y\'ikibazo cya porogaramu',
  'Describe the bug — what happened, steps to reproduce, when it started...': 'Sobanura ikibazo — ibyabaye, intambwe zo kongera kugikora, igihe cyatangiriye...',
  'Submit to Developer': 'Ohereza ku muhanga wa porogaramu'
});

Object.assign(frenchUiText, {
  'System Issue': 'Problème système',
  'System Issues': 'Problèmes système',
  'Software issues reported by staff': 'Problèmes logiciels signalés par le personnel',
  'Report System Issue': 'Signaler un problème système',
  'Report a System Issue': 'Signaler un problème système',
  'Brief description of the software issue': 'Brève description du problème logiciel',
  'Describe the bug — what happened, steps to reproduce, when it started...': 'Décrivez le bug — ce qui s\'est passé, les étapes pour le reproduire, quand cela a commencé...',
  'Submit to Developer': 'Envoyer au développeur'
});

Object.assign(kinyarwandaUiText, {
  'Backup & Restore': 'Kubika no Gusubiza',
  'Create Backup Now': 'Kora ikibiko ubu',
  'Creating backup...': 'Birimo gukora ikibiko...',
  'Backup': 'Ikibiko',
  'Manual': 'Ku ntoki',
  'Automatic': 'Byikora',
  'Pre-restore': 'Mbere yo gusubiza',
  'Size': 'Ingano',
  'Created': 'Byakozwe',
  'Restore': 'Subiza',
  'Restoring...': 'Birimo gusubizwa...',
  'Restore Now': 'Subiza ubu',
  'Restore from backup?': 'Gusubiza uhereye ku kibiko?',
  'Type RESTORE to confirm:': 'Andika RESTORE kugira ngo wemeze:',
  'Failed': 'Byanze',
  'No backups yet — click \'Create Backup Now\' to make the first one.': 'Nta bibiko birahari — kanda \'Kora ikibiko ubu\' kugira ngo ukore icya mbere.',
  'Delete this backup? This cannot be undone.': 'Gusiba iki kibiko? Ibi ntibishobora gusubizwa inyuma.',
  'Could not load backups': 'Ntibishobotse gutangira ibibiko',
  'Backup failed': 'Ikibiko cyananiranye',
  'Download failed': 'Gukuramo byananiranye',
  'Delete failed': 'Gusiba byananiranye',
  'Restore failed': 'Gusubiza byananiranye'
});

Object.assign(frenchUiText, {
  'Backup & Restore': 'Sauvegarde et restauration',
  'Create Backup Now': 'Créer une sauvegarde',
  'Creating backup...': 'Création de la sauvegarde...',
  'Backup': 'Sauvegarde',
  'Manual': 'Manuelle',
  'Automatic': 'Automatique',
  'Pre-restore': 'Avant restauration',
  'Size': 'Taille',
  'Created': 'Créée',
  'Restore': 'Restaurer',
  'Restoring...': 'Restauration...',
  'Restore Now': 'Restaurer maintenant',
  'Restore from backup?': 'Restaurer à partir de cette sauvegarde ?',
  'Type RESTORE to confirm:': 'Tapez RESTORE pour confirmer :',
  'Failed': 'Échec',
  'No backups yet — click \'Create Backup Now\' to make the first one.': 'Aucune sauvegarde pour le moment — cliquez sur \'Créer une sauvegarde\' pour créer la première.',
  'Delete this backup? This cannot be undone.': 'Supprimer cette sauvegarde ? Cette action est irréversible.',
  'Could not load backups': 'Impossible de charger les sauvegardes',
  'Backup failed': 'Échec de la sauvegarde',
  'Download failed': 'Échec du téléchargement',
  'Delete failed': 'Échec de la suppression',
  'Restore failed': 'Échec de la restauration'
});

// --- Strings introduced by the standalone public pages (About/Services/
// Booking/Report/Contact/Privacy/Terms) split out of the homepage ---
Object.assign(kinyarwandaUiText, {
  'Back to Home': 'Subira Ahabanza',
  'All rights reserved.': 'Uburenganzira bwose burarinzwe.',
  'Asset Management': 'Gucunga Umutungo',
  'All space rentals priced in RWF. Clear pricing per hour with amount shown before booking.': 'Ubukode bwose bw\'imyanya bugenwa muri RWF. Igiciro kigaragara ku isaha hamwe n\'umubare ugaragazwa mbere yo kwiyandikisha.',
  'This page hasn\'t been filled in yet.': 'Iyi paji ntiraruzuzwa.',
  'Event Title': 'Umutwe w\'Ibirori',
  'Purpose': 'Intego',
  'Rental Order Submitted!': 'Icyifuzo cy\'Ubukode Cyoherejwe!',
  'Booking Request Submitted!': 'Icyifuzo cyo Kwiyandikisha Cyoherejwe!',
  'Balance due on arrival · Currency:': 'Amafaranga asigaye yishyurwa uje · Ifaranga:',
  'Available Spaces': 'Imyanya Iboneka',
  'All prices shown in': 'Ibiciro byose byerekanwa muri',
  'Spaces are temporarily unavailable. Please try again.': 'Imyanya ntiboneka by\'agateganyo. Ongera ugerageze.',
  '⚠️ Already booked:': '⚠️ Byamaze kwandikwa:',
  'ℹ️ Your request will be reviewed by the facility manager. You\'ll be contacted at your email to confirm.': 'ℹ️ Icyifuzo cyawe kizasuzumwa n\'umuyobozi w\'ikorwaremezo. Uzavugwaho kuri imeli yawe kugira ngo bikwemezwe.',
  'Trusted By': 'Twizerwa na',
  'Our Partners': 'Abafatanyabikorwa Bacu',
  'Add fast access from your home screen.': 'Ongeraho uburyo bwihuse bwo kubona kuri ecran yawe itangira.',
  'Tap Share, then Add to Home Screen.': 'Kanda "Share", hanyuma wongere ku Home Screen.',
  'Dashboard →': 'Imbonerahamwe →'
});

Object.assign(frenchUiText, {
  'Back to Home': 'Retour à l\'accueil',
  'All rights reserved.': 'Tous droits réservés.',
  'Asset Management': 'Gestion des actifs',
  'All space rentals priced in RWF. Clear pricing per hour with amount shown before booking.': 'Toutes les locations d\'espaces sont tarifées en RWF. Prix clair par heure avec le montant affiché avant la réservation.',
  'This page hasn\'t been filled in yet.': 'Cette page n\'a pas encore été remplie.',
  'Event Title': 'Titre de l\'événement',
  'Purpose': 'Objectif',
  'Rental Order Submitted!': 'Commande de location soumise !',
  'Booking Request Submitted!': 'Demande de réservation soumise !',
  'Balance due on arrival · Currency:': 'Solde dû à l\'arrivée · Devise :',
  'Available Spaces': 'Espaces disponibles',
  'All prices shown in': 'Tous les prix sont affichés en',
  'Spaces are temporarily unavailable. Please try again.': 'Les espaces sont temporairement indisponibles. Veuillez réessayer.',
  '⚠️ Already booked:': '⚠️ Déjà réservé :',
  'ℹ️ Your request will be reviewed by the facility manager. You\'ll be contacted at your email to confirm.': 'ℹ️ Votre demande sera examinée par le gestionnaire de l\'installation. Vous serez contacté(e) par e-mail pour confirmation.',
  'Trusted By': 'Ils nous font confiance',
  'Our Partners': 'Nos partenaires',
  'Add fast access from your home screen.': 'Ajoutez un accès rapide depuis votre écran d\'accueil.',
  'Tap Share, then Add to Home Screen.': 'Appuyez sur Partager, puis sur Ajouter à l\'écran d\'accueil.',
  'Dashboard →': 'Tableau de bord →'
});

function getTranslatedUiText(value, language) {
  const trimmedValue = value.trim();
  const languageUiText = language === 'rw' ? kinyarwandaUiText : frenchUiText;
  const translated = languageUiText[trimmedValue];
  if (!translated) return value;
  return value.replace(trimmedValue, translated);
}

// Native browser dialogs (window.alert / window.confirm / window.prompt) render
// outside the page's DOM, so the MutationObserver-based auto-translate above can
// never reach their text — it only rewrites text nodes and a few attributes that
// are actually in the document. Any page that calls one of those dialogs with a
// literal English string should wrap it with this helper instead. It reuses the
// same frenchUiText/kinyarwandaUiText lookup tables as the DOM walker (so one
// translation entry covers both), but works from plain JS with no React context,
// which is what a call site like `alert(...)` or `window.confirm(...)` needs.
export function translateUiText(value) {
  if (typeof value !== 'string') return value;
  let language = DEFAULT_LANGUAGE;
  try {
    const stored = typeof localStorage !== 'undefined' ? localStorage.getItem('fms_language') : null;
    if (stored && translations[stored]) language = stored;
  } catch (error) { /* ignore - localStorage may be unavailable */ }
  if (language === DEFAULT_LANGUAGE) return value;
  return getTranslatedUiText(value, language);
}

export function LanguageProvider({ children }) {
  const [language, setLanguage] = useState(() => {
    const stored = localStorage.getItem('fms_language');
    return stored && translations[stored] ? stored : DEFAULT_LANGUAGE;
  });
  const translatedTextNodes = useRef(new Map());
  const translatedAttributes = useRef(new Map());
  useEffect(() => {
    localStorage.setItem('fms_language', language);
    document.documentElement.lang = language;
  }, [language]);

  useEffect(() => {
    const restore = () => {
      translatedTextNodes.current.forEach((value, node) => { if (node.isConnected) node.nodeValue = value; });
      translatedAttributes.current.forEach(({ element, attribute, value }) => { if (element.isConnected) element.setAttribute(attribute, value); });
      translatedTextNodes.current.clear();
      translatedAttributes.current.clear();
    };

    restore();
    if (language === DEFAULT_LANGUAGE) return undefined;

    const translateValue = value => getTranslatedUiText(value, language);
    const translateTextNode = node => {
      if (node.nodeType !== Node.TEXT_NODE || !node.nodeValue?.trim() || node.parentElement?.closest('[data-no-translate]')) return;
      const original = node.nodeValue;
      const translated = translateValue(original);
      if (translated !== original) {
        translatedTextNodes.current.set(node, original);
        node.nodeValue = translated;
      }
    };
    const translateElement = element => {
      if (!(element instanceof Element) || element.closest('[data-no-translate]')) return;
      ['placeholder', 'title', 'aria-label'].forEach(attribute => {
        const original = element.getAttribute(attribute);
        if (!original) return;
        const translated = translateValue(original);
        if (translated !== original) {
          const key = `${attribute}:${original}`;
          translatedAttributes.current.set(`${element.tagName}:${key}:${translatedAttributes.current.size}`, { element, attribute, value: original });
          element.setAttribute(attribute, translated);
        }
      });
    };
    const translateTree = root => {
      if (root.nodeType === Node.TEXT_NODE) translateTextNode(root);
      else if (root instanceof Element || root instanceof DocumentFragment) {
        if (root instanceof Element) translateElement(root);
        const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
        let node = walker.nextNode();
        while (node) { translateTextNode(node); node = walker.nextNode(); }
        if (root.querySelectorAll) root.querySelectorAll('[placeholder], [title], [aria-label]').forEach(translateElement);
      }
    };
    translateTree(document.body);
    const observer = new MutationObserver(mutations => mutations.forEach(mutation => mutation.addedNodes.forEach(translateTree)));
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [language]);

  const value = useMemo(() => ({
    language,
    setLanguage,
    // params (optional): an object of {token}->value substitutions, e.g.
    // t('developer.deleteSystemPrompt', 'Type the name: {name}', { name: system.name })
    t: (key, fallback = '', params) => {
      let str = translations[language]?.[key] || translations[DEFAULT_LANGUAGE]?.[key] || fallback || key;
      if (params) {
        Object.keys(params).forEach(token => {
          str = str.split(`{${token}}`).join(params[token]);
        });
      }
      return str;
    }
  }), [language]);

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}

const LANGUAGE_FLAGS = { en: 'EN', fr: '\u{1F1EB}\u{1F1F7}', rw: '\u{1F1F7}\u{1F1FC}' };

export function LanguageSelector({ className = '', style = {} }) {
  const { language, setLanguage, t } = useLanguage();

  return (
    <label title={t('language.label')} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, ...style }} className={className}>
      <select
        aria-label={t('language.label')}
        value={language}
        onChange={(event) => setLanguage(event.target.value)}
        className="form-select form-select-sm"
        style={{ width: 44, minWidth: 44, background: '#fff', padding: '2px 4px', cursor: 'pointer', fontSize: 12, lineHeight: 1.4, textAlign: 'center' }}
      >
        <option value="en" title={t('language.english')}>{LANGUAGE_FLAGS.en}</option>
        <option value="fr" title={t('language.french')}>{LANGUAGE_FLAGS.fr}</option>
        <option value="rw" title={t('language.kinyarwanda')}>{LANGUAGE_FLAGS.rw}</option>
      </select>
    </label>
  );
}

export { translations };
